import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../firebase';
import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, updateDoc, query, where, serverTimestamp, writeBatch, onSnapshot, increment } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, getMetadata } from 'firebase/storage';
import { getUserRole, ROLES } from '../utils/roles';
import SusunMenuV39 from './SusunMenuV39';
import { productDisplayNo, buildScrapeSourceUrl, extractTikTokProductId } from '../utils/productDisplayId';
import { SAIZ_SENARAI, SAIZ_RADIUS, SAIZ_PILIHAN, bacaSaizGambar, simpanSaizGambar } from '../utils/saizGambarSenarai';
import { aiService } from '../services/aiService';
import { loadLibraryPayload, saveLibraryPayload, deleteLibraryPayload } from '../services/libraryPayloadService';
import { repairPendingLibraryCards, pendingReasonText, buildDataProdukBlock } from '../services/productReadyService';
import { simpanDataProdukItem } from '../utils/editDataProduk';
import { KUNCI_HANTAR_WORKSPACE, HAD_HANTAR_WORKSPACE, produkPending, sediakanHantaran, namaRingkas } from '../utils/hantarKeWorkspace';
import ScrapedDataModal from './ScrapedDataModal';
import CompleteProductDataModal from './CompleteProductDataModal';
import ImportStepperV39 from './ImportStepperV39';
import ImportShopeeV39 from './ImportShopeeV39';
import { TabPlatformImport, TandaPlatform } from './PlatformImportV39';
import { simpanImportShopee, kunciShopee, kemasStorefrontTersimpan } from '../utils/shopeeImport';
import { useT } from '../i18n';

// ═══ Reka bentuk v3.9 (suis `config/ui_v39`) ═══
import '../design/tokens.css';
import { PageHeader, Segmented, Field, Btn, Badge, Card } from '../design/ui';
import { Search as IkonCari, Tag as IkonTag, Pencil as IkonPensel, Upload as IkonUpload, ListChecks as IkonPilih, Menu as IkonMenu, UserRound as IkonProfil, Plus as IkonTambah, Layers as IkonLapis, LayoutGrid as IkonGrid, Rows3 as IkonSenarai, Image as IkonGambar, ExternalLink as IkonBuka, Trash2 as IkonPadam, FileText as IkonNota, Store, ShoppingBag, Heart, Star, Flame, Sparkles } from 'lucide-react';

// ═══ PROFILE TIKTOK (Fasa A — lihat PLAN_IMPORT_BACKEND.md) ═══
// 1 profile = 1 akaun TikTok pelanggan. Disimpan dalam users/{uid}.tiktokProfiles
// (corak SAMA dengan libraryGroups sedia ada; `tiktokProfiles` BUKAN medan
// terlindung dalam rules, jadi tiada deploy rules diperlukan).
//
// ⭐ PERATURAN TETAP: profile TIDAK PERNAH wajib. Produk tanpa profile kekal
// kelihatan di "Semua produk" (landing lalai) — jangan sekali-kali jadikan
// profile pertama sebagai penapis lalai, produk lama akan nampak "hilang".
const MAX_TIKTOK_PROFILES = 10;
const DEFAULT_PROFILE_SLOTS = 3;   // 1 lesen = 3-5 akaun TikTok

// ═══ Warna + ikon identiti profile (arahan user 2026-08-20) ═══
// Setiap akaun TikTok boleh diberi warna + ikon sendiri supaya user tidak
// silap akaun bila tengok sepintas lalu. Nilai warna TETAP (bukan token
// tema) — ia identiti akaun yang user pilih sendiri, terbaca pada tema
// terang DAN gelap kerana latar guna tint ~13% dan ikon/huruf guna warna
// penuh. Ikon = lucide (bukan emoji — v3.9 monokrom untuk selebihnya).
const PROFILE_COLORS = ['#64748b', '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#f59e0b', '#10b981', '#06b6d4'];
const PROFILE_ICON_KEYS = ['user', 'store', 'bag', 'heart', 'star', 'flame', 'sparkle', 'tag'];
const PROFILE_ICONS = { user: IkonProfil, store: Store, bag: ShoppingBag, heart: Heart, star: Star, flame: Flame, sparkle: Sparkles, tag: IkonTag };

// Relatif ringkas untuk "terakhir dikemas kini" pada kad profile. Guna
// kunci kamus supaya ikut bahasa. `ms` = milisaat aktiviti terakhir.
function masaLaluRingkas(ms, t) {
    if (!ms) return '';
    const hari = Math.floor((Date.now() - ms) / 86400000);
    if (hari <= 0) return t('products.time.today');
    if (hari === 1) return t('products.time.yesterday');
    if (hari < 7) return t('products.time.daysAgo', { n: hari });
    if (hari < 30) return t('products.time.weeksAgo', { n: Math.floor(hari / 7) });
    if (hari < 365) return t('products.time.monthsAgo', { n: Math.floor(hari / 30) });
    return t('products.time.yearsAgo', { n: Math.floor(hari / 365) });
}

// ═══ Paparan harga — dikongsi paparan Grid (kad) DAN Jadual ═══
// Nilai tersimpan kadangkala sudah membawa simbol mata wang sendiri
// (harga Malaysia datang dengan "RM" tercetak dari halaman TikTok), jadi
// awalan hanya ditambah bila nilai itu bermula dengan digit. Menambah
// "RM" tanpa semakan ini menghasilkan "RMRM311.21".
const formatPrice = (val) => {
    if (val === undefined || val === null || val === '') return '-';
    const s = String(val).trim();
    if (s === '-' || s === '') return '-';
    if (/^\D/.test(s)) return s;
    return `RM ${s}`;
};
const formatDiscount = (val) => {
    if (val === undefined || val === null || val === '') return '-';
    const s = String(val).trim();
    return s || '-';
};

// Anggaran cost per library item (Stage 1 vision + Stage 2 research, gpt-4o-mini)
const COST_PER_INTEL_USD = 0.0017;
// Konkurensi parallel — sesuai untuk OpenAI rate-limit Tier 1+
const INTEL_PARALLEL_LIMIT = 3;

/* ═══ Susunan produk v3.9 — SELARAS dengan senarai + pemilih Workspace ═══
   Halaman /products dulu menyenaraikan tiga pilihan sehala sahaja
   (Number / Name / Newest). Kawalan ini kini SAMA dengan Workspace:
   guna [SusunMenuV39.jsx] yang dikongsi, tambah susun ikut Status
   (READY dahulu), dan arah boleh diterbalikkan (tekan jenis yang sama
   sekali lagi). ⭐ Kunci kamus DIPINJAM daripada pemilih Workspace
   (`workspace.composer.picker.sort.*`) yang sudah wujud dalam KEDUA-DUA
   kamus — jadi tiada kunci baharu, tiada risiko nama kunci mentah.

   ⛔ `id` = nilai yang disimpan pada `sortBy` dan dibanding dalam logik
   susun. Ia KEKAL 'number'|'name'|'date'|'status' supaya rupa lama
   (yang juga membaca `sortBy`) tidak pecah. Hanya label/tip diterjemah. */
const SUSUNAN_PRODUK_V39 = [
    { id: 'number', kunci: 'workspace.composer.picker.sort.productId', lalaiTeks: 'Product ID', lalai: 'asc', tip: { asc: '1 → 10', desc: '10 → 1' } },
    {
        id: 'date', kunci: 'workspace.composer.picker.sort.time', lalaiTeks: 'Time', lalai: 'desc',
        kunciTip: { asc: 'workspace.composer.picker.sort.timeAsc', desc: 'workspace.composer.picker.sort.timeDesc' },
        tipTeks: { asc: 'Oldest first', desc: 'Newest first' },
    },
    { id: 'name', kunci: 'workspace.composer.picker.sort.name', lalaiTeks: 'Name', lalai: 'asc', tip: { asc: 'A → Z', desc: 'Z → A' } },
    /* Lalai READY dahulu — hanya produk READY yang berguna untuk ditarik
       ke Workspace, jadi melihat PENDING di atas jarang yang dicari.
       Arah terbalik kekal untuk pengguna yang mahu lihat yang belum siap. */
    {
        id: 'status', kunci: 'workspace.composer.picker.sort.status', lalaiTeks: 'Status', lalai: 'desc',
        kunciTip: { asc: 'workspace.composer.picker.sort.statusAsc', desc: 'workspace.composer.picker.sort.statusDesc' },
        tipTeks: { asc: 'Pending first', desc: 'Ready first' },
    },
];
// Arah lalai bagi setiap jenis — dipakai bila jenis BERTUKAR (rupa lama
// tukar `sortBy` sahaja, jadi peta ini yang mengekalkan perangai asalnya:
// number 1→10, name A→Z, date terbaru dahulu).
const ARAH_LALAI_PRODUK = { number: 'asc', name: 'asc', date: 'desc', status: 'desc' };

/* ═══ Panel kandungan v3.9 ═══════════════════════════════════════════
   Bentuk yang SAMA seperti prototaip /products-v3.9: jurang 10px dari
   tepi, panel #141414 di atas latar #111111, garis rambut keliling,
   bucu 16px, dan skrol berlaku DI DALAM panel.

   ⚠️ Ditakrif di peringkat modul, BUKAN dalam LibraryView. Komponen
      yang dicipta semula pada setiap render membawa identiti baharu
      setiap kali, jadi React akan membuang dan membina semula seluruh
      isi di dalamnya — carian hilang fokus setiap kali ditaip dan
      kedudukan skrol melompat ke atas.

   ⛔ DIBUANG 2026-08-11 — bingkai halaman kini datang daripada SATU
   tempat: `BingkaiHalaman` dalam App.jsx. Jangan hidupkan semula bekas
   tempatan di sini; itu akan menghasilkan bingkai dalam bingkai, dan
   nilainya akan terpesong daripada bingkai sebenar bila tema dilaras. */

// ═══════════════════════════════════════════════════════════
//  LIBRARY VIEW — Shared resource hub (admin-managed)
//  Phase 1: Library > Product (with Intelligence JSON)
//
//  `uiV39` datang sebagai PROP dari App.jsx (bukan dibaca semula di
//  sini) — corak sama seperti WorkspaceView: `useUiV39` membuka
//  pendengar Firestore, jadi memanggilnya kali kedua bermakna satu
//  lagi bacaan doc yang sama pada setiap sesi setiap browser.
// ═══════════════════════════════════════════════════════════

export default function LibraryView({ initialTab, onMenuClick, uiV39 = false }) {
    const user = auth.currentUser;
    const userRole = getUserRole(user?.email);
    const isAdmin = userRole === ROLES.ADMIN;
    const navigate = useNavigate();

    /* ⭐ DUA BAHASA TANPA MENGUBAH RUPA LAMA.
       Halaman ini melayan DUA rupa serentak: rupa LAMA (yang pelanggan
       nampak hari ini) dan rupa v3.9. Bahasa LALAI app ialah English,
       jadi memanggil t() tanpa syarat bermakna pelanggan pada rupa lama
       tiba-tiba nampak halaman English — regresi yang sampai kepada
       pelanggan sebenar.

       Oleh itu: `T(kunci, lalai)` memulangkan ayat asal huruf demi huruf
       pada rupa lama, dan hanya rupa v3.9 membaca kamus. Bila v3.9 sudah
       menjadi satu-satunya rupa, penampan ini boleh dibuang dan t()
       dipanggil terus. */
    const t = useT();
    const T = (kunci, lalai, vars) => (uiV39 ? t(kunci, vars) : lalai);

    // ═══ STATE ═══
    const [currentView, setCurrentView] = useState('products'); // Skip folders landing — go straight to products
    const [currentFolder, setCurrentFolder] = useState(null);

    // Folders (top-level library categories)
    const [folders, setFolders] = useState([]);
    const [loadingFolders, setLoadingFolders] = useState(true);

    // Products (items inside a folder/subfolder)
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const loadSeqRef = useRef(0); // guard: abaikan hasil load lama bila ada load lebih baru

    // Search, filter, sort, group
    const [searchQuery, setSearchQuery] = useState('');
    const [filterMode, setFilterMode] = useState('all'); // 'all' | 'with_intel' | 'without_intel'
    const [sortBy, setSortBy] = useState(initialTab === 'own' ? 'date' : 'number'); // 'number' | 'name' | 'date' | 'status'
    // Arah susun (v3.9). Rupa lama tak pernah tukar ini secara langsung — ia
    // hanya di-reset ikut jenis (ARAH_LALAI_PRODUK) bila sortBy berubah, jadi
    // perangai susun rupa lama kekal sama.
    const [sortDir, setSortDir] = useState(ARAH_LALAI_PRODUK[initialTab === 'own' ? 'date' : 'number']); // 'asc' | 'desc'
    const [productGroup, setProductGroup] = useState(initialTab || 'system'); // 'system' | 'own'

    // Sync tab when URL changes (e.g. /products/system ↔ /products/myproducts)
    useEffect(() => {
        if (initialTab) {
            setProductGroup(initialTab);
            setSortBy(initialTab === 'own' ? 'date' : 'number');
        }
    }, [initialTab]);

    // Custom product groups
    const [customGroupFilter, setCustomGroupFilter] = useState('all'); // 'all' | 'uncategorized' | groupId

    // ═══ PAPARAN: Jadual (senarai) atau Grid (kad) ═══
    // LALAI = JADUAL/senarai (arahan user 2026-08-21). Pilihan diingat
    // per-browser; terpakai pada KEDUA-DUA tab (System + My Products), rupa
    // v3.9 sahaja.
    // ⛔ NILAI DITULIS HANYA BILA PENGGUNA MENEKAN SUIS — jangan pulangkan
    //    tulisan-pada-mount. Versi lama menyimpan nilai dalam useEffect yang
    //    turut berjalan pada mount, jadi SETIAP browser yang pernah membuka
    //    halaman ini terus menyimpan 'grid' walaupun penggunanya tidak pernah
    //    memilih apa-apa. Kesannya menukar lalai di sini langsung TIDAK
    //    berkesan untuk mereka — lalai baharu hanya kelihatan pada browser
    //    yang betul-betul baharu. Sekarang "tiada nilai tersimpan" benar-benar
    //    bermakna "pengguna belum pernah memilih".
    // ⚠️ NAMA KUNCI DINAIKKAN ke _v2 atas sebab yang sama: kunci LAMA sudah
    //    tertulis 'grid' pada setiap browser yang pernah membuka halaman ini
    //    (kesan tulisan-pada-mount di atas), jadi membiarkan nama lama
    //    bermakna lalai baharu ini tidak akan pernah kelihatan. Nilai lama itu
    //    BUKAN pilihan pengguna — ia hanya lalai lama yang tercatat sendiri.
    //    (Kunci modal Workspace SENGAJA tidak dinaikkan: lalai di sana memang
    //    sudah senarai, jadi 'grid' yang tersimpan di situ ialah pilihan
    //    sebenar pengguna dan tidak patut dipadam.)
    const [paparanProduk, setPaparanProduk] = useState(() => {
        try {
            const v = localStorage.getItem('sesaat_products_paparan_v2');
            return v === 'grid' ? 'grid' : 'jadual';
        } catch { return 'jadual'; }
    });
    const tukarPaparanProduk = (mod) => {
        setPaparanProduk(mod);
        try { localStorage.setItem('sesaat_products_paparan_v2', mod); } catch { /* mod privasi */ }
    };

    /* Saiz gambar dalam mod senarai — 's' | 'm' | 'l'. Pilihan diingat
       per-browser dan DIKONGSI dengan modal "Add products" di Workspace
       (lihat utils/saizGambarSenarai.js untuk sebab satu kunci sahaja).
       ⛔ Ditulis hanya di sini, iaitu tepat pada klik pengguna — jangan
          pindahkan ke useEffect. */
    const [saizGambar, setSaizGambar] = useState(bacaSaizGambar);
    const tukarSaizGambar = (v) => { setSaizGambar(v); simpanSaizGambar(v); };
    /* Kawalannya duduk dalam KEPALA jadual, tepat di atas lajur gambar
       (arahan user 2026-08-26) — tekan sekali, saiz berganjak ke saiz
       berikutnya dan berpusing balik ke kecil. Satu petak sahaja, jadi ia
       tidak merampas ruang bar alat yang sudah penuh. */
    const kitarSaizGambar = () => {
        const i = SAIZ_PILIHAN.indexOf(saizGambar);
        tukarSaizGambar(SAIZ_PILIHAN[(i + 1) % SAIZ_PILIHAN.length]);
    };
    const namaSaizGambar = {
        s: T('products.view.imgS', 'Kecil'),
        m: T('products.view.imgM', 'Sederhana'),
        l: T('products.view.imgL', 'Besar'),
    }[saizGambar];
    const gbrPx = SAIZ_SENARAI[saizGambar] || SAIZ_SENARAI.s;

    // ═══ PROFILE TIKTOK ═══
    const [profileFilter, setProfileFilter] = useState('all');       // 'all' | 'none' | profileId
    const [profileModal, setProfileModal] = useState(null);          // { mode:'add'|'edit', id?, name, username }
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileMenu, setProfileMenu] = useState(null);            // { mode:'move'|'copy' } — dropdown bar pukal
    const [bulkProfileBusy, setBulkProfileBusy] = useState(false);
    const [groupToProfile, setGroupToProfile] = useState(null);      // { groupId, groupName } — modal pindah group
    const [showManageGroups, setShowManageGroups] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupColor, setNewGroupColor] = useState('#6366f1');
    const [savingGroup, setSavingGroup] = useState(false);
    const [assigningGroupProductId, setAssigningGroupProductId] = useState(null);
    const [groupMenuRect, setGroupMenuRect] = useState(null);
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editGroupName, setEditGroupName] = useState('');
    const [renamingGroup, setRenamingGroup] = useState(false);

    // Admin modals
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const [bulkText, setBulkText] = useState('');
    const [bulkUploading, setBulkUploading] = useState(false);
    const [showManualAdd, setShowManualAdd] = useState(false);
    const [importOpen, setImportOpen] = useState(false); // Fasa 2 — modal Import Produk (stepper)
    const [importPlatform, setImportPlatform] = useState('tiktok'); // ★ 2026-10-03 — tab platform modal import: 'tiktok' | 'shopee'
    const [manualEntries, setManualEntries] = useState([]);
    const [manualSaving, setManualSaving] = useState(false);
    const manualFileRefs = useRef({});

    // Intelligence JSON modal (Edit — admin/owner paste/edit JSON)
    const [intelModalProduct, setIntelModalProduct] = useState(null);
    const [intelJsonText, setIntelJsonText] = useState('');
    const [savingIntel, setSavingIntel] = useState(false);
    const [intelModalLoading, setIntelModalLoading] = useState(false);

    // View Intelligence modal (read-only for non-admin)
    const [viewIntelProduct, setViewIntelProduct] = useState(null);
    const [viewIntelData, setViewIntelData] = useState(null);
    const [viewIntelLoading, setViewIntelLoading] = useState(false);

    // View Scraped Data modal (for imported products)
    const [scrapedDataProduct, setScrapedDataProduct] = useState(null);
    const [scrapedDataLoading, setScrapedDataLoading] = useState(false);

    // Edit folder name
    const [editingFolderName, setEditingFolderName] = useState(null);
    const [editFolderValue, setEditFolderValue] = useState('');

    // Edit product (inline)
    const [editingProductId, setEditingProductId] = useState(null);
    // Kad yang sedang dibuka dalam borang "Lengkapkan Data Produk" (taip sendiri).
    const [completeDataProduct, setCompleteDataProduct] = useState(null);
    // Link mana yang baru disalin — tukar ikon jadi tanda semak sekejap.
    const [copiedLinkId, setCopiedLinkId] = useState(null);
    const [editProductData, setEditProductData] = useState({ number: '', name: '', link: '', price: '', originalPrice: '', discount: '' });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 50;

    // ═══ ADMIN INTELLIGENCE GENERATION (auto Stage 1+2) ═══
    // Bulk select mode: admin select multiple cards untuk generate sekaligus
    const [intelSelectMode, setIntelSelectMode] = useState(false);
    const [intelSelectedIds, setIntelSelectedIds] = useState(() => new Set());
    // Per-item generation status — Map: itemId → { status, error?, partial? }
    const [intelGenStatus, setIntelGenStatus] = useState({});
    const [intelBatchActive, setIntelBatchActive] = useState(false);
    const [intelBatchCancelled, setIntelBatchCancelled] = useState(false);
    // Bulk confirm modal
    const [intelBulkConfirm, setIntelBulkConfirm] = useState(null); // { items, forceRefresh }

    // ═══ BULK SELECT (admin: System+MyProducts, user: MyProducts) ═══
    // Selection-driven: checkbox selalu visible pada card, action bar muncul bila ≥1 selected.
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [lastAnchorId, setLastAnchorId] = useState(null); // anchor for shift+click range
    const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(null); // { items }
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [showMoveToGroup, setShowMoveToGroup] = useState(false); // dropdown popup
    const [bulkMoving, setBulkMoving] = useState(false);
    const [bulkSending, setBulkSending] = useState(false); // "Send to Workspace" sedang baca produk segar
    /* Pagar SERTA-MERTA untuk hantaran async (state `bulkSending` hanya berkesan
       selepas render seterusnya — dua klik pantas boleh sama-sama lepas, dan yang
       kedua meninggalkan hantaran basi yang di-stage semula lain kali Workspace dibuka).
       `halamanHidupRef`: pelanggan tekan menu lain semasa "Processing…" → jangan
       rampas navigasinya ke Workspace. Diset `true` DALAM effect (bukan nilai awal
       sahaja) supaya kitaran pasang-buka-pasang StrictMode tidak meninggalkannya false. */
    const sedangHantarRef = useRef(false);
    const halamanHidupRef = useRef(true);
    useEffect(() => {
        halamanHidupRef.current = true;
        return () => { halamanHidupRef.current = false; };
    }, []);
    const [bulkClearIntelConfirm, setBulkClearIntelConfirm] = useState(null); // { items } admin only
    const [bulkClearingIntel, setBulkClearingIntel] = useState(false);

    // Schema v2 migration (admin only) — split heavy fields ke subdoc

    // ═══ LOAD FOLDERS — one-shot getDocs (no persistent listener) ═══
    const loadFolders = async () => {
        try {
            const snap = await getDocs(collection(db, 'library'));
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            data.sort((a, b) => (a.order || 0) - (b.order || 0));
            setFolders(data);
        } catch (err) {
            console.error('[Library] Firestore read error:', err);
        } finally {
            setLoadingFolders(false);
        }
    };
    // Mount: pakai folder ID terakhir dari localStorage (optimistic) supaya loadProducts run paralel dgn loadFolders.
    // Reconcile bila folders fresh tiba (effect di bawah).
    useEffect(() => {
        const cachedId = localStorage.getItem('library_last_folder_id');
        if (cachedId) setCurrentFolder({ id: cachedId, name: '...', _optimistic: true });
        loadFolders();
    }, []);

    // ═══ LOAD PRODUCTS — ambik yang BERKAITAN sahaja (bukan 51k semua user) ═══
    // Koleksi items simpan produk peribadi SEMUA user (group 'own'). User ni cuma
    // perlu nampak: (a) produk System (21, shared) + (b) produk dia sendiri.
    // Dua query kecil ni ganti satu query gergasi → ~87 doc, bukan 51,289.
    // loadSeqRef: abaikan hasil load lama bila ada load lebih baru.
    const loadProducts = async () => {
        if (!currentFolder) return;
        const seq = ++loadSeqRef.current;
        const folderId = currentFolder.id;
        setLoadingProducts(true);
        try {
            const itemsRef = collection(db, 'library', folderId, 'items');

            // (a) Produk System (admin-curated, shared) — semua user nampak
            const queries = [getDocs(query(itemsRef, where('group', '==', 'system')))];
            // (b) Produk milik user ni sahaja (My Products)
            if (user?.email) {
                queries.push(getDocs(query(itemsRef, where('addedBy', '==', user.email))));
            }

            const snaps = await Promise.all(queries);
            if (seq !== loadSeqRef.current) return; // load lain dah ambil alih

            // Gabung + buang duplikat (cth admin yang cipta produk system pun jadi addedBy dia)
            const byId = new Map();
            snaps.forEach(snap => snap.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() })));
            const loaded = [...byId.values()];
            setProducts(loaded);

            // ═══ AUTO-PULIH KAD PENDING (senyap) ═══
            // Kad yang datanya sebenarnya sudah cukup terus jadi READY tanpa
            // pelanggan buat apa-apa. Jalan di latar — jangan sekat paparan.
            repairPendingLibraryCards({ folderId, products: loaded, email: user?.email })
                .then(({ updates }) => {
                    if (!updates?.length || seq !== loadSeqRef.current) return;
                    const byUpdateId = new Map(updates.map(u => [u.id, u]));
                    setProducts(prev => prev.map(p => byUpdateId.has(p.id) ? { ...p, ...byUpdateId.get(p.id) } : p));
                })
                .catch(err => console.warn('[Library] auto-pulih gagal:', err?.message));
        } catch (err) {
            console.error('[Library] Products read error:', err);
        } finally {
            if (seq === loadSeqRef.current) setLoadingProducts(false);
        }
    };
    useEffect(() => { loadProducts(); }, [currentFolder?.id]);

    // ═══ AUTO-SEED: Create "Product" folder if empty ═══
    useEffect(() => {
        if (!isAdmin || loadingFolders) return;
        if (folders.length === 0) {
            // Seed default "Product" folder
            const seedData = async () => {
                try {
                    await setDoc(doc(db, 'library', 'product'), {
                        name: 'Product',
                        icon: 'inventory_2',
                        order: 1,
                        description: 'Product data collection with Intelligence JSON',
                        accessRoles: ['admin', 'moderator', 'client'],
                        createdAt: serverTimestamp(),
                    });
                    console.log('[Library] Seeded default Product folder');
                    await loadFolders();
                } catch (e) {
                    console.error('[Library] Seed error:', e);
                }
            };
            seedData();
        }
    }, [isAdmin, loadingFolders, folders.length]);

    // ═══ AUTO-SELECT / RECONCILE first folder ═══
    // Bila folders fresh tiba: upgrade currentFolder optimistic ke full data,
    // atau fallback ke folder pertama kalau cached ID dah hilang.
    useEffect(() => {
        if (loadingFolders || folders.length === 0) return;
        const match = folders.find(f => f.id === currentFolder?.id);
        if (match && currentFolder?._optimistic) {
            setCurrentFolder(match); // upgrade optimistic → full data
        } else if (!currentFolder) {
            setCurrentFolder(folders[0]); // tiada cached, pakai pertama
        } else if (!match) {
            console.warn('[Library] Cached folder gone, fallback to first');
            setCurrentFolder(folders[0]);
        }
    }, [loadingFolders, folders]);

    // Cache folder ID setiap kali user tukar (skip optimistic state)
    useEffect(() => {
        if (currentFolder?.id && !currentFolder._optimistic) {
            localStorage.setItem('library_last_folder_id', currentFolder.id);
        }
    }, [currentFolder?.id, currentFolder?._optimistic]);

    // Folder-level groups (System tab — admin managed)
    const productGroups = useMemo(() => {
        const folder = folders.find(f => f.id === currentFolder?.id);
        return folder?.productGroups || [];
    }, [folders, currentFolder?.id]);

    // User-specific groups (My Products tab — per user, stored in users/{uid})
    // Profile TikTok dibaca dari listener yang SAMA — tiada bacaan tambahan.
    const [userLibraryGroups, setUserLibraryGroups] = useState([]);
    const [userProfiles, setUserProfiles] = useState([]);
    // ★ 2026-10-03 — storefront Shopee tersimpan (dropdown Scrape Storefront), dari listener yang SAMA.
    const [userShopeeStorefronts, setUserShopeeStorefronts] = useState([]);
    useEffect(() => {
        if (!user?.uid) return;
        const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
            const d = snap.exists() ? snap.data() : {};
            setUserShopeeStorefronts(kemasStorefrontTersimpan(d.shopeeStorefronts));
            setUserLibraryGroups(d.libraryGroups || []);
            setUserProfiles(Array.isArray(d.tiktokProfiles) ? d.tiktokProfiles : []);
        });
        return () => unsub();
    }, [user?.uid]);

    // Active groups — ikut tab semasa
    const activeGroups = productGroup === 'own' ? userLibraryGroups : productGroups;

    // ═══ GROUP YATIM — produk yang tertinggal group yang sudah dipadam ═══
    // (semakan user 2026-08-21) Satu produk dalam data sebenar masih membawa id
    // group yang tiada dalam senarai. Kesannya produk itu HILANG daripada semua
    // pil: ia tidak padan mana-mana group, dan ia juga tidak dikira "Tiada
    // Group" kerana medan customGroup-nya tidak kosong. Kiraan pil jadi 89
    // sedangkan jumlah 90 — pelanggan membacanya sebagai kiraan yang tak tally.
    // Penyelesaian di sini ialah PAPARAN sahaja: group yang sudah tiada dikira
    // sama seperti "tiada group", jadi jumlah sentiasa cukup DAN produk itu
    // boleh dijumpai semula lalu ditukar group. Tiada data ditulis.
    // ⛔ Peraturan: mana-mana tempat yang menyemak `!p.customGroup` mesti guna
    //    `tiadaGroup(p)` — kalau tidak, kiraan dan penapis akan bercanggah balik.
    const idGroupWujud = useMemo(() => new Set(activeGroups.map(g => g.id)), [activeGroups]);
    const tiadaGroup = (p) => !p.customGroup || !idGroupWujud.has(p.customGroup);

    // Group yang boleh dipilih untuk SATU produk = HANYA group milik profil produk
    // itu (arahan user 2026-08-22). Produk tanpa profil → group tanpa profil sahaja.
    // Kekalkan group semasa walaupun di luar skop (data lama silang-profil) supaya
    // pemilih tidak keluar KOSONG & nampak rosak. Dipakai oleh pemilih group baris
    // jadual DAN menu group kad — satu takrifan supaya dua tempat tak terpesong.
    const groupsForProduct = (p) => {
        const skop = activeGroups.filter(g => p?.profileId ? g.profileId === p.profileId : !g.profileId);
        const cur = p?.customGroup;
        if (cur && idGroupWujud.has(cur) && !skop.some(g => g.id === cur)) {
            const extra = activeGroups.find(g => g.id === cur);
            if (extra) return [...skop, extra];
        }
        return skop;
    };

    // ═══ FILTERED + SORTED PRODUCTS ═══
    const filteredProducts = useMemo(() => {
        let result = [...products];

        // Group filter
        if (productGroup === 'system') {
            result = result.filter(p => !p.group || p.group === 'system');
        } else {
            result = result.filter(p => p.group === 'own' && p.addedBy === user?.email);
        }

        // Custom group filter
        if (customGroupFilter === 'uncategorized') {
            result = result.filter(p => !p.customGroup || !idGroupWujud.has(p.customGroup));
        } else if (customGroupFilter !== 'all') {
            result = result.filter(p => p.customGroup === customGroupFilter);
        }

        // Penapis profile TikTok — My Products sahaja. 'all' = SEMUA produk
        // termasuk yang tiada profile (produk lama tidak boleh "hilang").
        if (productGroup === 'own') {
            if (profileFilter === 'none') {
                result = result.filter(p => !p.profileId);
            } else if (profileFilter !== 'all') {
                result = result.filter(p => p.profileId === profileFilter);
            }
        }

        // Search
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(p =>
                p.name?.toLowerCase().includes(q) ||
                String(p.number).includes(q) ||
                p.link?.toLowerCase().includes(q)
            );
        }

        // Filter
        if (filterMode === 'with_intel') {
            result = result.filter(p => p.hasIntelligence);
        } else if (filterMode === 'without_intel') {
            result = result.filter(p => !p.hasIntelligence);
        }

        // Sort — arah (sortDir) kini boleh diterbalikkan (v3.9). Setiap
        // pembanding ditulis dalam semantik ASC, kemudian didarab `dir`:
        // asc → +1 (turutan asal), desc → −1 (songsang). Lalai tiap jenis
        // (ARAH_LALAI_PRODUK) menjaga perangai rupa lama tak berubah.
        const dir = sortDir === 'asc' ? 1 : -1;
        if (sortBy === 'name') {
            // asc = A → Z
            result.sort((a, b) => dir * (a.name || '').localeCompare(b.name || ''));
        } else if (sortBy === 'date') {
            // asc = lama dahulu, desc = terbaru dahulu
            result.sort((a, b) => {
                const da = a.updatedAt?.toDate?.() || a.addedAt?.toDate?.() || new Date(0);
                const db2 = b.updatedAt?.toDate?.() || b.addedAt?.toDate?.() || new Date(0);
                return dir * (da - db2);
            });
        } else if (sortBy === 'status') {
            // READY = hasIntelligence (1), PENDING = 0. desc = READY dahulu.
            result.sort((a, b) => dir * ((a.hasIntelligence ? 1 : 0) - (b.hasIntelligence ? 1 : 0)));
        } else {
            // asc = 1 → 10
            result.sort((a, b) => dir * ((a.number || 0) - (b.number || 0)));
        }

        return result;
        // idGroupWujud WAJIB ada dalam senarai ini: tanpa ia, memadam satu group
        // tidak akan mengira semula senarai, jadi kiraan pil bergerak sementara
        // senarai di bawahnya kekal seperti lama.
    }, [products, searchQuery, filterMode, sortBy, sortDir, productGroup, customGroupFilter, profileFilter, idGroupWujud]);

    // Group counts
    const systemCount = products.filter(p => !p.group || p.group === 'system').length;
    const ownCount = products.filter(p => p.group === 'own' && p.addedBy === user?.email).length;

    // ═══════════════════════════════════════════════════════════
    //  ADMIN INTEL GENERATION — Helpers (parallel-3, per-item)
    // ═══════════════════════════════════════════════════════════

    // Toggle bulk select mode — clear selections bila tutup
    const toggleIntelSelectMode = () => {
        setIntelSelectMode(prev => {
            const next = !prev;
            if (next) {
                // Turn ON intel mode — clear any delete selection (mutual-exclusive)
                if (selectedIds.size > 0) {
                    setSelectedIds(new Set());
                    setLastAnchorId(null);
                }
            } else {
                setIntelSelectedIds(new Set());
            }
            return next;
        });
    };

    const toggleIntelSelected = (itemId) => {
        setIntelSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) next.delete(itemId);
            else next.add(itemId);
            return next;
        });
    };

    const selectAllPending = () => {
        const pending = filteredProducts.filter(p => !p.hasIntelligence).map(p => p.id);
        setIntelSelectedIds(new Set(pending));
    };

    const clearIntelSelection = () => setIntelSelectedIds(new Set());

    // Worker: process satu item, update genStatus
    const processIntelItem = async (item, forceRefresh) => {
        if (!currentFolder?.id) return;
        setIntelGenStatus(s => ({ ...s, [item.id]: { status: 'running' } }));
        try {
            const result = await aiService.generateLibraryIntelligence({
                folderId: currentFolder.id,
                itemId: item.id,
                forceRefresh: !!forceRefresh,
            });
            setIntelGenStatus(s => ({
                ...s,
                [item.id]: {
                    status: 'done',
                    partial: !!result?.partial,
                    durationMs: result?.durationMs,
                },
            }));
        } catch (err) {
            console.error(`[LibIntel] item ${item.id} failed:`, err);
            setIntelGenStatus(s => ({
                ...s,
                [item.id]: { status: 'error', error: err?.message || T('acc.unknownError', 'Unknown error') },
            }));
        }
    };

    // Per-item generate (single, no bulk modal)
    const handleGenerateOne = async (item, forceRefresh = false) => {
        if (!isAdmin || !currentFolder?.id) return;
        if (forceRefresh && item.hasIntelligence) {
            const ok = window.confirm(T(
                'products.intel.confirmRegenerate',
                `Re-generate intelligence untuk "${item.name}"?\n\nIni akan replace cache sedia ada (cost ~$${COST_PER_INTEL_USD.toFixed(4)}).`,
                { nama: item.name, kos: COST_PER_INTEL_USD.toFixed(4) }
            ));
            if (!ok) return;
        }
        await processIntelItem(item, forceRefresh);
        await loadProducts();
    };

    // Bulk generate — parallel-3 controller dengan resubmit support
    const runIntelBatch = async (items, forceRefresh) => {
        if (!items?.length) return;
        setIntelBatchActive(true);
        setIntelBatchCancelled(false);

        // Reset status untuk items yang akan di-process (kekal yang done dari run sebelum kecuali kita override)
        setIntelGenStatus(s => {
            const next = { ...s };
            items.forEach(it => { next[it.id] = { status: 'pending' }; });
            return next;
        });

        let nextIndex = 0;
        const cancelledRef = { current: false };
        // Sync cancelledRef dengan state — checked per worker iteration
        const cancelChecker = setInterval(() => {
            // Read state via setState callback (avoid stale closure)
            setIntelBatchCancelled(cur => {
                if (cur) cancelledRef.current = true;
                return cur;
            });
        }, 250);

        try {
            const runners = Array.from({ length: INTEL_PARALLEL_LIMIT }, async () => {
                while (true) {
                    if (cancelledRef.current) return;
                    const i = nextIndex++;
                    if (i >= items.length) return;
                    await processIntelItem(items[i], forceRefresh);
                }
            });
            await Promise.all(runners);
        } finally {
            clearInterval(cancelChecker);
            setIntelBatchActive(false);
            setIntelBatchCancelled(false);
            await loadProducts();
        }
    };

    // Open confirm modal for bulk run (so admin nampak cost dulu)
    const openBulkConfirm = (forceRefresh = false) => {
        const selected = filteredProducts.filter(p => intelSelectedIds.has(p.id));
        if (selected.length === 0) return;
        setIntelBulkConfirm({ items: selected, forceRefresh });
    };

    const confirmBulkRun = async () => {
        if (!intelBulkConfirm) return;
        const { items, forceRefresh } = intelBulkConfirm;
        setIntelBulkConfirm(null);
        await runIntelBatch(items, forceRefresh);
    };

    // ═══════════════════════════════════════════════════════════
    //  BULK DELETE MODE — Helpers (parallel-3, mutual-exclusive dengan Intel mode)
    // ═══════════════════════════════════════════════════════════

    const toggleSelected = (itemId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) next.delete(itemId);
            else next.add(itemId);
            return next;
        });
    };

    // Esc keyboard — close dropdown first, kalau takda then clear selection.
    // Ignore bila taip dalam input/textarea atau modal open.
    useEffect(() => {
        if (selectedIds.size === 0 && !showMoveToGroup) return;
        const handler = (e) => {
            if (e.key !== 'Escape') return;
            const tag = (e.target?.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
            // Don't fire if any confirm modal is open
            if (bulkDeleteConfirm || bulkClearIntelConfirm) return;
            // Close dropdown first
            if (showMoveToGroup) {
                setShowMoveToGroup(false);
                return;
            }
            // Clear selection
            setSelectedIds(new Set());
            setLastAnchorId(null);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [selectedIds.size, bulkDeleteConfirm, bulkClearIntelConfirm, showMoveToGroup]);

    // Click-outside — clear selection bila klik di luar card/action bar/dropdown.
    // Skip kalau modal open atau klik dalam input/textarea.
    useEffect(() => {
        if (selectedIds.size === 0) return;
        const handler = (e) => {
            if (bulkDeleteConfirm || bulkClearIntelConfirm) return;
            if (!e.target || !(e.target instanceof Element)) return;
            // Tolerate clicks on form inputs (e.g. search box) — user mungkin nak filter sambil ada selection
            const tag = (e.target.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;
            // Klik dalam "keep zone" — jangan clear
            if (e.target.closest('[data-keep-selection]')) return;
            // Klik luar zone — clear
            setSelectedIds(new Set());
            setLastAnchorId(null);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [selectedIds.size, bulkDeleteConfirm, bulkClearIntelConfirm]);

    // Shift+click range select — pakai paginatedProducts (visible) sebagai range basis.
    // Klik biasa = toggle satu + set anchor.
    // Shift+klik = select range dari anchor ke target (semua dalam range ADD, tak toggle).
    const handleCardSelect = (productId, event) => {
        if (event?.shiftKey && lastAnchorId && lastAnchorId !== productId) {
            const ids = paginatedProducts.map(p => p.id);
            const i1 = ids.indexOf(lastAnchorId);
            const i2 = ids.indexOf(productId);
            if (i1 >= 0 && i2 >= 0) {
                const [start, end] = i1 < i2 ? [i1, i2] : [i2, i1];
                const rangeIds = ids.slice(start, end + 1);
                setSelectedIds(prev => {
                    const next = new Set(prev);
                    rangeIds.forEach(id => {
                        const p = paginatedProducts.find(pp => pp.id === id);
                        const canDel = isAdmin || (p?.group === 'own' && p?.addedBy === user?.email);
                        if (canDel) next.add(id);
                    });
                    return next;
                });
                setLastAnchorId(productId); // update anchor ke target terkini
                return;
            }
        }
        // Default: toggle + set anchor
        toggleSelected(productId);
        setLastAnchorId(productId);
    };

    // Produk yang pengguna ini benar-benar boleh pilih — pagar yang SAMA seperti
    // laluan padam (admin: semua; pelanggan: produk sendiri sahaja).
    const bolehDipilih = (p) => isAdmin || (p.group === 'own' && p.addedBy === user?.email);

    /* ═══ DUA TINDAKAN BERASINGAN (arahan user 2026-08-21) ═══
       Dulu hanya ada SATU "Select all", dan ia memilih seluruh hasil tapisan.
       Pada halaman 2 yang cuma ada 5 baris, ia memilih 55 — pengguna menekan
       sesuatu berlabel "semua", nampak 5 baris di depan mata, kemudian mendapat
       55. Tindakan borong yang salah baca begini boleh memadam benda yang tidak
       dijangka.
       Sekarang: kotak tanda kepala jadual = HALAMAN INI sahaja (dan betul-betul
       boleh dibuang); bar hitam beri dua butang, setiap satu bawa nombornya. */

    // Kotak tanda kepala jadual — TOGGLE halaman semasa sahaja.
    // ⚠️ Ia mesti bergabung dengan pilihan sedia ada, bukan menggantikannya:
    // pengguna boleh menanda halaman 1, pergi halaman 2, dan menanda halaman itu
    // juga. Mengganti akan membuang pilihan halaman sebelumnya secara senyap.
    const togglePilihHalaman = () => {
        const senarai = paginatedProducts.filter(bolehDipilih);
        if (senarai.length === 0) return;
        const semuaSudah = senarai.every(p => selectedIds.has(p.id));
        setSelectedIds(prev => {
            const baru = new Set(prev);
            senarai.forEach(p => { if (semuaSudah) baru.delete(p.id); else baru.add(p.id); });
            return baru;
        });
    };

    // Butang "Pilih halaman (N)" — sentiasa TAMBAH, tidak pernah membuang.
    // (Butang berlabel "pilih" yang kadangkala membuang pilihan ialah perangkap.)
    const pilihHalamanIni = () => {
        setSelectedIds(prev => {
            const baru = new Set(prev);
            paginatedProducts.filter(bolehDipilih).forEach(p => baru.add(p.id));
            return baru;
        });
    };

    // Butang "Pilih semua N" — seluruh hasil tapisan, merentas semua halaman.
    // Inilah perangai LAMA fungsi ini; ia dikekalkan sebagai satu-satunya butang
    // pada rupa lama supaya rupa itu tidak berubah langsung.
    const selectAllVisible = () => {
        setSelectedIds(new Set(filteredProducts.filter(bolehDipilih).map(p => p.id)));
    };

    const clearSelection = () => setSelectedIds(new Set());

    const openBulkDeleteConfirm = () => {
        const selected = filteredProducts.filter(p => selectedIds.has(p.id));
        const deletable = selected.filter(p =>
            isAdmin || (p.group === 'own' && p.addedBy === user?.email)
        );
        if (deletable.length === 0) return;
        setBulkDeleteConfirm({ items: deletable });
    };

    const handleBulkDelete = async () => {
        if (!bulkDeleteConfirm || !currentFolder) return;
        const items = bulkDeleteConfirm.items;
        setBulkDeleting(true);
        try {
            // Parallel-3 worker
            let idx = 0;
            const workers = Array.from({ length: 3 }, async () => {
                while (idx < items.length) {
                    const p = items[idx++];
                    try {
                        await deleteLibraryPayload(currentFolder.id, p.id);
                        await deleteDoc(doc(db, 'library', currentFolder.id, 'items', p.id));
                        await padamGambarSimpanan(p.imageUrl); // gambar simpanan kita ikut terpadam + kiraan ditolak
                        for (const u of (Array.isArray(p.extraImageUrls) ? p.extraImageUrls : [])) await padamGambarSimpanan(u); // gambar tambahan milik sendiri juga
                    } catch (err) {
                        console.error(`[Library] Bulk delete item ${p.id} failed:`, err);
                    }
                }
            });
            await Promise.all(workers);
            setBulkDeleteConfirm(null);
            setSelectedIds(new Set());
            setLastAnchorId(null);
            await loadProducts();
        } catch (err) {
            console.error('[Library] Bulk delete error:', err);
            alert(T('products.alert.bulkDeleteFailed', 'Gagal padam bulk: ' + (err?.message || err), { ralat: err?.message || err }));
        } finally {
            setBulkDeleting(false);
        }
    };

    // ═══ BULK MOVE TO GROUP — batch writeBatch (max 500 ops, but we cap selection lower) ═══
    const handleBulkMoveToGroup = async (groupId) => {
        if (!currentFolder) return;
        const selected = filteredProducts.filter(p =>
            selectedIds.has(p.id) && (isAdmin || (p.group === 'own' && p.addedBy === user?.email))
        );
        if (selected.length === 0) return;
        setBulkMoving(true);
        setShowMoveToGroup(false);
        // Group ada akaun → produk own auto-ikut akaun group itu.
        const targetProfile = productGroup === 'own' ? groupProfileId(groupId) : null;
        try {
            // Firestore writeBatch limit 500 ops — chunk kalau lebih
            const CHUNK = 400;
            for (let i = 0; i < selected.length; i += CHUNK) {
                const slice = selected.slice(i, i + CHUNK);
                const batch = writeBatch(db);
                slice.forEach(p => {
                    const patch = {
                        customGroup: groupId,
                        updatedAt: serverTimestamp(),
                        updatedBy: user.email,
                    };
                    if (targetProfile && p.group === 'own' && p.addedBy === user?.email) patch.profileId = targetProfile;
                    batch.update(doc(db, 'library', currentFolder.id, 'items', p.id), patch);
                });
                await batch.commit();
            }
            setSelectedIds(new Set());
            setLastAnchorId(null);
            await loadProducts();
        } catch (err) {
            console.error('[Library] Bulk move error:', err);
            alert(T('products.alert.moveToGroupFailed', 'Gagal move ke group: ' + (err?.message || err), { ralat: err?.message || err }));
        } finally {
            setBulkMoving(false);
        }
    };

    // ═══════════════════════════════════════════════════════════════
    //  PROFILE TIKTOK — cipta / namakan semula / padam
    //  Disimpan dalam users/{uid}.tiktokProfiles (array).
    // ═══════════════════════════════════════════════════════════════
    const handleSaveProfile = async () => {
        if (!profileModal || !user?.uid) return;
        const nama = (profileModal.name || '').trim();
        if (!nama) { alert(T('products.profile.nameRequired', 'Sila masukkan nama profile.')); return; }
        const username = (profileModal.username || '').trim().replace(/^@+/, '');
        const bertindih = userProfiles.some(p =>
            p.id !== profileModal.id && p.name.toLowerCase() === nama.toLowerCase()
        );
        if (bertindih) { alert(T('products.profile.nameExists', 'Nama profile ini sudah wujud.')); return; }

        setSavingProfile(true);
        try {
            // Warna + ikon identiti akaun (pilihan). Simpan hanya nilai sah.
            const warna = PROFILE_COLORS.includes(profileModal.color) ? profileModal.color : PROFILE_COLORS[0];
            const ikon = PROFILE_ICON_KEYS.includes(profileModal.icon) ? profileModal.icon : null;
            let senarai;
            if (profileModal.mode === 'edit') {
                senarai = userProfiles.map(p => p.id === profileModal.id ? { ...p, name: nama, username, color: warna, icon: ikon } : p);
            } else {
                if (userProfiles.length >= MAX_TIKTOK_PROFILES) {
                    alert(T('products.profile.limitReached', `Had ${MAX_TIKTOK_PROFILES} profile telah dicapai.`, { n: MAX_TIKTOK_PROFILES }));
                    setSavingProfile(false);
                    return;
                }
                senarai = [...userProfiles, { id: `tp_${Date.now().toString(36)}`, name: nama, username, color: warna, icon: ikon }];
            }
            await updateDoc(doc(db, 'users', user.uid), { tiktokProfiles: senarai });
            setProfileModal(null);
        } catch (err) {
            console.error('[Library] Simpan profile gagal:', err);
            alert(T('products.profile.saveFailed', 'Gagal simpan profile: ' + (err?.message || err), { ralat: err?.message || err }));
        } finally {
            setSavingProfile(false);
        }
    };

    // Padam profile — produk TIDAK dipadam, hanya tag profileId dilepaskan.
    const handleDeleteProfile = async (profileId) => {
        if (!user?.uid) return;
        const pr = userProfiles.find(p => p.id === profileId);
        const bilangan = products.filter(p => p.profileId === profileId).length;
        const msg = bilangan > 0
            ? T('products.profile.confirmDeleteWithProducts',
                `Padam profile "${pr?.name}"?\n\n${bilangan} produk di dalamnya TIDAK akan dipadam — ia akan kembali menjadi "Tanpa profile".`,
                { nama: pr?.name, n: bilangan })
            : T('products.profile.confirmDelete', `Padam profile "${pr?.name}"?`, { nama: pr?.name });
        if (!window.confirm(msg)) return;
        try {
            await updateDoc(doc(db, 'users', user.uid), {
                tiktokProfiles: userProfiles.filter(p => p.id !== profileId),
            });
            // Lepaskan tag pada produk yang terlibat (elak profileId yatim)
            const terkesan = products.filter(p => p.profileId === profileId);
            for (let i = 0; i < terkesan.length; i += 400) {
                const batch = writeBatch(db);
                terkesan.slice(i, i + 400).forEach(p => {
                    batch.update(doc(db, 'library', currentFolder.id, 'items', p.id), { profileId: null });
                });
                await batch.commit();
            }
            if (profileFilter === profileId) setProfileFilter('all');
            setProfileModal(null);
            if (terkesan.length > 0) await loadProducts();
        } catch (err) {
            console.error('[Library] Padam profile gagal:', err);
            alert(T('products.profile.deleteFailed', 'Gagal padam profile: ' + (err?.message || err), { ralat: err?.message || err }));
        }
    };

    // ═══ PINDAH produk terpilih ke profile (profileId = null → keluarkan) ═══
    const handleBulkMoveToProfile = async (profileId) => {
        if (!currentFolder) return;
        const selected = filteredProducts.filter(p =>
            selectedIds.has(p.id) && p.group === 'own' && p.addedBy === user?.email
        );
        if (selected.length === 0) return;
        setBulkProfileBusy(true);
        setProfileMenu(null);
        try {
            for (let i = 0; i < selected.length; i += 400) {
                const batch = writeBatch(db);
                selected.slice(i, i + 400).forEach(p => {
                    const patch = {
                        profileId: profileId || null,
                        updatedAt: serverTimestamp(),
                        updatedBy: user.email,
                    };
                    // Kosongkan group kalau ia bukan milik profil baru (arahan user
                    // 2026-08-22) — group tergolong dalam satu profil sahaja.
                    if (p.customGroup) {
                        const grp = userLibraryGroups.find(g => g.id === p.customGroup);
                        if (!grp || (grp.profileId || null) !== (profileId || null)) patch.customGroup = null;
                    }
                    batch.update(doc(db, 'library', currentFolder.id, 'items', p.id), patch);
                });
                await batch.commit();
            }
            setSelectedIds(new Set());
            setLastAnchorId(null);
            await loadProducts();
        } catch (err) {
            console.error('[Library] Pindah ke profile gagal:', err);
            alert(T('products.profile.moveFailed', 'Gagal pindah ke profile: ' + (err?.message || err), { ralat: err?.message || err }));
        } finally {
            setBulkProfileBusy(false);
        }
    };

    // ═══ SALIN produk terpilih ke profile lain (kad asal KEKAL) ═══
    // ⚠️ Skema v2: medan berat (intelligenceJson + scrapedData) duduk dalam
    // subdoc `payload/data`. Salinan WAJIB bawa subdoc itu sekali — tanpa ini
    // kad salinan lahir READY pada paparan tetapi KOSONG bila digenerate.
    const handleBulkCopyToProfile = async (profileId) => {
        if (!currentFolder || !profileId) return;
        const selected = filteredProducts.filter(p =>
            selectedIds.has(p.id) && p.group === 'own' && p.addedBy === user?.email
        );
        if (selected.length === 0) return;

        // Dedupe: produk yang SAMA sudah wujud dalam profile destinasi → langkau.
        const sedia = new Set(
            products
                .filter(p => p.profileId === profileId && p.group === 'own' && p.addedBy === user?.email)
                .map(p => productDisplayNo(p) || p.link || p.id)
        );
        const untukSalin = selected.filter(p => !sedia.has(productDisplayNo(p) || p.link || p.id));
        const dilangkau = selected.length - untukSalin.length;
        if (untukSalin.length === 0) {
            alert(T('products.profile.allAlreadyExist', `Semua ${selected.length} produk yang dipilih sudah wujud dalam profile ini.`, { n: selected.length }));
            return;
        }

        setBulkProfileBusy(true);
        setProfileMenu(null);
        try {
            const itemsRef = collection(db, 'library', currentFolder.id, 'items');
            let gagal = 0;
            for (const p of untukSalin) {
                try {
                    // Baca doc induk SEGAR — `products` dalam state hanya bawa
                    // medan ringan yang dipilih semasa senarai dimuat.
                    const asal = await getDoc(doc(db, 'library', currentFolder.id, 'items', p.id));
                    if (!asal.exists()) { gagal++; continue; }
                    const data = { ...asal.data() };
                    delete data.id;
                    const barunya = doc(itemsRef);
                    // Kosongkan group kalau ia bukan milik profil destinasi (arahan
                    // user 2026-08-22) — salinan mewarisi group profil asal jika tidak.
                    const grpAsal = data.customGroup ? userLibraryGroups.find(g => g.id === data.customGroup) : null;
                    const groupBaru = (data.customGroup && (!grpAsal || (grpAsal.profileId || null) !== profileId)) ? null : (data.customGroup || null);
                    await setDoc(barunya, {
                        ...data,
                        profileId,
                        customGroup: groupBaru,
                        addedBy: user.email,          // WAJIB — rules menolak create tanpa ini
                        group: 'own',
                        copiedFrom: p.id,
                        addedAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                        updatedBy: user.email,
                    });
                    // Bawa subdoc payload (kalau ada) — barulah salinan benar-benar lengkap
                    const payload = await loadLibraryPayload(currentFolder.id, p.id);
                    if (payload) {
                        await saveLibraryPayload(currentFolder.id, barunya.id, {
                            intelligenceJson: payload.intelligenceJson,
                            scrapedData: payload.scrapedData,
                            updatedBy: user.email,
                        });
                    }
                } catch (err) {
                    console.error('[Library] Salin produk gagal:', p.id, err);
                    gagal++;
                }
            }
            setSelectedIds(new Set());
            setLastAnchorId(null);
            await loadProducts();
            const nota = [
                T('products.profile.copyResultCopied', `${untukSalin.length - gagal} produk disalin`, { n: untukSalin.length - gagal }),
                dilangkau > 0 ? T('products.profile.copyResultSkipped', `${dilangkau} dilangkau (sudah wujud)`, { n: dilangkau }) : null,
                gagal > 0 ? T('products.profile.copyResultFailed', `${gagal} gagal`, { n: gagal }) : null,
            ].filter(Boolean).join(' · ');
            alert(nota);
        } catch (err) {
            console.error('[Library] Salin ke profile gagal:', err);
            alert(T('products.profile.copyFailed', 'Gagal salin ke profile: ' + (err?.message || err), { ralat: err?.message || err }));
        } finally {
            setBulkProfileBusy(false);
        }
    };

    // ═══ PINDAH SATU GROUP PENUH ke profile ═══
    // Ambil SEMUA produk dalam group itu (tidak kira profile asalnya, termasuk
    // yang tiada profile) — inilah maksud "pindah group ke profile".
    const handleMoveGroupToProfile = async (groupId, profileId) => {
        if (!currentFolder || !groupId || !user?.uid) return;
        const senarai = products.filter(p =>
            p.customGroup === groupId && p.group === 'own' && p.addedBy === user?.email
        );
        setBulkProfileBusy(true);
        try {
            // 1) Tetapkan akaun pada GROUP itu sendiri (sumber kebenaran) —
            //    group kosong pun boleh diberi akaun.
            const updatedGroups = userLibraryGroups.map(g =>
                g.id === groupId ? { ...g, profileId: profileId || null } : g
            );
            await updateDoc(doc(db, 'users', user.uid), { libraryGroups: updatedGroups });
            // 2) Cascade akaun ke semua produk dalam group itu.
            for (let i = 0; i < senarai.length; i += 400) {
                const batch = writeBatch(db);
                senarai.slice(i, i + 400).forEach(p => {
                    batch.update(doc(db, 'library', currentFolder.id, 'items', p.id), {
                        profileId: profileId || null,
                        updatedAt: serverTimestamp(),
                        updatedBy: user.email,
                    });
                });
                await batch.commit();
            }
            setGroupToProfile(null);
            await loadProducts();
        } catch (err) {
            console.error('[Library] Pindah group ke profile gagal:', err);
            alert(T('products.profile.moveGroupFailed', 'Gagal pindah group: ' + (err?.message || err), { ralat: err?.message || err }));
        } finally {
            setBulkProfileBusy(false);
        }
    };

    // ═══ HANTAR produk terpilih ke Workspace ═══
    // Produk di-stage di panel kiri Workspace (user pilih avatar/module lalu
    // tekan "Add Product") — corak SAMA seperti pilih dari picker Workspace.
    // ⭐ Peraturan + bentuk hantaran hidup dalam utils/hantarKeWorkspace.js —
    //    baca nota di kepala fail itu sebelum menyentuh fungsi ini.
    // ⛔ [2026-09-22] Versi lama menghantar salinan 11 medan dan TERTINGGAL data
    //    produk inline (85% item READY) + gambar tambahan, serta tidak menyemak
    //    PENDING. Jangan kembalikan senarai medan.
    const handleSendToWorkspace = async () => {
        if (sedangHantarRef.current) return;
        const dipilih = filteredProducts.filter(p =>
            selectedIds.has(p.id) && p.group === 'own' && p.addedBy === user?.email
        );
        if (dipilih.length === 0) return;
        const senaraiNama = (senarai) => {
            const { nama, baki } = namaRingkas(senarai);
            const baris = nama.map(n => `• ${n}`);
            if (baki > 0) baris.push(`• ${T('products.bulk.sendMore', `dan ${baki} lagi`, { n: baki })}`);
            return baris.join('\n');
        };

        // (1) Ada PENDING dalam pilihan → TIADA apa dihantar (keputusan user 2026-09-22).
        //     Pilihan dikekalkan — pelanggan hanya perlu buang tanda produk PENDING.
        const pending = produkPending(dipilih);
        if (pending.length > 0) {
            const nama = senaraiNama(pending);
            alert(T('products.bulk.sendPending',
                `Tiada produk dihantar. Produk berikut dalam pilihan masih PENDING (data produk belum lengkap):\n${nama}\n\nBuang tanda pada produk PENDING, atau lengkapkan datanya dahulu, kemudian tekan Hantar ke Workspace semula.`,
                { n: pending.length, nama }));
            return;
        }

        sedangHantarRef.current = true;
        setBulkSending(true);
        try {
            // (2) Baca SEGAR dari pangkalan data — salinan halaman boleh basi.
            const hasil = await sediakanHantaran(dipilih.slice(0, HAD_HANTAR_WORKSPACE), async (id) => {
                const snap = await getDoc(doc(db, 'library', currentFolder.id, 'items', id));
                return snap.exists() ? { ...snap.data(), id: snap.id } : null;
            });
            // Pelanggan sudah meninggalkan halaman ini semasa menunggu → batal senyap.
            if (!halamanHidupRef.current) return;
            if (!hasil.ok) {
                const nama = senaraiNama(hasil.berubah);
                alert(T('products.bulk.sendChanged',
                    `Tiada produk dihantar. Produk berikut sudah berubah selepas halaman ini dibuka (dipadam, atau kini PENDING):\n${nama}\n\nMuat semula halaman ini, kemudian cuba lagi.`,
                    { n: hasil.berubah.length, nama }));
                return;
            }
            // (3) Gagal tulis = BISING. Dahulu ditelan senyap dan Workspace dibuka kosong.
            sessionStorage.setItem(KUNCI_HANTAR_WORKSPACE, JSON.stringify(hasil.produk));
        } catch (err) {
            console.error('[Library] Hantar ke Workspace gagal:', err);
            if (halamanHidupRef.current) {
                alert(T('products.bulk.sendFailed',
                    `Gagal menghantar produk ke Workspace: ${err?.message || err}. Cuba lagi.`,
                    { ralat: err?.message || err }));
            }
            return;
        } finally {
            sedangHantarRef.current = false;
            setBulkSending(false);
        }

        if (dipilih.length > HAD_HANTAR_WORKSPACE) {
            alert(T('products.bulk.sendCapped',
                `Maksimum ${HAD_HANTAR_WORKSPACE} produk dihantar ke Workspace pada satu masa. ${HAD_HANTAR_WORKSPACE} pertama dihantar — teruskan baki selepas ini.`,
                { n: HAD_HANTAR_WORKSPACE }));
        }
        setSelectedIds(new Set());
        setLastAnchorId(null);
        navigate('/workspace');
    };

    // ═══ BULK CLEAR INTELLIGENCE — admin only, reset hasIntelligence + clear payload intelligenceJson ═══
    const openBulkClearIntelConfirm = () => {
        if (!isAdmin) return;
        const selected = filteredProducts.filter(p => selectedIds.has(p.id) && p.hasIntelligence);
        if (selected.length === 0) {
            alert(T('products.intel.noneWithIntel', 'Tiada produk yang ada intelligence dalam selection. Pilih produk yang ada badge READY.'));
            return;
        }
        setBulkClearIntelConfirm({ items: selected });
    };

    const handleBulkClearIntel = async () => {
        if (!bulkClearIntelConfirm || !currentFolder || !isAdmin) return;
        const items = bulkClearIntelConfirm.items;
        setBulkClearingIntel(true);
        try {
            // Parallel-3 worker — clear payload + flag
            let idx = 0;
            const workers = Array.from({ length: 3 }, async () => {
                while (idx < items.length) {
                    const p = items[idx++];
                    try {
                        // Clear intelligenceJson dalam payload subdoc
                        await saveLibraryPayload(currentFolder.id, p.id, {
                            intelligenceJson: null,
                            updatedBy: user.email,
                        });
                        // Reset flag dalam parent doc
                        await updateDoc(doc(db, 'library', currentFolder.id, 'items', p.id), {
                            hasIntelligence: false,
                            intelligenceJson: null, // clear legacy inline juga
                            updatedAt: serverTimestamp(),
                            updatedBy: user.email,
                        });
                    } catch (err) {
                        console.error(`[Library] Bulk clear intel item ${p.id} failed:`, err);
                    }
                }
            });
            await Promise.all(workers);
            setBulkClearIntelConfirm(null);
            setSelectedIds(new Set());
            setLastAnchorId(null);
            await loadProducts();
        } catch (err) {
            console.error('[Library] Bulk clear intel error:', err);
            alert(T('products.intel.clearFailed', 'Gagal clear intelligence: ' + (err?.message || err), { ralat: err?.message || err }));
        } finally {
            setBulkClearingIntel(false);
        }
    };


    // Stats untuk batch progress display
    const intelBatchStats = useMemo(() => {
        if (!intelBatchActive) return null;
        const all = Object.values(intelGenStatus);
        const total = intelSelectedIds.size || all.length;
        const done = all.filter(x => x.status === 'done').length;
        const error = all.filter(x => x.status === 'error').length;
        const running = all.filter(x => x.status === 'running').length;
        return { total, done, error, running };
    }, [intelBatchActive, intelGenStatus, intelSelectedIds]);

    // Set of sourceIds already copied to My Products — for duplicate check
    const myProductSourceIds = useMemo(() => {
        return new Set(
            products
                .filter(p => p.group === 'own' && p.addedBy === user?.email && p.sourceId)
                .map(p => p.sourceId)
        );
    }, [products, user?.email]);

    // Products in current tab (for scoped No Group count)
    const currentTabProducts = productGroup === 'system'
        ? products.filter(p => !p.group || p.group === 'system')
        : products.filter(p => p.group === 'own' && p.addedBy === user?.email);

    // ═══ Group ikut profil (arahan user 2026-08-20) ═══
    // Setiap group kini boleh dimiliki oleh satu akaun TikTok (medan
    // `profileId` pada group). Bila satu akaun ditapis, tunjuk HANYA group
    // milik akaun itu; "Semua produk" tunjuk semua; "Tanpa profile" tunjuk
    // group lama yang belum ada akaun. Tab System tiada konsep akaun → penuh.
    const profilAktif = (productGroup === 'own' && profileFilter !== 'all' && profileFilter !== 'none') ? profileFilter : null;
    const visibleGroups = productGroup !== 'own'
        ? activeGroups
        : profileFilter === 'all'
            ? activeGroups
            : profileFilter === 'none'
                ? activeGroups.filter(g => !g.profileId)
                : activeGroups.filter(g => g.profileId === profileFilter);
    // Akaun pemilik sesuatu group — untuk auto-selaras profil produk bila
    // ia dimasukkan ke dalam group.
    const groupProfileId = (gid) => (gid ? (userLibraryGroups.find(g => g.id === gid)?.profileId || null) : null);

    // Pagination
    const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
    const paginatedProducts = filteredProducts.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Kiraan untuk label butang pilih + keadaan kotak tanda kepala jadual.
    // ⚠️ Mesti duduk SELEPAS paginatedProducts — nilai ini dibaca semasa render.
    const halamanBolehDipilih = paginatedProducts.filter(bolehDipilih);
    const tapisanBolehDipilih = filteredProducts.filter(bolehDipilih);
    const halamanSemuaDitanda = halamanBolehDipilih.length > 0
        && halamanBolehDipilih.every(p => selectedIds.has(p.id));

    // Reset page when filter changes
    useEffect(() => { setCurrentPage(1); }, [searchQuery, filterMode, sortBy, sortDir, productGroup, customGroupFilter, profileFilter]);
    // Tukar akaun → tapisan group direset (group kini milik akaun, jadi
    // pilihan group akaun lama tidak lagi relevan pada akaun baharu).
    useEffect(() => { setCustomGroupFilter('all'); }, [profileFilter]);

    // ═══ SKOP AKAUN — senarai produk milik akaun yang SEDANG dibuka ═══
    // (arahan user 2026-08-21) Baris kawalan di bawah kad profile (susun,
    // kiraan status, Group, Manual, Import, Bulk Upload) mesti bercakap tentang
    // akaun yang dibuka sahaja. Sebelum ini kiraan membaca SELURUH tab, jadi
    // buka Profile 1 yang ada 2 produk pun tetap tertulis "90 Total" —
    // pelanggan membacanya sebagai skrin rosak.
    // ⚠️ currentTabProducts KEKAL seluruh tab dan JANGAN ditukar: kad profile di
    // atas mengira miliknya sendiri daripada senarai penuh itu (kad "Semua
    // produk" + kad setiap akaun + pil "Tanpa profile").
    const produkSkopProfil = productGroup !== "own"
        ? currentTabProducts
        : profileFilter === "none"
            ? currentTabProducts.filter(p => !p.profileId)
            : profileFilter === "all"
                ? currentTabProducts
                : currentTabProducts.filter(p => p.profileId === profileFilter);

    // Stats — ikut AKAUN yang dibuka (bukan seluruh tab)
    const totalProducts = produkSkopProfil.length;
    const withIntel = produkSkopProfil.filter(p => p.hasIntelligence).length;
    const withoutIntel = totalProducts - withIntel;

    // ═══ ADMIN: BULK UPLOAD ═══
    const handleBulkUpload = async () => {
        if (!bulkText.trim() || !currentFolder || (!isAdmin && productGroup !== 'own')) return;
        setBulkUploading(true);

        try {
            const lines = bulkText.split('\n').filter(l => l.trim());
            const batch = writeBatch(db);
            const itemsRef = collection(db, 'library', currentFolder.id, 'items');

            // Get current max number
            const maxNumber = products.reduce((max, p) => Math.max(max, p.number || 0), 0);

            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();
                const parts = line.split('*|*').map(s => s.trim().replace(/^['\"]+|['\"]+$/g, ''));

                let number = maxNumber + i + 1;
                let name = '';
                let link = '';
                let imageUrl = '';

                // Parse: check if first part is a number
                if (/^\d+$/.test(parts[0])) {
                    number = parseInt(parts[0]);
                    name = parts[1] || '';
                    imageUrl = parts[2] || '';
                    link = parts[3] || '';
                } else {
                    const match = parts[0].match(/^(\d+)[.)]\s+(.*)/);
                    if (match) {
                        number = parseInt(match[1]);
                        name = match[2];
                    } else {
                        name = parts[0];
                    }
                    link = parts[1] || '';
                    imageUrl = parts[2] || '';
                }

                const newDocRef = doc(itemsRef);
                const payload = {
                    number,
                    name,
                    link,
                    imageUrl,
                    group: productGroup,
                    hasIntelligence: false,
                    addedAt: serverTimestamp(),
                    addedBy: user.email,
                };
                // Masuk akaun yang SEDANG dibuka (arahan user 2026-08-21).
                if (profilAktif) payload.profileId = profilAktif;
                batch.set(newDocRef, payload);
            }

            await batch.commit();
            setBulkText('');
            setShowBulkUpload(false);
            console.log(`[Library] Bulk uploaded ${lines.length} products`);
            await loadProducts();
        } catch (err) {
            console.error('[Library] Bulk upload error:', err);
            alert(T('products.alert.bulkUploadFailed', 'Gagal bulk upload: ' + err.message, { ralat: err.message }));
        } finally {
            setBulkUploading(false);
        }
    };

    // ═══ ADMIN: PASTE INTELLIGENCE JSON ═══
    const handleSaveIntelligence = async () => {
        if (!intelModalProduct || !isAdmin) return;
        setSavingIntel(true);

        try {
            let parsed = null;
            if (intelJsonText.trim()) {
                parsed = JSON.parse(intelJsonText);
            }

            // Schema v2: heavy intelligenceJson disimpan dalam subdoc payload/data.
            // Parent doc cuma simpan flag hasIntelligence supaya list query ringan.
            await saveLibraryPayload(currentFolder.id, intelModalProduct.id, {
                intelligenceJson: parsed,
                updatedBy: user.email,
            });
            const docRef = doc(db, 'library', currentFolder.id, 'items', intelModalProduct.id);
            await updateDoc(docRef, {
                hasIntelligence: parsed !== null,
                intelligenceJson: null, // clear legacy inline field bila ada
                updatedAt: serverTimestamp(),
                updatedBy: user.email,
            });

            setIntelModalProduct(null);
            setIntelJsonText('');
            await loadProducts();
        } catch (err) {
            if (err instanceof SyntaxError) {
                alert(T('products.alert.invalidJson', 'JSON format tidak valid! Sila semak semula.'));
            } else {
                console.error('[Library] Save intelligence error:', err);
                alert(T('products.alert.saveFailed', 'Gagal simpan: ' + err.message, { ralat: err.message }));
            }
        } finally {
            setSavingIntel(false);
        }
    };

    // ═══ MODAL OPENERS — lazy-fetch payload subdoc (heavy field) ═══
    // List query tak ambik intelligenceJson + scrapedData — kena fetch on-demand.
    // Backward-compat: fall back ke parent inline field kalau subdoc takda
    // (item belum di-migrate dari schema v1).

    const openViewIntel = async (product) => {
        if (!currentFolder?.id) return;
        setViewIntelProduct(product);
        setViewIntelLoading(true);
        setViewIntelData(null);
        try {
            const payload = await loadLibraryPayload(currentFolder.id, product.id);
            setViewIntelData(payload?.intelligenceJson ?? product.intelligenceJson ?? null);
        } finally {
            setViewIntelLoading(false);
        }
    };
    const closeViewIntel = () => {
        setViewIntelProduct(null);
        setViewIntelData(null);
        setViewIntelLoading(false);
    };

    const openEditIntel = async (product) => {
        if (!currentFolder?.id) return;
        setIntelModalProduct(product);
        setIntelModalLoading(true);
        setIntelJsonText('');
        try {
            const payload = await loadLibraryPayload(currentFolder.id, product.id);
            const intel = payload?.intelligenceJson ?? product.intelligenceJson ?? null;
            setIntelJsonText(intel ? JSON.stringify(intel, null, 2) : '');
        } finally {
            setIntelModalLoading(false);
        }
    };
    const closeEditIntel = () => {
        setIntelModalProduct(null);
        setIntelJsonText('');
        setIntelModalLoading(false);
    };

    const openScrapedData = async (product) => {
        if (!currentFolder?.id) return;
        setScrapedDataLoading(true);
        try {
            // Kalau parent dah ada scrapedData (legacy v1), guna terus.
            // Kalau tidak, fetch dari subdoc.
            if (product.scrapedData) {
                setScrapedDataProduct(product);
            } else {
                const payload = await loadLibraryPayload(currentFolder.id, product.id);
                setScrapedDataProduct({ ...product, scrapedData: payload?.scrapedData ?? null });
            }
        } finally {
            setScrapedDataLoading(false);
        }
    };
    const closeScrapedData = () => {
        setScrapedDataProduct(null);
        setScrapedDataLoading(false);
    };

    // ═══ DELETE PRODUCT — admin or own product ═══
    const handleDeleteProduct = async (productId, product) => {
        const isOwn = product?.group === 'own' && product?.addedBy === user?.email;
        if (!currentFolder || (!isAdmin && !isOwn)) return;
        if (!confirm(T('products.alert.confirmDeleteProduct', 'Padam produk ini?'))) return;
        try {
            // Padam payload subdoc dulu (safe idempotent — silent kalau takda)
            await deleteLibraryPayload(currentFolder.id, productId);
            await deleteDoc(doc(db, 'library', currentFolder.id, 'items', productId));
            /* Padam produk = padam juga gambar SIMPANAN KITA miliknya + tolak
               kiraan (arahan user 2026-08-26). Gambar CDN TikTok / library
               System tidak disentuh — helper yang menapis. */
            await padamGambarSimpanan(product?.imageUrl);
            for (const u of (Array.isArray(product?.extraImageUrls) ? product.extraImageUrls : [])) await padamGambarSimpanan(u); // gambar tambahan milik sendiri juga
            await loadProducts();
        } catch (err) {
            console.error('[Library] Delete error:', err);
        }
    };

    // ═══ EDIT PRODUCT (inline) — admin or own product ═══
    // Price/originalPrice/discount source: top-level field (sentiasa diset masa import
    // dari scrapedData.price etc, kemudian boleh override manual via this edit form).
    // Schema v2: scrapedData dah pindah ke payload subdoc — tak boleh fallback ke
    // sd.price lagi tanpa async fetch. Top-level fields cukup.
    const startEditProduct = (product) => {
        setEditingProductId(product.id);
        setEditProductData({
            number: product.number || '',
            name: product.name || '',
            link: product.link || '',
            price: product.price ?? '',
            originalPrice: product.originalPrice ?? '',
            discount: product.discount ?? '',
        });
    };
    const cancelEditProduct = () => {
        setEditingProductId(null);
        setEditProductData({ number: '', name: '', link: '', price: '', originalPrice: '', discount: '' });
    };
    const handleSaveProduct = async (product) => {
        const isOwn = product?.group === 'own' && product?.addedBy === user?.email;
        if (!editingProductId || !currentFolder || (!isAdmin && !isOwn)) return;
        const updates = {
            number: (() => { const n = String(editProductData.number ?? '').replace(/\D/g, ''); if (!n) return 0; return n.length <= 15 ? parseInt(n, 10) : n; })(), // nilai panjang (cth productId) simpan as-string elak precision loss
            name: editProductData.name.trim(),
            link: editProductData.link.trim(),
            price: editProductData.price?.toString().trim() || '',
            originalPrice: editProductData.originalPrice?.toString().trim() || '',
            discount: editProductData.discount?.toString().trim() || '',
        };
        const savedId = editingProductId;
        try {
            await updateDoc(doc(db, 'library', currentFolder.id, 'items', savedId), {
                ...updates,
                updatedAt: serverTimestamp(),
                updatedBy: user.email,
            });
            cancelEditProduct();
            // Optimistic update: patch local state terus, no re-fetch (tiada full-page spinner).
            setProducts(prev => prev.map(p =>
                p.id === savedId ? { ...p, ...updates, updatedBy: user.email } : p
            ));
        } catch (err) {
            console.error('[Library] Edit product error:', err);
            alert(T('products.alert.saveFailed', 'Gagal simpan: ' + err.message, { ralat: err.message }));
        }
    };

    // Salin link + tanda semak sekejap supaya user tahu ia berjaya.
    const copyLinkToClipboard = (url, tagId) => {
        if (!url) return;
        navigator.clipboard.writeText(url)
            .then(() => {
                setCopiedLinkId(tagId);
                setTimeout(() => setCopiedLinkId(prev => (prev === tagId ? null : prev)), 1500);
            })
            .catch(err => console.warn('[Library] Gagal salin link:', err?.message));
    };

    // ═══ LENGKAPKAN DATA PRODUK — pelanggan taip sendiri ═══
    // Untuk produk yang keterangannya hanya ada dalam gambar, jadi scrape
    // memang takkan berjaya. Selepas simpan, kad terus dinilai semula.
    /* ★ Laluan simpan kini SATU takrifan dalam utils/editDataProduk.js — dikongsi
       dengan chip "Edit data" pada kad Workspace (PLAN_EDIT_DATA_KAD.md, 2026-09-12).
       Kelakuan My Products TIDAK berubah: medan identiti ditulis dahulu, gambar lama
       milik sendiri dipadam + kaunter turun, link Shopee ditolak ke kad, perenggan
       data dijana semula + nilai READY. Di sini tinggal kemas kini paparan tempatan. */
    const handleSaveManualData = async (borang) => {
        const target = completeDataProduct;
        if (!target || !currentFolder?.id) return;

        let hasil;
        try {
            hasil = await simpanDataProdukItem({
                folderId: currentFolder.id,
                item: target,
                uid: user?.uid,
                email: user.email,
                borang,
                padamGambar: padamGambarSimpanan,
            });
        } catch (e) {
            // Medan identiti mungkin sudah tertulis — selaraskan paparan, kemudian
            // biar borang memaparkan ralat (ia tetap terbuka).
            if (e?.idPatch) setProducts(prev => prev.map(p => p.id === target.id ? { ...p, ...e.idPatch } : p));
            throw e;
        }
        const { idPatch, ready, scrapedData, pendingReason } = hasil;

        // Tampal state tempatan — tiada muat semula, kad bertukar serta-merta.
        setProducts(prev => prev.map(p => p.id === target.id
            ? {
                ...p,
                ...idPatch,
                scrapedData,
                hasIntelligence: ready,
                pendingReason,
                ...(scrapedData.price ? { price: scrapedData.price } : {}),
            }
            : p));
        setCompleteDataProduct(null);
    };

    // ═══ IMPORT PRODUK (Fasa 2) — tulis kad hasil scrape stepper ke My Products ═══
    // Bentuk kad SAMA seperti import extension (ImportProductsView handleConfirmSave):
    // link /view/product/, group:'own', scrapedData, hasIntelligence + intelligenceJson
    // (READY) atau pendingReason (PENDING), number, customGroup, profileId.
    // ⚠️ `profileId` TIDAK lagi diterima daripada stepper — pemilih akaun di
    // dalam wizard import sudah dibuang (arahan user 2026-08-21). Akaun
    // destinasi datang daripada kad profile yang SEDANG dibuka pada halaman.
    const handleImportSave = async ({ items, groupId }) => {
        if (!currentFolder?.id || !Array.isArray(items) || items.length === 0) return { saved: 0 };
        const itemsRef = collection(db, 'library', currentFolder.id, 'items');
        // Nombor kad = teruskan dari max sedia ada (number = kunci susunan "Sort: Number").
        const maxNum = products.reduce((m, p) => {
            const n = typeof p.number === 'number' ? p.number : parseInt(p.number, 10);
            return Number.isFinite(n) ? Math.max(m, n) : m;
        }, 0);

        // writeBatch had 500 ops — chunk kalau import besar.
        const CHUNK = 400;
        let saved = 0;
        for (let start = 0; start < items.length; start += CHUNK) {
            const slice = items.slice(start, start + CHUNK);
            const batch = writeBatch(db);
            slice.forEach((it, j) => {
                // JSON round-trip = buang `undefined` (Firestore tolak) + nilai bukan-JSON.
                const sd = JSON.parse(JSON.stringify(it.data || {}));
                const productId = it.productId || sd.productId
                    || extractTikTokProductId(sd.resolvedUrl || sd.sourceUrl || it.link || '') || '';
                const link = it.link || (productId ? `https://shop.tiktok.com/view/product/${productId}` : (sd.sourceUrl || ''));
                const refDoc = doc(itemsRef);
                const payload = {
                    link,
                    group: 'own',
                    scrapedData: sd,
                    name: sd.name || '',
                    imageUrl: sd.imageUrl || (Array.isArray(sd.gallery) && sd.gallery[0]) || '',
                    number: maxNum + start + j + 1,
                    customGroup: it.groupId ?? groupId ?? null,   // ★ group per-produk (fallback ke group global lama)
                    price: sd.price ?? '',
                    originalPrice: sd.originalPrice ?? '',
                    discount: sd.discount ?? '',
                    addedAt: serverTimestamp(),
                    addedBy: user.email,
                    updatedAt: serverTimestamp(),
                };
                // Buka "Semua produk" atau "Tanpa profile" → profilAktif = null →
                // produk masuk TANPA akaun (ia duduk dalam "Semua produk" sahaja).
                if (profilAktif) payload.profileId = profilAktif;
                if (it.ready) {
                    payload.hasIntelligence = true;
                    payload.intelligenceJson = {
                        imageAnalysis: buildDataProdukBlock(sd),
                        webResearch: '',
                        generatedAt: serverTimestamp(),
                        generatedBy: user.email,
                        sourceType: 'scrape-only',
                    };
                    payload.pendingReason = null;
                } else {
                    payload.hasIntelligence = false;
                    payload.pendingReason = it.pendingReason || 'Data tidak lengkap';
                }
                batch.set(refDoc, payload);
            });
            await batch.commit();
            saved += slice.length;
        }
        await loadProducts();
        return { saved };
    };

    // ═══ IMPORT PRODUK — tab SHOPEE (2026-10-03) ═══
    // Tulisan + pagar nilai duduk dalam utils/shopeeImport.js (`simpanImportShopee`);
    // di sini hanya konteks halaman (folder, nombor seterusnya, akaun destinasi).
    // ⚠️ Item Shopee menyimpan `link` KOSONG dengan sengaja — lihat nota kepala fail itu.
    const handleImportSaveShopee = async ({ items }) => {
        if (!currentFolder?.id || !Array.isArray(items) || items.length === 0) return { saved: 0, ditolak: [] };
        // isSafeInteger: nombor kad boleh jadi ID TikTok 19 digit (hilang ketepatan) — jangan sambung dari situ.
        const maxNum = products.reduce((m, p) => {
            const n = typeof p.number === 'number' ? p.number : parseInt(p.number, 10);
            return Number.isSafeInteger(n) && n < 1e9 ? Math.max(m, n) : m;
        }, 0);
        const hasil = await simpanImportShopee({
            folderId: currentFolder.id,
            items,
            nomborMaks: maxNum,
            email: user.email,
            profileId: profilAktif || '',
        });
        await loadProducts();
        return hasil;
    };

    // ★ 2026-10-03 — simpan senarai storefront Shopee pada akaun (`users/{uid}.shopeeStorefronts`,
    //   corak sama libraryGroups). Senarai dikemas + dihadkan oleh `kemasStorefrontTersimpan`
    //   SEBELUM ditulis; gagal → pulang false supaya modal memaparkannya (bukan senyap).
    const handleSaveShopeeStorefronts = async (senarai) => {
        if (!user?.uid) return false;
        try {
            await updateDoc(doc(db, 'users', user.uid), { shopeeStorefronts: kemasStorefrontTersimpan(senarai) });
            return true;
        } catch (e) {
            console.error('[Library] simpan storefront Shopee gagal', e);
            return false;
        }
    };

    // Cipta group baru terus dari modal Import (Review) — tulis ke users/{uid}.libraryGroups
    //   (Import sentiasa masuk My Products = group 'own'), pulangkan {id} supaya modal terus assign.
    const handleCreateGroupInline = async (name, color) => {
        const nm = String(name || '').trim();
        if (!nm || !user?.uid) return null;
        // ★ 2026-10-03 — group dari modal Import jadi milik akaun yang SEDANG dibuka (sama seperti
        //   handleAddGroup); nama disemak dalam skop akaun itu sahaja. Tanpa ini group baharu lahir
        //   tanpa akaun dan tidak muncul dalam menu group akaun tersebut.
        const skopGroup = userLibraryGroups.filter(g => (profilAktif ? g.profileId === profilAktif : !g.profileId));
        if (skopGroup.some(g => String(g.name).toLowerCase() === nm.toLowerCase())) return null;
        try {
            const newGroup = { id: Date.now().toString(36), name: nm, color: color || '#6366f1' };
            if (profilAktif) newGroup.profileId = profilAktif;
            await updateDoc(doc(db, 'users', user.uid), { libraryGroups: [...userLibraryGroups, newGroup] });
            return { id: newGroup.id };
        } catch (e) {
            console.error('[Library] create group inline error', e);
            return null;
        }
    };

    // Set productId yang dah ada dalam My Products — untuk tanda "DAH ADA" di Review import.
    const ownProductIds = useMemo(() => {
        const s = new Set();
        products.forEach(p => {
            if (p.group === 'own') {
                const id = extractTikTokProductId(p.link || '');
                if (id) s.add(String(id));
            }
        });
        return s;
    }, [products]);

    // ★ 2026-10-03 — kunci produk SHOPEE ("shopId.itemId") yang dah ada — tanda "DAH ADA" tab Shopee.
    const ownShopeeKeys = useMemo(() => {
        const s = new Set();
        products.forEach(p => {
            if (p.group === 'own') {
                const k = kunciShopee(p.shopeeProductUrl || '');
                if (k) s.add(k);
            }
        });
        return s;
    }, [products]);

    // ═══ ADMIN: EDIT FOLDER NAME ═══
    const handleSaveFolderName = async (folderId) => {
        if (!editFolderValue.trim() || !isAdmin) return;
        try {
            await updateDoc(doc(db, 'library', folderId), { name: editFolderValue.trim() });
            setEditingFolderName(null);
            await loadFolders();
        } catch (err) {
            console.error('[Library] Rename error:', err);
        }
    };

    // ═══ MANUAL ADD PRODUCTS ═══
    const newManualEntry = (nextNum) => ({
        _key: `m_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        number: nextNum,
        name: '',
        imageUrl: '',
        imagePreview: null,
        imageBase64: null,
        link: '',
    });

    const openManualAdd = () => {
        const maxNum = products.reduce((max, p) => Math.max(max, p.number || 0), 0);
        setManualEntries([newManualEntry(maxNum + 1)]);
        setShowManualAdd(true);
    };

    const updateManualEntry = (key, field, value) => {
        setManualEntries(prev => prev.map(e => e._key === key ? { ...e, [field]: value } : e));
    };

    const removeManualEntry = (key) => {
        if (manualEntries.length <= 1) return;
        setManualEntries(prev => prev.filter(e => e._key !== key));
    };

    const addManualEntry = () => {
        const maxNum = Math.max(...manualEntries.map(e => e.number || 0), 0);
        setManualEntries(prev => [...prev, newManualEntry(maxNum + 1)]);
    };

    const handleManualImageUpload = (key, file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setManualEntries(prev => prev.map(e => e._key === key ? {
                ...e, imageBase64: ev.target.result, imagePreview: ev.target.result, imageUrl: '',
            } : e));
        };
        reader.readAsDataURL(file);
    };

    const handleManualImageUrl = (key, url) => {
        setManualEntries(prev => prev.map(e => e._key === key ? {
            ...e, imageUrl: url, imagePreview: url.startsWith('http') ? url : null, imageBase64: null,
        } : e));
    };

    // ═══ REPLACE PRODUCT IMAGE — admin (System) or owner (My Products) ═══
    const [replacingImageId, setReplacingImageId] = useState(null); // productId currently uploading
    const replaceImageInputRef = useRef(null);
    const replaceImageTargetRef = useRef(null); // product being replaced (captured on click)

    const canReplaceImage = (product) => {
        if (!product) return false;
        const isOwn = product.group === 'own' && product.addedBy === user?.email;
        return isAdmin || isOwn;
    };

    const handleTriggerReplaceImage = (product) => {
        if (!canReplaceImage(product) || !replaceImageInputRef.current) return;
        replaceImageTargetRef.current = product;
        replaceImageInputRef.current.value = ''; // allow re-selecting same file
        replaceImageInputRef.current.click();
    };

    /* ⭐ SATU takrifan muat naik gambar — dipakai oleh TIGA tempat: hover
       gambar pada kad grid, hover gambar pada baris senarai, dan butang
       Upload dalam borang Data Produk. Ia hanya MEMUAT NAIK dan memulangkan
       URL; penulisan Firestore terserah kepada pemanggil — borang menulisnya
       sekali bersama medan lain, jadi pelanggan tekan Simpan SEKALI sahaja.
       ⚠️ Laluan storage berbeza ikut peranan dan ia BUKAN pilihan gaya:
       storage.rules hanya benarkan UID admin pada library/**, dan pemilik
       pada users/{uid}/**. Salah laluan = tulisan ditolak. Dua salinan logik
       ini pasti terpesong, sebab itu ia hidup di satu tempat sahaja. */
    const muatNaikGambarProduk = async (file, product) => {
        if (!file || !product || !currentFolder) throw new Error('Missing file / product / folder');
        const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
        const path = isAdmin
            ? `library/${currentFolder.id}/${product.id}_${Date.now()}.${ext}`
            : `users/${user.uid}/library/${currentFolder.id}/${product.id}_${Date.now()}.${ext}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg', cacheControl: 'public, max-age=31536000, immutable' });
        /* Kiraan simetri (arahan user 2026-08-26): fail pemilik masuk skop bar
           → kaunter NAIK serta-merta. Laluan admin (library/**) di LUAR skop
           bar — mengiranya buat nombor melompat turun semula pada recount
           Optimize (recount hanya imbas users/{uid} + videos/{uid} + avatar). */
        if (path.startsWith(`users/${user.uid}/`)) ubahKaunterStorage(file.size);
        return await getDownloadURL(storageRef);
    };

    /* ★ Kiraan simetri gambar produk (arahan user 2026-08-26):
       muat naik → kaunter naik · ganti/padam produk → fail lama DIPADAM +
       kaunter turun. Sebelum ini setiap ganti meninggalkan fail lama
       SELAMANYA (nama fail bercap masa = tiada apa menimpanya), dan padam
       produk langsung tidak menyentuh storan — kedua-duanya kos yang
       terkumpul senyap.
       ⛔ HANYA fail di bawah users/{uid}/library/** disentuh. Fail library/**
       (System) SENGAJA tidak dipadam: "Add to My Products" menyalin URL yang
       sama, jadi memadamnya mematikan gambar pada salinan pelanggan lain. */
    const ubahKaunterStorage = (deltaBytes) => {
        if (!user?.uid || !deltaBytes) return;
        setDoc(doc(db, 'users', user.uid), { storageUsedBytes: increment(Math.round(deltaBytes)) }, { merge: true })
            .catch(() => { /* bukan kritikal — recount Optimize betulkan */ });
    };
    const padamGambarSimpanan = async (imageUrl) => {
        try {
            if (!imageUrl || !user?.uid || !imageUrl.includes('firebasestorage.googleapis.com')) return;
            const m = new URL(imageUrl).pathname.match(/\/o\/(.+?)(\?|$)/);
            if (!m) return;
            const path = decodeURIComponent(m[1]);
            if (!path.startsWith(`users/${user.uid}/library/`)) return; // fail kongsi/CDN — jangan sentuh
            const rj = ref(storage, path);
            let sz = 0;
            try { sz = (await getMetadata(rj)).size || 0; } catch { /* abaikan */ }
            await deleteObject(rj);
            if (sz > 0) ubahKaunterStorage(-sz);
        } catch (e) { console.warn('[Library] Gagal padam gambar lama:', e?.message); }
    };

    const handleReplaceImageFile = async (event) => {
        const file = event.target.files?.[0];
        const product = replaceImageTargetRef.current;
        replaceImageTargetRef.current = null;
        if (!file || !product || !currentFolder || !canReplaceImage(product)) return;

        setReplacingImageId(product.id);
        try {
            const urlLama = product.imageUrl || '';
            const newUrl = await muatNaikGambarProduk(file, product);

            await updateDoc(doc(db, 'library', currentFolder.id, 'items', product.id), {
                imageUrl: newUrl,
                updatedAt: serverTimestamp(),
                updatedBy: user.email,
            });
            /* Ganti = fail lama tidak dirujuk sesiapa lagi → padam + tolak
               kiraan. Dipadam SELEPAS doc menunjuk URL baharu — kalau padam
               gagal, paling teruk satu fail yatim (keadaan lama), bukan kad
               tanpa gambar. */
            if (urlLama && urlLama !== newUrl) await padamGambarSimpanan(urlLama);
            await loadProducts();
        } catch (err) {
            console.error('[Library] Replace image error:', err);
            alert(T('products.alert.replaceImageFailed', 'Gagal ganti gambar: ' + err.message, { ralat: err.message }));
        } finally {
            setReplacingImageId(null);
        }
    };

    // ═══ COPY SYSTEM PRODUCT → MY PRODUCTS ═══
    const [addingToMyProducts, setAddingToMyProducts] = useState(null); // productId being added
    const handleAddToMyProducts = async (product) => {
        if (!currentFolder || !user) return;
        if (myProductSourceIds.has(product.id)) return; // already copied
        setAddingToMyProducts(product.id);
        try {
            const itemsRef = collection(db, 'library', currentFolder.id, 'items');
            const newDocRef = doc(itemsRef);
            await setDoc(newDocRef, {
                number: product.number,
                name: product.name,
                link: product.link || '',
                imageUrl: product.imageUrl || '',
                hasIntelligence: false,
                group: 'own',
                addedBy: user.email,
                sourceId: product.id,
                addedAt: serverTimestamp(),
            });
            await loadProducts();
        } catch (err) {
            console.error('[Library] Add to My Products error:', err);
            alert(T('products.alert.addToMineFailed', 'Gagal tambah ke My Products: ' + err.message, { ralat: err.message }));
        } finally {
            setAddingToMyProducts(null);
        }
    };

    // ═══ NOTA (2026-07-28): ciri "Import Products" (tampal link share TikTok) DIBUANG ═══
    // Sebab: ia hasilkan produk TANPA data scrape (PENDING) — sedangkan Stage 1+2 dah OFF,
    // jadi produk begitu tak boleh dijana langsung. Satu laluan sahaja sekarang: butang
    // Import di dalam Extension SESAAT. Fungsi server scrapeTikTokProductLite masih wujud
    // tetapi tiada lagi pemanggil di webapp.

    const handleManualSubmit = async () => {
        if (!currentFolder || (!isAdmin && productGroup !== 'own')) return;
        const valid = manualEntries.filter(e => e.name.trim());
        if (valid.length === 0) return;
        setManualSaving(true);
        try {
            const batch = writeBatch(db);
            const itemsRef = collection(db, 'library', currentFolder.id, 'items');

            for (const entry of valid) {
                let finalImageUrl = entry.imageUrl || '';
                // Upload base64 image to Storage if present
                if (entry.imageBase64) {
                    try {
                        const blob = await fetch(entry.imageBase64).then(r => r.blob());
                        // See handleReplaceImageFile: non-admin must write under users/{uid}/** per storage.rules
                        const path = isAdmin
                            ? `library/${currentFolder.id}/${Date.now()}_${Math.random().toString(36).substring(2, 7)}.jpg`
                            : `users/${user.uid}/library/${currentFolder.id}/${Date.now()}_${Math.random().toString(36).substring(2, 7)}.jpg`;
                        const storageRef = ref(storage, path);
                        await uploadBytes(storageRef, blob, { contentType: blob.type || 'image/jpeg', cacheControl: 'public, max-age=31536000, immutable' });
                        finalImageUrl = await getDownloadURL(storageRef);
                    } catch (uploadErr) {
                        console.error('[Library] Image upload error:', uploadErr);
                    }
                }
                const newDocRef = doc(itemsRef);
                const payload = {
                    number: entry.number,
                    name: entry.name.trim(),
                    link: entry.link.trim(),
                    imageUrl: finalImageUrl,
                    group: productGroup,
                    hasIntelligence: false,
                    addedAt: serverTimestamp(),
                    addedBy: user.email,
                };
                // Masuk akaun yang SEDANG dibuka (arahan user 2026-08-21).
                if (profilAktif) payload.profileId = profilAktif;
                batch.set(newDocRef, payload);
            }
            await batch.commit();
            setShowManualAdd(false);
            setManualEntries([]);
            console.log(`[Library] Manual added ${valid.length} products`);
            await loadProducts();
        } catch (err) {
            console.error('[Library] Manual add error:', err);
            alert(T('products.alert.addManualFailed', 'Gagal tambah produk: ' + err.message, { ralat: err.message }));
        } finally {
            setManualSaving(false);
        }
    };

    // Navigate to folder
    const openFolder = (folder) => {
        setCurrentFolder(folder);
        setCurrentView('products');
        setSearchQuery('');
        setFilterMode('all');
        setProductGroup('all');
        setCustomGroupFilter('all');
        setCurrentPage(1);
    };

    // ═══ CUSTOM GROUPS: Add ═══
    const handleAddGroup = async () => {
        const name = newGroupName.trim();
        if (!name || !currentFolder) return;
        // Dedupe dalam skop akaun yang sedang dilihat — dua akaun berbeza
        // boleh ada group bernama sama (cth "Skincare" untuk setiap akaun).
        if (visibleGroups.some(g => g.name.toLowerCase() === name.toLowerCase())) {
            alert(T('products.alert.groupNameExists', 'Nama kumpulan sudah wujud.')); return;
        }
        setSavingGroup(true);
        try {
            const newGroup = { id: Date.now().toString(36), name, color: newGroupColor };
            // Group baharu terus jadi milik akaun yang sedang ditapis.
            if (profilAktif) newGroup.profileId = profilAktif;
            if (productGroup === 'own') {
                // Save to user document (per-user groups)
                await updateDoc(doc(db, 'users', user.uid), {
                    libraryGroups: [...userLibraryGroups, newGroup],
                });
            } else {
                // Save to folder document (admin system groups)
                if (!isAdmin) return;
                await updateDoc(doc(db, 'library', currentFolder.id), {
                    productGroups: [...productGroups, newGroup],
                });
                await loadFolders();
            }
            setNewGroupName('');
            setNewGroupColor('#6366f1');
        } catch (err) {
            console.error('[Library] Add group error:', err);
            alert(T('products.alert.addGroupFailed', 'Gagal tambah kumpulan: ' + err.message, { ralat: err.message }));
        } finally {
            setSavingGroup(false);
        }
    };

    // ═══ CUSTOM GROUPS: Delete ═══
    const handleDeleteGroup = async (groupId) => {
        if (!currentFolder) return;
        if (!window.confirm(T('products.confirm.deleteGroup', 'Padam kumpulan ini? Produk dalam kumpulan ini tidak akan terpadam, tapi akan jadi tidak berkumpulan.'))) return;
        try {
            if (productGroup === 'own') {
                // Remove from user document
                await updateDoc(doc(db, 'users', user.uid), {
                    libraryGroups: userLibraryGroups.filter(g => g.id !== groupId),
                });
                // Clear customGroup on user's own affected products
                const affected = products.filter(p => p.customGroup === groupId && p.group === 'own' && p.addedBy === user?.email);
                if (affected.length > 0) {
                    const batch = writeBatch(db);
                    affected.forEach(p => {
                        batch.update(doc(db, 'library', currentFolder.id, 'items', p.id), { customGroup: null });
                    });
                    await batch.commit();
                }
            } else {
                if (!isAdmin) return;
                await updateDoc(doc(db, 'library', currentFolder.id), {
                    productGroups: productGroups.filter(g => g.id !== groupId),
                });
                const affected = products.filter(p => p.customGroup === groupId);
                if (affected.length > 0) {
                    const batch = writeBatch(db);
                    affected.forEach(p => {
                        batch.update(doc(db, 'library', currentFolder.id, 'items', p.id), { customGroup: null });
                    });
                    await batch.commit();
                }
            }
            if (customGroupFilter === groupId) setCustomGroupFilter('all');
            await loadFolders();
            await loadProducts();
        } catch (err) {
            console.error('[Library] Delete group error:', err);
            alert(T('products.alert.deleteGroupFailed', 'Gagal padam kumpulan: ' + err.message, { ralat: err.message }));
        }
    };

    // ═══ CUSTOM GROUPS: Rename ═══
    const handleRenameGroup = async (groupId) => {
        const name = editGroupName.trim();
        if (!name || !currentFolder) return;
        if (activeGroups.some(g => g.id !== groupId && g.name.toLowerCase() === name.toLowerCase())) {
            alert(T('products.alert.groupNameExists', 'Nama kumpulan sudah wujud.')); return;
        }
        const target = activeGroups.find(g => g.id === groupId);
        if (!target) return;
        if (target.name === name) { setEditingGroupId(null); setEditGroupName(''); return; }
        setRenamingGroup(true);
        try {
            if (productGroup === 'own') {
                const updated = userLibraryGroups.map(g => g.id === groupId ? { ...g, name } : g);
                await updateDoc(doc(db, 'users', user.uid), { libraryGroups: updated });
            } else {
                if (!isAdmin) return;
                const updated = productGroups.map(g => g.id === groupId ? { ...g, name } : g);
                await updateDoc(doc(db, 'library', currentFolder.id), { productGroups: updated });
                await loadFolders();
            }
            setEditingGroupId(null);
            setEditGroupName('');
        } catch (err) {
            console.error('[Library] Rename group error:', err);
            alert(T('products.alert.renameGroupFailed', 'Gagal rename kumpulan: ' + err.message, { ralat: err.message }));
        } finally {
            setRenamingGroup(false);
        }
    };

    // ═══ CUSTOM GROUPS: Assign product to group — admin or own product ═══
    const handleAssignGroup = async (productId, groupId, product) => {
        const isOwn = product?.group === 'own' && product?.addedBy === user?.email;
        if (!currentFolder || (!isAdmin && !isOwn)) return;
        try {
            const patch = {
                customGroup: groupId,
                updatedAt: serverTimestamp(),
                updatedBy: user.email,
            };
            // Group ada akaun → produk own auto-ikut akaun group itu.
            const tp = productGroup === 'own' ? groupProfileId(groupId) : null;
            if (tp && isOwn) patch.profileId = tp;
            await updateDoc(doc(db, 'library', currentFolder.id, 'items', productId), patch);
            await loadProducts();
        } catch (err) {
            console.error('[Library] Assign group error:', err);
            alert(T('products.alert.assignGroupFailed', 'Gagal assign kumpulan: ' + err.message, { ralat: err.message }));
        } finally {
            setAssigningGroupProductId(null);
        }
    };

    // Tutup dropdown group bila scroll/resize (posisi fixed jadi basi bila page bergerak)
    useEffect(() => {
        if (!assigningGroupProductId) return;
        const close = () => setAssigningGroupProductId(null);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [assigningGroupProductId]);

    // Navigate back
    const goBack = () => {
        setCurrentView('folders');
        setCurrentFolder(null);
        setProducts([]);
    };

    // ═══════════════════════════════════════════════════════════
    //  RENDER: FOLDERS VIEW
    // ═══════════════════════════════════════════════════════════
    if (currentView === 'folders') {
        return (
            <div className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50 dark:bg-slate-950">
                {/* Header */}
                <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
                            <span className="material-symbols-outlined text-white text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>library_books</span>
                        </div>
                        <div className="flex-1">
                            <h1 className="text-lg font-bold text-slate-800 dark:text-white">{T('products.folders.title', 'Library')}</h1>
                            <p className="text-xs text-slate-400">{T('products.folders.subtitle', 'Pusat sumber kongsi — akses data produk, hook, eCourse dan lain-lain')}</p>
                        </div>
                    </div>
                </div>

                {/* Folder Grid */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6">
                    {loadingFolders ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="w-8 h-8 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
                        </div>
                    ) : folders.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                            <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                                <span className="material-symbols-outlined text-3xl text-slate-300">folder_off</span>
                            </div>
                            <h3 className="text-sm font-bold text-slate-500 mb-1">{T('products.folders.emptyTitle', 'Tiada Folder')}</h3>
                            <p className="text-[11px] text-slate-400 max-w-xs">{T('products.folders.emptyDesc', 'Library akan di-setup oleh admin.')}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {folders.map((folder) => {
                                // Folder icon colors
                                const colorMap = {
                                    'inventory_2': { from: 'from-blue-500', to: 'to-indigo-600', shadow: 'shadow-blue-500/20', bg: 'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20' },
                                    'campaign': { from: 'from-amber-500', to: 'to-orange-600', shadow: 'shadow-amber-500/20', bg: 'from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20' },
                                    'school': { from: 'from-emerald-500', to: 'to-teal-600', shadow: 'shadow-emerald-500/20', bg: 'from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20' },
                                };
                                const colors = colorMap[folder.icon] || colorMap['inventory_2'];

                                return (
                                    <div
                                        key={folder.id}
                                        onClick={() => openFolder(folder)}
                                        className="group cursor-pointer bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-700 hover:-translate-y-0.5 transition-all duration-300"
                                    >
                                        <div className="flex items-start gap-3 mb-4">
                                            <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${colors.from} ${colors.to} flex items-center justify-center shrink-0 shadow-lg ${colors.shadow} group-hover:scale-110 transition-transform duration-300`}>
                                                <span className="material-symbols-outlined text-white text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>{folder.icon || 'folder'}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                {editingFolderName === folder.id ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <input
                                                            autoFocus
                                                            value={editFolderValue}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => setEditFolderValue(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') handleSaveFolderName(folder.id);
                                                                if (e.key === 'Escape') setEditingFolderName(null);
                                                            }}
                                                            className="flex-1 text-sm font-bold bg-white dark:bg-slate-800 border border-purple-300 dark:border-purple-600 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-400/50"
                                                        />
                                                        <button onClick={(e) => { e.stopPropagation(); handleSaveFolderName(folder.id); }} className="p-0.5 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-emerald-500">
                                                            <span className="material-symbols-outlined text-[16px]">check</span>
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <h3 className="text-sm font-bold text-slate-800 dark:text-white truncate">{folder.name}</h3>
                                                )}
                                                <p className="text-[10px] text-slate-400 mt-0.5">{folder.description || T('products.folders.noDescription', 'No description')}</p>
                                            </div>
                                        </div>

                                        {/* Folder meta */}
                                        <div className="flex items-center justify-between">
                                            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gradient-to-r ${colors.bg}`}>
                                                <span className="material-symbols-outlined text-[12px] text-slate-400">group</span>
                                                <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 capitalize">
                                                    {(folder.accessRoles || []).join(', ') || 'All'}
                                                </span>
                                            </div>
                                            {isAdmin && editingFolderName !== folder.id && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingFolderName(folder.id);
                                                        setEditFolderValue(folder.name);
                                                    }}
                                                    className="p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-purple-500 transition-all"
                                                    title={T('products.folders.editNameTitle', 'Edit nama folder')}
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">edit</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════
    //  RENDER: PRODUCTS VIEW (inside a folder)
    // ═══════════════════════════════════════════════════════════
    /* Butang tindakan — SATU sumber, dipakai oleh kepala v3.9 dan rupa
       lama. Syarat siapa nampak apa tidak boleh disalin dua kali:
       "Select for Intel" khusus admin pada tab System, dan menyalinnya
       bermakna satu salinan akan terlepas bila syarat itu berubah. */
    const bolehUrusProduk = isAdmin || productGroup === 'own';

    /* Bingkai halaman DIBUANG dari sini (tukar 2026-08-11) — ia kini
       dipasang sekali untuk seluruh app dalam App.jsx (`BingkaiHalaman`).
       Dahulu fail ini menulis bingkai salinannya sendiri (jurang 10px,
       --s-panel, garis, bucu) dan itulah salinan kedua yang boleh
       terpesong daripada bingkai sebenar.

       ⚠️ Dalam v3.9 bekas ini TIDAK boleh `h-full overflow-hidden`: ia
          duduk di dalam kawasan menatal bingkai pusat, jadi mengunci
          tingginya bermakna kandungan yang panjang terpotong dan tiada
          apa boleh ditatal. */
    const Panel = React.Fragment;

    /* Kad produk + bar pilihan pukal di bawah masih bergaya Tailwind
       `dark:` — ia perlu dibalut kelas `dark` HANYA semasa tema v3.9
       gelap; tanpa balutan itu kad putih terapung di atas panel #141414.
       Balutan tetap `dark` di bawah tema TERANG pula memaksa palet
       gelapnya ke atas halaman putih: bar "N selected" keluar merah
       pucat atas merah jambu (kontras 1.3:1, baris Tip hampir hilang)
       dan badge READY/Pending/diskaun pada setiap kad jadi lesu —
       cacat yang ditangkap user 2026-08-16. Corak + alasan sama seperti
       `kelasSeksyenLama` dalam DashboardV39View. Dibaca semasa render
       sahaja: tema hanya boleh bertukar di halaman Settings, dan
       halaman ini di-mount semula setiap kali user kembali dari sana. */
    const kelasSeksyenLama = document.documentElement.classList.contains('s-light') ? '' : 'dark';

    /* Bar pukal ditakrifkan SEKALI di sini, dipasang di dua tempat:
       rupa lama kekal di atas senarai (tidak disentuh), rupa v3.9
       duduk di BAWAH baris label group, tepat atas kad pertama
       (arahan user 2026-08-16). Dua salinan JSX bermakna satu
       daripadanya pasti terlepas pembetulan kemudian hari. */
    const barPukal = (
                        selectedIds.size > 0 && (isAdmin || productGroup === 'own') && (
                            /* Rupa v3.9 memakai kelas bar pukal yang SAMA dengan
                               /workspace (`sesaat-barpukal-v39`, index.css) — satu
                               baris senyap, bukan kotak merah berpil warna-warni.
                               `display: contents` pada pembalut dalam membuatkan
                               anak-anaknya menjadi item flex bar itu sendiri, jadi
                               tiada salinan JSX kedua yang perlu diselenggara. */
                            <div data-keep-selection className={uiV39
                                ? 'flex flex-wrap items-center gap-1.5 sesaat-barpukal-v39'
                                : 'mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}>
                                <div className={uiV39 ? 'contents' : 'flex items-center justify-between gap-3'}>
                                    <div className={uiV39 ? 'contents' : 'flex items-center gap-2 flex-wrap'}>
                                        <span className={uiV39
                                            ? 'sesaat-barpukal-kira'
                                            : 'text-[12px] font-bold text-red-700 dark:text-red-300'}>
                                            {T('products.selectedCount', `${selectedIds.size} selected`, { n: selectedIds.size })}
                                        </span>
                                        {uiV39 && <span className="sesaat-barpukal-garis" />}
                                        {/* ⚠️ Rupa v3.9 SENGAJA menggugurkan kelas cip gaya lama
                                            (`bg-white` + bingkai berwarna + `text-slate-*`).
                                            Bar v3.9 ialah permukaan TERBALIK — cip putih di
                                            atasnya jadi tampalan putih berselerak, dan teks
                                            putih bar akan hilang terus di dalam cip itu.
                                            Menanggalkannya di CSS TIDAK menjadi (dicuba: cip
                                            kekal putih walaupun ditimpa `!important`), jadi ia
                                            digugurkan di sini — arahan user 2026-08-17
                                            "tiada kotak/cip putih". Warna datang sepenuhnya
                                            daripada `.sesaat-barpukal-v39` dalam index.css. */}
                                        {/* ⛔ RUPA LAMA sengaja kekal SATU butang "Select all"
                                            (seluruh tapisan) — itulah yang dilihat pelanggan hari
                                            ini dan ia bersara bila v3.9 naik; menukarnya di sana
                                            hanya menambah risiko tanpa faedah. */}
                                        {uiV39 ? (
                                            <>
                                                {/* Setiap butang membawa nombornya sendiri — itu
                                                    yang menghapuskan kekeliruan "tekan semua,
                                                    dapat 55 sedangkan skrin tunjuk 5". */}
                                                <button
                                                    onClick={pilihHalamanIni}
                                                    disabled={bulkDeleting || halamanBolehDipilih.length === 0}
                                                    className="flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {T('products.bulk.selectPage', 'Pilih halaman (' + halamanBolehDipilih.length + ')', { n: halamanBolehDipilih.length })}
                                                </button>
                                                {/* Disorok bila hanya ada satu halaman — dua butang
                                                    yang membuat benda sama hanyalah bunyi bising. */}
                                                {tapisanBolehDipilih.length > halamanBolehDipilih.length && (
                                                    <button
                                                        onClick={selectAllVisible}
                                                        disabled={bulkDeleting}
                                                        className="flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {T('products.bulk.selectAllN', 'Pilih semua ' + tapisanBolehDipilih.length, { n: tapisanBolehDipilih.length })}
                                                    </button>
                                                )}
                                            </>
                                        ) : (
                                            <button
                                                onClick={selectAllVisible}
                                                disabled={bulkDeleting}
                                                className="px-2.5 py-1 rounded-md text-[10px] font-bold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-red-400 active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {T('products.selectAll', 'Select all')}
                                            </button>
                                        )}
                                        <button
                                            onClick={clearSelection}
                                            disabled={bulkDeleting || selectedIds.size === 0}
                                            className={uiV39
                                                ? 'flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed'
                                                : 'px-2.5 py-1 rounded-md text-[10px] font-bold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-400 active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed'}
                                        >
                                            {T('products.bulk.unselectAll', 'Unselect all')}
                                        </button>
                                    </div>
                                    <div className={uiV39 ? 'flex items-center gap-1.5 ml-auto' : 'flex items-center gap-2'}>
                                        {/* ═══ HANTAR ke Workspace — produk di-stage di panel kiri
                                            Workspace (user pilih avatar/module → Add Product). ═══ */}
                                        {productGroup === 'own' && (
                                            <button
                                                onClick={handleSendToWorkspace}
                                                disabled={bulkDeleting || bulkMoving || bulkProfileBusy || bulkSending || selectedIds.size === 0}
                                                className={uiV39
                                                    ? 'flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed'
                                                    : 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-white dark:bg-slate-800 border border-sky-300 dark:border-sky-700 text-sky-700 dark:text-sky-400 hover:border-sky-500 shadow-sm active:scale-[0.97] transition-all disabled:opacity-40 disabled:cursor-not-allowed'}
                                                title={T('products.bulk.sendToWorkspaceTitle', 'Hantar produk terpilih ke Workspace')}
                                            >
                                                {bulkSending ? (
                                                    <>
                                                        <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>
                                                        {T('products.bulk.processing', 'Memproses…')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="material-symbols-outlined text-[14px]">send</span>
                                                        {T('products.bulk.sendToWorkspace', 'Hantar ke Workspace')}
                                                    </>
                                                )}
                                            </button>
                                        )}
                                        {/* ═══ PROFILE: Pindah / Salin produk terpilih ═══
                                            Pindah = tukar tag (kad berpindah).
                                            Salin  = kad baharu dalam profile lain, kad asal KEKAL
                                                     (produk sama boleh duduk dalam beberapa akaun
                                                     TikTok serentak). */}
                                        {uiV39 && productGroup === 'own' && (
                                            <div className="relative">
                                                <button
                                                    onClick={() => setProfileMenu(profileMenu ? null : { mode: 'move' })}
                                                    disabled={bulkDeleting || bulkMoving || bulkProfileBusy || selectedIds.size === 0}
                                                    className={uiV39
                                                        ? 'flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed'
                                                        : 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-white dark:bg-slate-800 border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 hover:border-emerald-500 shadow-sm active:scale-[0.97] transition-all disabled:opacity-40 disabled:cursor-not-allowed'}
                                                    title={T('products.bulk.profileTitle', 'Pindah atau salin produk terpilih ke profile TikTok')}
                                                >
                                                    {bulkProfileBusy ? (
                                                        <>
                                                            <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>
                                                            {T('products.bulk.processing', 'Memproses…')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="material-symbols-outlined text-[14px]">account_circle</span>
                                                            {T('products.bulk.profile', 'Profile')}
                                                            <span className="material-symbols-outlined text-[14px] opacity-70">{profileMenu ? 'expand_less' : 'expand_more'}</span>
                                                        </>
                                                    )}
                                                </button>
                                                {profileMenu && (
                                                    <>
                                                        <div data-keep-selection className="fixed inset-0 z-40" onClick={() => setProfileMenu(null)} />
                                                        <div data-keep-selection className="absolute top-full right-0 mt-1 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1 min-w-[230px] max-h-[320px] overflow-y-auto">
                                                            {/* Suis Pindah / Salin */}
                                                            <div className="flex gap-1 px-2 py-1.5 border-b border-slate-100 dark:border-slate-800">
                                                                {[
                                                                    { k: 'move', label: T('products.profile.move', 'Pindah') },
                                                                    { k: 'copy', label: T('products.profile.copy', 'Salin') },
                                                                ].map(m => (
                                                                    <button
                                                                        key={m.k}
                                                                        onClick={() => setProfileMenu({ mode: m.k })}
                                                                        className={`flex-1 px-2 py-1 rounded-md text-[10px] font-bold transition-colors ${
                                                                            profileMenu.mode === m.k
                                                                                ? 'bg-slate-800 dark:bg-white text-white dark:text-slate-900'
                                                                                : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'
                                                                        }`}
                                                                    >
                                                                        {m.label}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                            <div className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-wider">
                                                                {profileMenu.mode === 'copy'
                                                                    ? T('products.bulk.copyNTo', `Salin ${selectedIds.size} ke:`, { n: selectedIds.size })
                                                                    : T('products.bulk.moveNTo', `Pindah ${selectedIds.size} ke:`, { n: selectedIds.size })}
                                                            </div>
                                                            {userProfiles.map(pr => (
                                                                <button
                                                                    key={pr.id}
                                                                    onClick={() => profileMenu.mode === 'copy'
                                                                        ? handleBulkCopyToProfile(pr.id)
                                                                        : handleBulkMoveToProfile(pr.id)}
                                                                    className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                                                                >
                                                                    <span className="w-5 h-5 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[9px] font-black text-slate-500 shrink-0">
                                                                        {(pr.name || '?').trim().charAt(0).toUpperCase()}
                                                                    </span>
                                                                    <span className="flex-1 truncate">{pr.name}</span>
                                                                </button>
                                                            ))}
                                                            {userProfiles.length === 0 && (
                                                                <div className="px-3 py-2 text-[10px] text-slate-400 italic">
                                                                    {T('products.profile.emptyMenu', 'Belum ada profile. Tambah profile dahulu.')}
                                                                </div>
                                                            )}
                                                            {profileMenu.mode === 'move' && (
                                                                <>
                                                                    <div className="border-t border-slate-100 dark:border-slate-800 my-0.5" />
                                                                    <button
                                                                        onClick={() => handleBulkMoveToProfile(null)}
                                                                        className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-bold text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                                                                    >
                                                                        {T('products.profile.removeFromProfile', 'Keluarkan dari profile')}
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {/* Move to Group dropdown */}
                                        <div className="relative">
                                            <button
                                                onClick={() => setShowMoveToGroup(v => !v)}
                                                disabled={bulkDeleting || bulkMoving || bulkClearingIntel || selectedIds.size === 0}
                                                className={uiV39
                                                    ? 'flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed'
                                                    : 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-white dark:bg-slate-800 border border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 hover:border-indigo-500 shadow-sm active:scale-[0.97] transition-all disabled:opacity-40 disabled:cursor-not-allowed'}
                                                title={T('products.bulk.moveToGroupTitle', 'Move selected products to a group')}
                                            >
                                                {bulkMoving ? (
                                                    <>
                                                        <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>
                                                        {T('products.bulk.moving', 'Moving...')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="material-symbols-outlined text-[14px]">drive_file_move</span>
                                                        {T('products.bulk.moveToGroup', 'Move to group')}
                                                        <span className="material-symbols-outlined text-[14px] opacity-70">{showMoveToGroup ? 'expand_less' : 'expand_more'}</span>
                                                    </>
                                                )}
                                            </button>
                                            {showMoveToGroup && (
                                                <>
                                                    {/* Backdrop untuk close bila klik luar */}
                                                    <div data-keep-selection className="fixed inset-0 z-40" onClick={() => setShowMoveToGroup(false)} />
                                                    <div data-keep-selection className="absolute top-full right-0 mt-1 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1 min-w-[200px] max-h-[300px] overflow-y-auto">
                                                        <div className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                                                            {T('products.bulk.moveNTo', `Move ${selectedIds.size} to:`, { n: selectedIds.size })}
                                                        </div>
                                                        <button
                                                            onClick={() => handleBulkMoveToGroup(null)}
                                                            className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-bold text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                                                        >
                                                            <span className="material-symbols-outlined text-[14px]">remove_circle_outline</span>
                                                            {T('products.bulk.removeGroup', 'Remove group')}
                                                        </button>
                                                        {visibleGroups.length > 0 && (
                                                            <div className="border-t border-slate-100 dark:border-slate-800 my-0.5" />
                                                        )}
                                                        {visibleGroups.map(g => (
                                                            <button
                                                                key={g.id}
                                                                onClick={() => handleBulkMoveToGroup(g.id)}
                                                                className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                                                            >
                                                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                                                                <span className="flex-1 truncate">{g.name}</span>
                                                            </button>
                                                        ))}
                                                        {visibleGroups.length === 0 && (
                                                            <div className="px-3 py-2 text-[10px] text-slate-400 italic">
                                                                {profilAktif
                                                                    ? T('products.bulk.noGroupsForProfile', 'Akaun ini belum ada group. Cipta group melalui butang Group.')
                                                                    : T('products.bulk.noGroupsYet', 'Tiada group dicipta lagi.')}
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* Clear Intelligence — admin only */}
                                        {isAdmin && (
                                            <button
                                                onClick={openBulkClearIntelConfirm}
                                                disabled={bulkDeleting || bulkMoving || bulkClearingIntel || selectedIds.size === 0}
                                                className={uiV39
                                                    ? 'flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed'
                                                    : 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:border-amber-500 shadow-sm active:scale-[0.97] transition-all disabled:opacity-40 disabled:cursor-not-allowed'}
                                                title={T('products.bulk.clearIntelTitle', 'Clear intelligence cache (admin only) — reset hasIntelligence + remove cached JSON')}
                                            >
                                                {bulkClearingIntel ? (
                                                    <>
                                                        <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>
                                                        {T('products.bulk.clearing', 'Clearing...')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="material-symbols-outlined text-[14px]">cleaning_services</span>
                                                        {T('products.bulk.clearIntel', 'Clear Intel')}
                                                    </>
                                                )}
                                            </button>
                                        )}

                                        {/* Delete — satu-satunya tindakan yang kekal MERAH dalam
                                            rupa v3.9 (`sesaat-bahaya`), sama seperti bar workspace:
                                            ia memusnahkan kerja pelanggan. */}
                                        <button
                                            onClick={openBulkDeleteConfirm}
                                            disabled={bulkDeleting || bulkMoving || bulkClearingIntel || selectedIds.size === 0}
                                            className={uiV39
                                                ? 'sesaat-bahaya flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed'
                                                : 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white shadow active:scale-[0.97] transition-all disabled:opacity-40 disabled:cursor-not-allowed'}
                                        >
                                            <span className="material-symbols-outlined text-[14px]">delete</span>
                                            {T('products.bulk.deleteN', `Delete (${selectedIds.size})`, { n: selectedIds.size })}
                                        </button>
                                    </div>
                                </div>
                                {/* Tip row — rupa lama sahaja. Bar v3.9 ialah SATU baris senyap
                                    (sama seperti /workspace); baris tip menjadikannya dua baris.

                                    Ayat lalai kekal JSX supaya petak kekunci <kbd> pada rupa
                                    lama tidak berubah langsung. Kamus menyimpan ayat ini
                                    dengan pemboleh ubah {kekunci} sebagai teks biasa — jadi
                                    kalau baris ini dibuka semula untuk v3.9 kelak, petak
                                    kekunci itu perlu dibina semula dahulu. */}
                                <div className={uiV39 ? 'hidden' : 'mt-2 pt-2 border-t border-red-200/60 dark:border-red-800/60 flex items-center gap-3 text-[10px] text-red-600/80 dark:text-red-300/70 flex-wrap'}>
                                    <span className="flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[12px]">lightbulb</span>
                                        {T('products.bulk.tipShiftRange',
                                            <>Tip: <kbd className="px-1 py-0.5 rounded bg-white dark:bg-slate-800 border border-red-200 dark:border-red-700 font-mono text-[9px] mx-0.5">Shift</kbd> + click to select range</>,
                                            { kekunci: 'Shift' })}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        {T('products.bulk.tipEscClear',
                                            <><kbd className="px-1 py-0.5 rounded bg-white dark:bg-slate-800 border border-red-200 dark:border-red-700 font-mono text-[9px]">Esc</kbd> to clear</>,
                                            { kekunci: 'Esc' })}
                                    </span>
                                </div>
                            </div>
                        )
    );

    return (
        <div
            className={uiV39
                ? `${kelasSeksyenLama} flex-1 min-w-0`.trim()
                : 'flex-1 flex flex-col h-full overflow-hidden bg-slate-50 dark:bg-slate-950'}
            style={undefined}
        >
        <Panel>
            {uiV39 ? (
                /* ═══ KEPALA v3.9 ═══════════════════════════════════════
                   Susunan diambil terus daripada prototaip /products-v3.9:
                     baris 1 — tajuk + tab System/My Products di kanan
                     baris 2 — carian · susunan · status · butang tindakan
                   Butang tindakan duduk di HUJUNG KANAN baris status
                   (arahan user 2026-08-10). */
                <div style={{ padding: '26px 28px 0' }}>
                    {/* ⚠️ `md:hidden` duduk pada PEMBALUT, bukan pada butang.
                        `.s-iconbtn` mengisytiharkan `display: grid` dengan
                        kekhususan yang sama seperti `.md\:hidden`, dan ui.css
                        dimuat kemudian — jadi butang itu kekal kelihatan pada
                        desktop kalau kedua-dua kelas dilekatkan pada elemen
                        yang sama. */}
                    {onMenuClick && (
                        <div className="md:hidden" style={{ marginBottom: 14 }}>
                            <button onClick={onMenuClick} aria-label={T('sidebar.openMenu', 'Buka menu')} className="s-iconbtn">
                                <IkonMenu size={16} strokeWidth={1.9} />
                            </button>
                        </div>
                    )}

                    <PageHeader
                        title={T('products.title', 'Products')}
                        subtitle={T('products.subtitle', 'Simpanan produk anda. Tarik masuk ke Workspace bila nak jalankan.')}
                    />

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
                        {/* Tab System/My Products duduk di KIRI kotak carian
                            (arahan user 2026-08-16, sama seperti prototaip
                            /products-v3.9) — ia penapis senarai, jadi tempatnya
                            bersama kawalan penapis, bukan di kepala halaman. */}
                        <Segmented
                            value={productGroup}
                            onChange={(v) => {
                                setProductGroup(v);
                                navigate(v === 'own' ? '/products/myproducts' : '/products/system');
                            }}
                            options={[
                                { value: 'system', label: <>{T('products.scope.system', 'System')} <span style={{ color: 'var(--s-ink-3)' }}>{systemCount}</span></> },
                                { value: 'own', label: <>{T('products.tab.myProducts', 'My Products')} <span style={{ color: 'var(--s-ink-3)' }}>{ownCount}</span></> },
                            ]}
                        />

                        {/* ⚠️ marginLeft:auto — kotak carian ditolak ke HUJUNG KANAN baris
                            (arahan user 2026-08-21); tab System/My Products kekal di kiri.
                            maxWidth:100% ialah pagar limpahan, BUKAN peregang: diukur pada
                            bekas 340px, baris berpatah dan kotak turun ke baris kedua kekal
                            280px dan rata kanan (limpahan mendatar 0). Kalau kelak ia perlu
                            memenuhi lebar pada telefon, itu perlukan media query dalam CSS —
                            gaya inline tidak boleh memegangnya. */}
                        <Field
                            icon={<IkonCari size={15} strokeWidth={1.9} />}
                            placeholder={T('products.searchPlaceholder', 'Cari produk…')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ width: 280, maxWidth: '100%', marginLeft: 'auto' }}
                        />

                    </div>
                </div>
            ) : (
            <>
            {/* Header with Breadcrumb */}
            <div className="px-4 sm:px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
{/* Title + Actions */}
                <div className="flex items-center gap-3 mb-3">
                    {onMenuClick && (
                        <button
                            onClick={onMenuClick}
                            aria-label={T('sidebar.openMenu', 'Buka menu')}
                            className="md:hidden -ml-1.5 w-9 h-9 flex items-center justify-center rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0"
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: 24 }}>menu</span>
                        </button>
                    )}
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                        <span className="material-symbols-outlined text-white text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>{currentFolder?.icon || 'folder'}</span>
                    </div>
                    <div className="flex-1">
                        <h1 className="text-lg font-bold text-slate-800 dark:text-white">{currentFolder?.name}</h1>
                    </div>

                </div>


{/* Search + Filter + Sort Bar */}
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Search */}
                    <div className="relative flex-1 min-w-[200px] max-w-md">
                        <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-[16px] text-slate-400">search</span>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={T('products.searchPlaceholder', 'Cari produk...')}
                            className="w-full h-8 pl-8 pr-3 text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400/30 focus:border-purple-400 placeholder:text-slate-300 transition-all"
                        />
                    </div>

{/* Sort */}
                    <select
                        value={sortBy}
                        /* Rupa lama tiada kawalan arah — reset ke arah lalai jenis
                           supaya perangai kekal sama (number 1→10, name A→Z,
                           date terbaru dahulu) walaupun logik susun kini berkongsi
                           `sortDir` dengan rupa v3.9. */
                        onChange={(e) => { const v = e.target.value; setSortBy(v); setSortDir(ARAH_LALAI_PRODUK[v] || 'asc'); }}
                        className="h-8 px-2.5 text-[10px] font-bold bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-400/30 cursor-pointer"
                    >
                        <option value="number">{T('products.sort.number', 'Sort: Number')}</option>
                        <option value="name">{T('products.sort.name', 'Sort: Name')}</option>
                        <option value="date">{T('products.sort.newest', 'Sort: Terbaru')}</option>
                    </select>
                </div>

            </div>
            </>
            )}

            {/* Product List */}
            {/* v3.9: panel di luar yang menatal, jadi badan ini TIDAK boleh
                menatal sendiri — dua kawasan menatal bersarang bermakna
                kepala halaman hilang dari skrin sementara panel kekal diam. */}
            <div
                className={uiV39 ? '' : 'flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6'}
                style={uiV39 ? { padding: '0 28px 48px' } : undefined}
            >
                {loadingProducts ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {[1,2,3,4,5,6].map(i => (
                            <div key={i} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 animate-pulse">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-12 h-12 rounded-lg bg-slate-200 dark:bg-slate-800"></div>
                                    <div className="flex-1 space-y-2">
                                        <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded w-1/4"></div>
                                        <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded w-3/4"></div>
                                    </div>
                                </div>
                                <div className="h-8 bg-slate-100 dark:bg-slate-800 rounded"></div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Group Tabs — System / Own (always visible).
                            ⚠️ v3.9: tab, status produk dan butang tindakan
                            sudah berpindah ke kepala halaman (arahan user
                            2026-08-10), jadi baris ini disorok di sana —
                            bukan dipadam, kerana rupa lama masih memakainya. */}
                        {!uiV39 && (
                        <div className="flex flex-col items-start gap-2.5 md:flex-row md:items-center md:justify-between md:gap-3 mb-3">
                            <div className="inline-flex items-center bg-white dark:bg-slate-900 rounded-xl p-1 border border-slate-200 dark:border-slate-700 shadow-sm">
                                {[
                                    { key: 'system', label: T('products.scope.system', 'System'), icon: 'auto_awesome', count: systemCount },
                                    { key: 'own', label: T('products.tab.myProducts', 'My Products'), icon: 'person', count: ownCount },
                                ].map((g) => (
                                    <button
                                        key={g.key}
                                        onClick={() => { setProductGroup(g.key); navigate(g.key === 'own' ? '/products/myproducts' : '/products/system'); }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all duration-200 ${
                                            productGroup === g.key
                                                ? g.key === 'system'
                                                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                                    : 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 shadow-sm'
                                                : 'text-slate-400 dark:text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-600'
                                        }`}
                                    >
                                        <span className="material-symbols-outlined text-[14px]" style={{ fontVariationSettings: productGroup === g.key ? "'FILL' 1" : undefined }}>{g.icon}</span>
                                        {g.label}
                                        <span className={`text-[10px] font-extrabold ml-0.5 px-1.5 py-0.5 rounded-md ${
                                            productGroup === g.key
                                                ? g.key === 'system'
                                                    ? 'bg-indigo-100 dark:bg-indigo-800/40 text-indigo-500'
                                                    : 'bg-teal-100 dark:bg-teal-800/40 text-teal-500'
                                                : 'text-slate-300 dark:text-slate-600'
                                        }`}>{g.count}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Stats Chips — clickable filter, ikut tab aktif */}
                            <div className="flex items-center gap-2 flex-wrap">
                                <button
                                    onClick={() => setFilterMode(filterMode === 'all' ? 'all' : 'all')}
                                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all ${filterMode === 'all' ? 'bg-slate-200 dark:bg-slate-700 ring-2 ring-slate-400/30' : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                                >
                                    <span className="material-symbols-outlined text-[12px] text-slate-500">inventory_2</span>
                                    <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400">{T('products.countTotal', `${totalProducts} Total`, { n: totalProducts })}</span>
                                </button>
                                <button
                                    onClick={() => setFilterMode(filterMode === 'with_intel' ? 'all' : 'with_intel')}
                                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all ${filterMode === 'with_intel' ? 'bg-emerald-100 dark:bg-emerald-900/40 ring-2 ring-emerald-400/30' : 'bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40'}`}
                                >
                                    <span className="material-symbols-outlined text-[12px] text-emerald-500" style={{ fontVariationSettings: "'FILL' 1" }}>auto_awesome</span>
                                    <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400">{T('products.countReady', `${withIntel} Ready`, { n: withIntel })}</span>
                                </button>
                                <button
                                    onClick={() => setFilterMode(filterMode === 'without_intel' ? 'all' : 'without_intel')}
                                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all ${filterMode === 'without_intel' ? 'bg-amber-100 dark:bg-amber-900/40 ring-2 ring-amber-400/30' : 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40'}`}
                                >
                                    <span className="material-symbols-outlined text-[12px] text-amber-500">pending</span>
                                    <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">{T('products.countPending', `${withoutIntel} Pending`, { n: withoutIntel })}</span>
                                </button>
                            </div>

                            {/* Action Buttons — tab-aware */}
                            <div className="flex items-center gap-2 flex-wrap">
                                {(isAdmin || productGroup === 'own') && (
                                    <button
                                        onClick={() => setShowManageGroups(true)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-violet-300 dark:hover:border-violet-600 text-slate-600 dark:text-slate-300 hover:text-violet-600 dark:hover:text-violet-400 text-[11px] font-bold shadow-sm active:scale-[0.97] transition-all"
                                    >
                                        <span className="material-symbols-outlined text-[15px]">label</span>
                                        {T('products.toolbar.group', 'Group')}
                                    </button>
                                )}
                                {(isAdmin || productGroup === 'own') && (
                                    <button
                                        onClick={openManualAdd}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-600 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 text-[11px] font-bold shadow-sm active:scale-[0.97] transition-all"
                                    >
                                        <span className="material-symbols-outlined text-[15px]">edit_note</span>
                                        {T('products.toolbar.manual', 'Manual')}
                                    </button>
                                )}
                                {(isAdmin || productGroup === 'own') && (
                                    <button
                                        onClick={() => setShowBulkUpload(true)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-[11px] font-bold shadow-lg shadow-indigo-500/20 active:scale-[0.97] transition-all"
                                    >
                                        <span className="material-symbols-outlined text-[15px]">upload_file</span>
                                        {T('products.toolbar.bulkUpload', 'Bulk Upload')}
                                    </button>
                                )}
                                {/* Admin-only: bulk select mode untuk Generate Intelligence */}
                                {isAdmin && productGroup === 'system' && (
                                    <button
                                        onClick={toggleIntelSelectMode}
                                        disabled={intelBatchActive || bulkDeleting}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold shadow-sm active:scale-[0.97] transition-all ${
                                            intelSelectMode
                                                ? 'bg-violet-600 hover:bg-violet-700 text-white border border-violet-700'
                                                : 'bg-white dark:bg-slate-800 border border-violet-300 dark:border-violet-700 text-violet-600 dark:text-violet-400 hover:border-violet-500'
                                        } ${(intelBatchActive || bulkDeleting) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        title={T('products.toolbar.intelSelectTitle', 'Pilih banyak items untuk generate intelligence sekaligus')}
                                    >
                                        <span className="material-symbols-outlined text-[15px]">{intelSelectMode ? 'check_box' : 'select_all'}</span>
                                        {intelSelectMode
                                            ? T('products.toolbar.intelSelectedN', `Selected: ${intelSelectedIds.size}`, { n: intelSelectedIds.size })
                                            : T('products.toolbar.selectForIntel', 'Select for Intel')}
                                    </button>
                                )}
                            </div>
                        </div>
                        )}

                        {/* ═══ BULK DELETE: Action bar (rupa lama — v3.9 dipasang bawah label group) ═══ */}
                        {!uiV39 && barPukal}

                        {/* ═══ ADMIN INTEL: Bulk action bar — visible bila select mode ON ═══ */}
                        {isAdmin && intelSelectMode && productGroup === 'system' && (
                            <div className="flex items-center justify-between gap-3 mb-4 p-3 rounded-xl bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[12px] font-bold text-violet-700 dark:text-violet-300">
                                        {T('products.selectedCount', `${intelSelectedIds.size} dipilih`, { n: intelSelectedIds.size })}
                                    </span>
                                    <button
                                        onClick={selectAllPending}
                                        disabled={intelBatchActive}
                                        className="px-2.5 py-1 rounded-md text-[10px] font-bold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-violet-400 active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {T('products.intel.selectAllPending', 'Select all pending')}
                                    </button>
                                    <button
                                        onClick={clearIntelSelection}
                                        disabled={intelBatchActive || intelSelectedIds.size === 0}
                                        className="px-2.5 py-1 rounded-md text-[10px] font-bold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-400 active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {T('products.intel.clear', 'Clear')}
                                    </button>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => openBulkConfirm(false)}
                                        disabled={intelBatchActive || intelSelectedIds.size === 0}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow active:scale-[0.97] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <span className="material-symbols-outlined text-[14px]">auto_awesome</span>
                                        {T('products.intel.generateN', `Generate (${intelSelectedIds.size})`, { n: intelSelectedIds.size })}
                                    </button>
                                    <button
                                        onClick={() => openBulkConfirm(true)}
                                        disabled={intelBatchActive || intelSelectedIds.size === 0}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:border-amber-500 active:scale-[0.97] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                        title={T('products.intel.regenTitle', 'Resubmit — bypass cache & re-generate (cost lagi sekali)')}
                                    >
                                        <span className="material-symbols-outlined text-[14px]">refresh</span>
                                        {T('products.intel.regenerate', 'Re-Generate')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ═══ ADMIN INTEL: Batch progress banner ═══ */}
                        {intelBatchActive && intelBatchStats && (
                            <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                                <span className="material-symbols-outlined text-[20px] text-blue-600 dark:text-blue-400 animate-spin">progress_activity</span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[12px] font-bold text-blue-800 dark:text-blue-300">
                                        {T('products.intel.progress',
                                            `Generating intelligence — ${intelBatchStats.done + intelBatchStats.error}/${intelBatchStats.total} done`,
                                            { siap: intelBatchStats.done + intelBatchStats.error, jumlah: intelBatchStats.total })}
                                        {intelBatchStats.running > 0 && <span className="ml-2 text-[10px] font-medium text-blue-600 dark:text-blue-400">{T('products.intel.running', `(${intelBatchStats.running} running)`, { n: intelBatchStats.running })}</span>}
                                        {intelBatchStats.error > 0 && <span className="ml-2 text-[10px] font-medium text-red-600 dark:text-red-400">{T('products.intel.errors', `(${intelBatchStats.error} error)`, { n: intelBatchStats.error })}</span>}
                                    </div>
                                    <div className="mt-1.5 h-1.5 rounded-full bg-blue-100 dark:bg-blue-900 overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-all"
                                            style={{ width: `${Math.round(((intelBatchStats.done + intelBatchStats.error) / Math.max(1, intelBatchStats.total)) * 100)}%` }}
                                        />
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIntelBatchCancelled(true)}
                                    className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-white dark:bg-slate-800 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:border-red-500 active:scale-[0.97] transition-all"
                                >
                                    {T('products.intel.stop', 'Stop')}
                                </button>
                            </div>
                        )}

                        {/* ═══ BARIS PROFILE TIKTOK — My Products, rupa v3.9 ═══
                            1 profile = 1 akaun TikTok. Kotak = suis: klik untuk
                            menapis senarai. "Semua produk" ialah landing lalai dan
                            ia memaparkan SEMUA kad termasuk yang tiada profile —
                            jangan tukar lalai ini kepada profile pertama. */}
                        {uiV39 && productGroup === 'own' && (
                            <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', flexWrap: 'wrap', marginBottom: 14 }}>
                                {userProfiles.length < MAX_TIKTOK_PROFILES && (
                                    <button
                                        type="button"
                                        onClick={() => setProfileModal({ mode: 'add', name: '', username: '', color: PROFILE_COLORS[userProfiles.length % PROFILE_COLORS.length], icon: null })}
                                        title={T('products.profile.addTitle', 'Tambah profile TikTok')}
                                        style={{
                                            flex: '0 0 92px', minHeight: 104, cursor: 'pointer',
                                            border: '1.5px dashed var(--s-line-strong)', borderRadius: 'var(--s-r)',
                                            background: 'none', fontFamily: 'inherit', color: 'var(--s-ink-3)',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                                            justifyContent: 'center', gap: 6, padding: '10px 8px',
                                        }}
                                    >
                                        <IkonTambah size={18} strokeWidth={1.9} />
                                        <span style={{ fontSize: 12, lineHeight: 1.3, textAlign: 'center' }}>{T('products.profile.add', 'Tambah profile')}</span>
                                    </button>
                                )}

                                {/* Semua produk — termasuk yang tiada profile.
                                    Chip "Tanpa profile" duduk DI DALAM kad ini (arahan user
                                    2026-08-21). Dulu ia pil berasingan yang terpencil di hujung
                                    kanan baris, jauh daripada kad — mata membacanya sebagai
                                    nombor yang sepatutnya sepadan dengan kad di sebelahnya,
                                    lalu "88" nampak bercanggah dengan "90". Di dalam kad ini ia
                                    terbaca betul: daripada 90 itu, 88 belum ada profile. */}
                                <KadProfilTT
                                    tajuk={T('products.profile.allTitle', 'Semua produk')}
                                    subtajuk={T('products.profile.allSub', 'Termasuk tanpa profile')}
                                    ikon
                                    aktif={profileFilter === 'all'}
                                    jumlah={currentTabProducts.length}
                                    sedia={currentTabProducts.filter(p => p.hasIntelligence).length}
                                    tunggu={currentTabProducts.filter(p => !p.hasIntelligence).length}
                                    onPilih={() => setProfileFilter('all')}
                                    tanpaProfilBil={currentTabProducts.filter(p => !p.profileId).length}
                                    tanpaProfilAktif={profileFilter === 'none'}
                                    onPilihTanpaProfil={() => setProfileFilter(profileFilter === 'none' ? 'all' : 'none')}
                                    tanpaProfilTeks={T('products.profile.withoutProfile',
                                        'Tanpa profile ' + currentTabProducts.filter(p => !p.profileId).length,
                                        { n: currentTabProducts.filter(p => !p.profileId).length })}
                                />

                                {userProfiles.map(pr => {
                                    const senarai = currentTabProducts.filter(p => p.profileId === pr.id);
                                    // Aktiviti terakhir = cap masa terbaru antara produk akaun ini
                                    // (dikira dari senarai sedia ada — sifar bacaan tambahan).
                                    const terakhirProfil = senarai.reduce((mx, p) => {
                                        const d = p.updatedAt?.toDate?.() || p.addedAt?.toDate?.() || null;
                                        return d ? Math.max(mx, d.getTime()) : mx;
                                    }, 0) || undefined;
                                    return (
                                        <KadProfilTT
                                            key={pr.id}
                                            tajuk={pr.name}
                                            subtajuk={pr.username ? `@${pr.username}` : T('products.profile.noUsername', 'Tiada username')}
                                            inisial={(pr.name || '?').trim().charAt(0).toUpperCase()}
                                            warna={pr.color}
                                            ikonNama={pr.icon}
                                            terakhir={terakhirProfil}
                                            aktif={profileFilter === pr.id}
                                            jumlah={senarai.length}
                                            /* Kiraan group = group MILIK akaun ini, bukan bilangan
                                               group yang produknya sedang duduk. Versi lama mengira
                                               `new Set(senarai.map(p => p.customGroup))`, jadi akaun
                                               yang baru cipta group kosong tertulis "0 groups"
                                               sedangkan baris pil di bawah memaparkan group itu —
                                               dua nombor bercanggah pada skrin yang sama.
                                               Sumber yang sama dengan baris pil: userLibraryGroups. */
                                            group={userLibraryGroups.filter(g => g.profileId === pr.id).length}
                                            sedia={senarai.filter(p => p.hasIntelligence).length}
                                            tunggu={senarai.filter(p => !p.hasIntelligence).length}
                                            onPilih={() => setProfileFilter(pr.id)}
                                            onSunting={() => setProfileModal({ mode: 'edit', id: pr.id, name: pr.name, username: pr.username || '', color: pr.color || PROFILE_COLORS[0], icon: pr.icon || null })}
                                        />
                                    );
                                })}

                                {/* Slot kosong sehingga 3 — 1 lesen = 3-5 akaun TikTok */}
                                {Array.from({ length: Math.max(0, DEFAULT_PROFILE_SLOTS - userProfiles.length) }).map((_, i) => (
                                    <KadProfilTT
                                        key={`slot_${i}`}
                                        tajuk={T('products.profile.slot', `Profile ${userProfiles.length + i + 1}`, { n: userProfiles.length + i + 1 })}
                                        subtajuk={T('products.profile.unused', 'Belum digunakan')}
                                        kosong
                                        onPilih={() => setProfileModal({ mode: 'add', name: `Profile ${userProfiles.length + i + 1}`, username: '', color: PROFILE_COLORS[(userProfiles.length + i) % PROFILE_COLORS.length], icon: null })}
                                    />
                                ))}

                            </div>
                        )}

                        {/* ═══ BARIS KAWALAN AKAUN (arahan user 2026-08-21) ═══
                            Dulu baris ini duduk di KEPALA halaman, di ATAS kad profile — jadi ia
                            terbaca sebagai kawalan seluruh halaman sedangkan kiraan + butangnya
                            kini milik akaun yang SEDANG dibuka. Ia diturunkan ke sini supaya
                            turutan membaca betul: pilih akaun → kawalan akaun itu → group akaun
                            itu → senarai produk.
                            ⚠️ SENGAJA tiada pagar productGroup === own: tab System tiada
                            konsep akaun, tetapi susun/kiraan/butangnya tetap diperlukan di sana. */}
                        {uiV39 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                            {/* Status produk — kekal PENAPIS yang boleh ditekan,
                                bukan sekadar kiraan. Tekan sekali lagi = buang
                                penapis (perangai sama seperti rupa lama). */}
                            {[
                                { key: 'all', tone: 'neutral', teks: T('products.countTotal', `${totalProducts} Total`, { n: totalProducts }) },
                                { key: 'with_intel', tone: 'ok', teks: T('products.countReady', `${withIntel} Ready`, { n: withIntel }) },
                                { key: 'without_intel', tone: 'warn', teks: T('products.countPending', `${withoutIntel} Pending`, { n: withoutIntel }) },
                            ].map((s) => (
                                <button
                                    key={s.key}
                                    type="button"
                                    onClick={() => setFilterMode(filterMode === s.key ? 'all' : s.key)}
                                    className={`s-badge s-badge--${s.tone}`}
                                    style={{
                                        cursor: 'pointer', fontFamily: 'inherit', border: 0, height: 24,
                                        boxShadow: filterMode === s.key ? 'inset 0 0 0 1px currentColor' : 'none',
                                    }}
                                >
                                    {s.teks}
                                </button>
                            ))}

                            {/* Suis paparan Grid / Jadual — ikon sahaja, sama seperti
                                prototaip /products-v3.9. Ia tetapan PANDANGAN, bukan
                                penapis, jadi ia duduk di hujung kanan bersama butang
                                tindakan dan bukan bersama tab System/My Products. */}
                            {/* ⚠️ Ditulis sebagai dua butang, BUKAN .map() atas senarai
                                {id, Ikon}: ESLint projek ini tiada `react/jsx-uses-vars`,
                                jadi komponen ikon yang datang daripada pemusnahan
                                parameter dilaporkan sebagai pemboleh ubah tidak
                                digunakan (lihat memori projek). */}
                            {/* ⚠️ marginLeft:auto duduk pada PEMBALUT ini, bukan lagi pada
                                .s-viewseg — ia yang menolak kumpulan kanan (susun + suis
                                paparan + butang tindakan) ke hujung kanan baris. Kalau ia
                                dipindah balik ke .s-viewseg, kawalan susun akan tertinggal
                                melekat pada pil status di sebelah kiri. */}
                            <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                            {/* Kawalan susun DIKONGSI dengan Workspace (SusunMenuV39):
                                satu jenis satu baris, arah diterbalikkan dengan menekan
                                jenis yang sama sekali lagi. Label/tip diselesaikan di sini
                                kerana SusunMenuV39 menerima senarai siap (corak sama
                                PemilihProduk dalam PromptComposerV39). */}
                            <SusunMenuV39
                                pilihan={SUSUNAN_PRODUK_V39.map((s) => ({
                                    ...s,
                                    label: T(s.kunci, s.lalaiTeks),
                                    tip: s.kunciTip
                                        ? { asc: T(s.kunciTip.asc, s.tipTeks.asc), desc: T(s.kunciTip.desc, s.tipTeks.desc) }
                                        : s.tip,
                                }))}
                                jenis={sortBy}
                                arah={sortDir}
                                onTukar={(j, a) => { setSortBy(j); setSortDir(a); }}
                            />
                            </span>

                            <div className="s-viewseg">
                                <button
                                    type="button"
                                    aria-label={T('products.view.grid', 'Paparan grid')} title={T('products.view.grid', 'Paparan grid')}
                                    aria-pressed={paparanProduk === 'grid'}
                                    className={`s-viewseg__b${paparanProduk === 'grid' ? ' s-viewseg__b--on' : ''}`}
                                    onClick={() => tukarPaparanProduk('grid')}
                                >
                                    <IkonGrid size={15} strokeWidth={1.9} />
                                </button>
                                <button
                                    type="button"
                                    aria-label={T('products.view.table', 'Paparan jadual')} title={T('products.view.table', 'Paparan jadual')}
                                    aria-pressed={paparanProduk === 'jadual'}
                                    className={`s-viewseg__b${paparanProduk === 'jadual' ? ' s-viewseg__b--on' : ''}`}
                                    onClick={() => tukarPaparanProduk('jadual')}
                                >
                                    <IkonSenarai size={15} strokeWidth={1.9} />
                                </button>
                            </div>

                            {/* Butang tindakan — hujung kanan baris yang sama */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {bolehUrusProduk && (
                                    <Btn variant="ghost" size="sm" icon={<IkonTag size={14} strokeWidth={1.9} />}
                                        onClick={() => setShowManageGroups(true)}>
                                        {T('products.toolbar.group', 'Group')}
                                    </Btn>
                                )}
                                {/* Manual — ADMIN SAHAJA (arahan user 2026-08-21). Pelanggan tambah
                                    produk melalui Import Produk; borang manual melahirkan kad PENDING
                                    (tiada data scrape), jadi ia bukan laluan untuk pelanggan. */}
                                {isAdmin && (
                                    <Btn variant="ghost" size="sm" icon={<IkonPensel size={14} strokeWidth={1.9} />}
                                        onClick={openManualAdd}>
                                        {T('products.toolbar.manual', 'Manual')}
                                    </Btn>
                                )}
                                {/* Import Produk (Fasa 2) — My Products sahaja, disorok pada telefon
                                    (import perlukan extension = desktop).
                                    ⚠️ variant="primary" (permukaan terbalik) SENGAJA — arahan user
                                    2026-08-21. Selepas Manual dikunci kepada admin, inilah SATU-SATUNYA
                                    cara pelanggan menambah produk, jadi ia butang paling menonjol pada
                                    baris ini. Bulk Upload (admin sahaja) diturunkan ke ghost. */}
                                {productGroup === 'own' && (
                                    <span className="hidden md:inline-flex">
                                        <Btn variant="primary" size="sm" icon={<IkonUpload size={14} strokeWidth={1.9} />}
                                            onClick={() => setImportOpen(true)}>
                                            {T('products.toolbar.import', 'Import Produk')}
                                        </Btn>
                                    </span>
                                )}
                                {bolehUrusProduk && (
                                    <Btn variant="ghost" size="sm" icon={<IkonUpload size={14} strokeWidth={1.9} />}
                                        onClick={() => setShowBulkUpload(true)}>
                                        {T('products.toolbar.bulkUpload', 'Bulk Upload')}
                                    </Btn>
                                )}
                                {isAdmin && productGroup === 'system' && (
                                    <Btn
                                        variant={intelSelectMode ? 'primary' : 'ghost'}
                                        size="sm"
                                        icon={<IkonPilih size={14} strokeWidth={1.9} />}
                                        disabled={intelBatchActive || bulkDeleting}
                                        onClick={toggleIntelSelectMode}
                                        title={T('products.toolbar.intelSelectTitle', 'Pilih banyak items untuk generate intelligence sekaligus')}
                                    >
                                        {intelSelectMode
                                            ? T('products.toolbar.intelSelectedN', `Selected: ${intelSelectedIds.size}`, { n: intelSelectedIds.size })
                                            : T('products.toolbar.selectForIntel', 'Select for Intel')}
                                    </Btn>
                                )}
                            </div>
                            </div>
                        )}

                        {/* Custom Group Filter Pills — ikut akaun yang ditapis */}
                        {visibleGroups.length > 0 && (
                            <div className="flex items-center gap-2 mb-4 flex-wrap">
                                <button
                                    onClick={() => setCustomGroupFilter('all')}
                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-bold border transition-all ${
                                        customGroupFilter === 'all'
                                            ? 'bg-slate-800 dark:bg-white text-white dark:text-slate-900 border-slate-800 dark:border-white'
                                            : 'bg-white dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                                    }`}
                                >
                                    <span className="material-symbols-outlined text-[11px]">label</span>
                                    {T('products.group.all', 'Semua Group')}
                                </button>
                                {visibleGroups.map(g => {
                                    const count = produkSkopProfil.filter(p => p.customGroup === g.id).length;
                                    const isActive = customGroupFilter === g.id;
                                    return (
                                        <button
                                            key={g.id}
                                            onClick={() => setCustomGroupFilter(isActive ? 'all' : g.id)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold border transition-all ${
                                                isActive
                                                    ? 'text-white border-transparent shadow-sm'
                                                    : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-400'
                                            }`}
                                            style={isActive ? { backgroundColor: g.color, borderColor: g.color } : {}}
                                        >
                                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.7)' : g.color }}></span>
                                            {g.name}
                                            <span className={`text-[9px] font-extrabold ml-0.5 ${isActive ? 'text-white/80' : 'text-slate-400'}`}>{count}</span>
                                        </button>
                                    );
                                })}
                                {produkSkopProfil.some(tiadaGroup) && (
                                    <button
                                        onClick={() => setCustomGroupFilter(customGroupFilter === 'uncategorized' ? 'all' : 'uncategorized')}
                                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-bold border transition-all ${
                                            customGroupFilter === 'uncategorized'
                                                ? 'bg-slate-600 text-white border-slate-600'
                                                : 'bg-white dark:bg-slate-900 text-slate-400 border-slate-300 dark:border-slate-600 hover:border-slate-400 border-dashed'
                                        }`}
                                    >
                                        <span className="material-symbols-outlined text-[11px]">help_outline</span>
                                        {T('products.group.none', 'Tiada Group')}
                                        <span className="text-[9px] font-extrabold ml-0.5">{produkSkopProfil.filter(tiadaGroup).length}</span>
                                    </button>
                                )}

                                {/* Pindah SATU group penuh ke profile — muncul bila satu
                                    group sedang ditapis. Ia mengambil semua produk dalam
                                    group itu, tidak kira profile asalnya. */}
                                {uiV39 && productGroup === 'own' && customGroupFilter !== 'all' && customGroupFilter !== 'uncategorized' && (
                                    <button
                                        type="button"
                                        onClick={() => setGroupToProfile({
                                            groupId: customGroupFilter,
                                            groupName: activeGroups.find(g => g.id === customGroupFilter)?.name || T('products.group.fallbackName', 'Group'),
                                        })}
                                        title={T('products.group.moveGroupTitle', 'Pindah semua produk dalam group ini ke satu profile')}
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                                            padding: '6px 12px', borderRadius: 999, fontFamily: 'inherit', fontSize: 12,
                                            border: '1px solid var(--s-line-strong)', background: 'none', color: 'var(--s-ink-2)',
                                        }}
                                    >
                                        <IkonLapis size={13} strokeWidth={1.9} />
                                        {T('products.group.moveGroupToProfile', 'Pindah group ke profile')}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Bar pukal v3.9 — di bawah label group, atas kad pertama */}
                        {uiV39 && barPukal}

                        {paginatedProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                            <span className="material-symbols-outlined text-3xl text-slate-300">inventory_2</span>
                        </div>
                        <h3 className="text-sm font-bold text-slate-500 dark:text-slate-400 mb-1">
                            {searchQuery || filterMode !== 'all' || productGroup !== 'all'
                                ? T('products.empty.notFoundTitle', 'Tiada Produk Dijumpai')
                                : T('products.empty.noneTitle', 'Belum Ada Produk')}
                        </h3>
                        <p className="text-[11px] text-slate-400 max-w-xs mb-4">
                            {searchQuery
                                ? T('products.empty.searchDesc', `Tiada produk sepadan dengan "${searchQuery}"`, { carian: searchQuery })
                                : productGroup === 'own'
                                    ? T('products.empty.ownDesc', 'Belum ada My Products. Tambah produk anda sendiri.')
                                    : filterMode !== 'all'
                                        ? T('products.empty.filterDesc', 'Tiada produk dalam kategori ini')
                                        : isAdmin
                                            ? T('products.empty.adminDesc', 'Klik "Bulk Upload" untuk menambah produk secara pukal.')
                                            : T('products.empty.systemDesc', 'Admin belum menambah produk lagi.')
                            }
                        </p>
                        {isAdmin && !searchQuery && filterMode === 'all' && productGroup === 'all' && (
                            <button
                                onClick={() => setShowBulkUpload(true)}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[11px] font-bold text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-dashed border-purple-300 dark:border-purple-700 hover:border-purple-500 transition-all"
                            >
                                <span className="material-symbols-outlined text-[16px]">upload_file</span>
                                {T('products.empty.bulkUploadCta', 'Bulk Upload Products')}
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {/* ═══ PAPARAN JADUAL (rupa v3.9) ═══
                            Senarai padat untuk pengguna yang menguruskan ratusan
                            produk: satu baris satu produk, medan yang SAMA seperti
                            kad (ID · nama · status · group · harga) tetapi boleh
                            diimbas sekali pandang. Pilihan, group dan tindakan
                            memakai handler yang SAMA dengan kad — tiada logik
                            kedua yang boleh terpesong. */}
                        {uiV39 && paparanProduk === 'jadual' ? (
                            <div className="s-tblwrap">
                                {/* `sesaat-prodtbl` — nama kelas SENDIRI, semata-mata
                                    untuk lapisan telefon (src/mobile.css seksyen O).
                                    `.s-tblwrap` dikongsi dengan halaman lakaran
                                    ProductsV39View yang lajurnya berbeza, jadi
                                    menyasarkan pembalut itu akan menyorok lajur yang
                                    SALAH di sana. */}
                                <table className="s-table s-table--rapat sesaat-prodtbl" style={{ minWidth: 940 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 42 }}>
                                                <input
                                                    type="checkbox"
                                                    /* Halaman ini SAHAJA, dan betul-betul boleh
                                                       dibuang. Versi lama memanggil "pilih semua
                                                       tapisan" pada onChange, jadi menekannya kali
                                                       kedua tidak membuang apa-apa — kotak tanda
                                                       yang mustahil dimatikan. */
                                                    checked={halamanSemuaDitanda}
                                                    onChange={togglePilihHalaman}
                                                    className="w-3.5 h-3.5 cursor-pointer"
                                                    title={T('products.table.selectAllPage', 'Pilih semua dalam halaman ini')}
                                                />
                                            </th>
                                            <th style={{ width: 150 }}>{T('products.col.id', 'ID')}</th>
                                            {/* Petak ini dulu kosong. Kawalan saiz gambar duduk di
                                                sini kerana ia betul-betul di atas benda yang ia ubah —
                                                pelanggan tidak perlu mencarinya dalam bar alat.
                                                ⚠️ Huruf S/M/L dipapar supaya keadaan semasa KELIHATAN;
                                                butang yang hanya berikon tidak memberitahu ia sedang
                                                pada saiz mana. */}
                                            <th style={{ width: gbrPx + 32 }}>
                                                <button
                                                    type="button"
                                                    className="s-tbtn"
                                                    onClick={kitarSaizGambar}
                                                    title={T('products.view.imgSizeTip', 'Saiz gambar: {saiz} — tekan untuk tukar', { saiz: namaSaizGambar })}
                                                    aria-label={T('products.view.imgSizeTip', 'Saiz gambar: {saiz} — tekan untuk tukar', { saiz: namaSaizGambar })}
                                                    style={{
                                                        width: 'auto', height: 22, padding: '0 6px', gap: 4,
                                                        display: 'inline-flex', alignItems: 'center',
                                                        fontFamily: 'inherit', fontSize: 10.5, fontWeight: 700,
                                                    }}
                                                >
                                                    <IkonGambar size={12} strokeWidth={1.9} />
                                                    {saizGambar.toUpperCase()}
                                                </button>
                                            </th>
                                            <th style={{ minWidth: 240 }}>{T('products.col.product', 'Produk')}</th>
                                            <th style={{ width: 96 }}>{T('common.status', 'Status')}</th>
                                            <th style={{ width: 150 }}>{T('products.col.group', 'Group')}</th>
                                            <th className="s-td-num" style={{ width: 96 }}>{T('products.col.price', 'Harga')}</th>
                                            <th className="s-td-num" style={{ width: 92 }}>{T('products.col.original', 'Asal')}</th>
                                            <th className="s-td-num" style={{ width: 84 }}>{T('products.col.discount', 'Diskaun')}</th>
                                            <th style={{ width: 118 }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedProducts.map((product, idx) => {
                                            const ditanda = selectedIds.has(product.id);
                                            const bolehPadam = isAdmin || (product.group === 'own' && product.addedBy === user?.email);
                                            const grp = activeGroups.find(g => g.id === product.customGroup);
                                            return (
                                                <tr
                                                    key={product.id}
                                                    className={ditanda ? 's-trow s-trow--on' : 's-trow'}
                                                    onClick={(e) => handleCardSelect(product.id, e)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={ditanda}
                                                            onChange={() => toggleSelected(product.id)}
                                                            className="w-3.5 h-3.5 cursor-pointer"
                                                        />
                                                    </td>

                                                    <td className="s-num" style={{ whiteSpace: 'nowrap' }}>
                                                        #{productDisplayNo(product, (currentPage - 1) * ITEMS_PER_PAGE + idx)}
                                                    </td>

                                                    {/* Gambar boleh diganti terus dari baris — hover memunculkan butang
                                                        Upload, corak SAMA seperti kad grid. Sebelum ini mod senarai
                                                        langsung TIADA jalan menukar gambar, sedangkan senarai ialah
                                                        paparan LALAI halaman ini — jadi majoriti pelanggan mendarat
                                                        dalam mod yang buntu. `stopPropagation` wajib: klik pada baris
                                                        menanda kad, jadi tanpanya butang ini menanda kad juga. */}
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <div
                                                            className="group/thumb"
                                                            style={{ position: 'relative', display: 'inline-flex', borderRadius: SAIZ_RADIUS[saizGambar], overflow: 'hidden' }}
                                                        >
                                                            {product.imageUrl ? (
                                                                <img src={product.imageUrl} alt="" loading="lazy"
                                                                    style={{ width: gbrPx, height: gbrPx, flex: 'none', objectFit: 'cover', borderRadius: SAIZ_RADIUS[saizGambar], border: '1px solid var(--s-line)' }} />
                                                            ) : (
                                                                /* Petak kosong WAJIB sebesar gambar — kalau ia kekal 34px,
                                                                   baris produk tanpa gambar jadi lebih pendek dan lajur
                                                                   di kanannya nampak bengkok pada baris itu sahaja. */
                                                                <span className="s-thumb" style={{
                                                                    width: gbrPx, height: gbrPx,
                                                                    borderRadius: SAIZ_RADIUS[saizGambar],
                                                                    border: '1px solid var(--s-line)',
                                                                }} />
                                                            )}
                                                            {canReplaceImage(product) && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleTriggerReplaceImage(product)}
                                                                    disabled={replacingImageId === product.id}
                                                                    className="absolute inset-0 bg-black/50 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center text-white disabled:cursor-wait"
                                                                    title={replacingImageId === product.id
                                                                        ? T('products.card.uploading', 'Uploading...')
                                                                        : T('products.card.replaceImage', 'Ganti gambar')}
                                                                    aria-label={T('products.card.replaceImage', 'Ganti gambar')}
                                                                >
                                                                    <IkonUpload size={13} strokeWidth={2} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="s-td-strong s-clamp2" title={product.name}>{product.name || '—'}</div>
                                                        {/* ★ 2026-10-03 — tanda platform: TikTok / Shopee / kedua-duanya */}
                                                        <TandaPlatform item={product} style={{ marginTop: 3 }} />
                                                        {!product.hasIntelligence && (
                                                            <div style={{ fontSize: 11, color: 'var(--s-warn)', marginTop: 2 }}>
                                                                {/* `uiV39 ? t : undefined` — ayat sebab PENDING datang dari
                                                                    perkhidmatan dan ditulis dalam Melayu. Rupa lama mahu ia
                                                                    kekal begitu; v3.9 perlu ia ikut bahasa halaman. */}
                                                                {pendingReasonText(product, uiV39 ? t : undefined) || T('products.table.dataIncomplete', 'Data produk belum lengkap')}
                                                            </div>
                                                        )}
                                                    </td>

                                                    <td>
                                                        {product.hasIntelligence
                                                            ? <Badge tone="ok">{T('products.status.ready', 'READY')}</Badge>
                                                            : <Badge tone="warn">{T('products.status.pending', 'PENDING')}</Badge>}
                                                    </td>

                                                    {/* Group — pemilih terus dalam baris. Menu portal kad tidak
                                                        digunakan di sini supaya tiada salinan kedua markupnya. */}
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        {bolehUrusProduk ? (
                                                            <select
                                                                /* Group yang sudah dipadam tidak padan mana-mana <option>,
                                                                   jadi pemilih akan keluar KOSONG dan nampak rosak. Papar
                                                                   "Tiada group" — sepadan dengan cara ia dikira. */
                                                                value={tiadaGroup(product) ? '' : product.customGroup}
                                                                onChange={(e) => handleAssignGroup(product.id, e.target.value || null, product)}
                                                                aria-label={T('products.table.groupAria', 'Group produk')}
                                                                style={{
                                                                    width: '100%', maxWidth: 138, padding: '4px 6px', borderRadius: 6,
                                                                    fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
                                                                    background: 'var(--s-fill)', color: 'var(--s-ink-2)',
                                                                    border: '1px solid var(--s-line)',
                                                                }}
                                                            >
                                                                <option value="">{T('products.noGroup', 'Tiada group')}</option>
                                                                {groupsForProduct(product).map(g => (
                                                                    <option key={g.id} value={g.id}>{g.name}</option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <span style={{ fontSize: 12, color: 'var(--s-ink-3)' }}>{grp ? grp.name : '—'}</span>
                                                        )}
                                                    </td>

                                                    {/* Harga memakai helper yang SAMA dengan kad (`formatPrice`
                                                        / `formatDiscount`) — nilai tersimpan kadangkala sudah
                                                        membawa "RM", jadi menambah awalan sendiri di sini
                                                        menghasilkan "RMRM311.21". */}
                                                    <td className="s-td-num s-td-strong">{formatPrice(product?.price)}</td>
                                                    <td className="s-td-num" style={{ color: 'var(--s-ink-3)', textDecoration: formatPrice(product?.originalPrice) !== '-' ? 'line-through' : 'none' }}>
                                                        {formatPrice(product?.originalPrice)}
                                                    </td>
                                                    <td className="s-td-num" style={{ color: formatDiscount(product?.discount) !== '-' ? 'var(--s-stop)' : 'var(--s-ink-3)' }}>
                                                        {formatDiscount(product?.discount)}
                                                    </td>

                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                                                            {product.link && (
                                                                <a className="s-tbtn" href={buildScrapeSourceUrl(product) || product.link}
                                                                    target="_blank" rel="noreferrer"
                                                                    title={T('products.openInTikTok', 'Buka di TikTok Shop')} aria-label={T('products.openInTikTok', 'Buka di TikTok Shop')}>
                                                                    <IkonBuka size={14} strokeWidth={1.9} />
                                                                </a>
                                                            )}
                                                            {/* SATU butang edit — borang gabungan (maklumat +
                                                                harga + data skrip). Dua butang berasingan dibuang
                                                                dalam rupa v3.9 kerana harga wujud dalam kedua-dua
                                                                borang lama. */}
                                                            {bolehUrusProduk && (
                                                                <button type="button" className="s-tbtn"
                                                                    onClick={() => setCompleteDataProduct(product)}
                                                                    title={product.hasIntelligence ? T('products.table.editData', 'Edit produk') : T('products.table.completeData', 'Lengkapkan data produk')}
                                                                    aria-label={product.hasIntelligence ? T('products.table.editData', 'Edit produk') : T('products.table.completeData', 'Lengkapkan data produk')}>
                                                                    <IkonPensel size={14} strokeWidth={1.9} />
                                                                </button>
                                                            )}
                                                            {bolehPadam && (
                                                                <button type="button" className="s-tbtn"
                                                                    onClick={() => handleDeleteProduct(product.id, product)}
                                                                    title={T('products.table.deleteProduct', 'Padam produk')} aria-label={T('products.table.deleteProduct', 'Padam produk')}
                                                                    style={{ color: 'var(--s-stop)' }}>
                                                                    <IkonPadam size={14} strokeWidth={1.9} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                        /* Product Cards Grid */
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                            {paginatedProducts.map((product) => {
                                const intelStatus = intelGenStatus[product.id];
                                const isIntelSelected = intelSelectedIds.has(product.id);
                                const isDeleteSelected = selectedIds.has(product.id);
                                const isSelected = isIntelSelected || isDeleteSelected;
                                const canDelete = isAdmin || (product.group === 'own' && product.addedBy === user?.email);
                                return (
                                <div
                                    key={product.id}
                                    data-keep-selection
                                    className={`group/card relative bg-white dark:bg-slate-900 shadow-sm rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 ${
                                        isDeleteSelected
                                            ? 'border-2 border-red-500 ring-2 ring-red-200 dark:ring-red-900/50'
                                            : isIntelSelected
                                                ? 'border-2 border-violet-500 ring-2 ring-violet-200 dark:ring-violet-900/50'
                                                : 'border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                                    }`}
                                    style={(() => {
                                        const grp = activeGroups.find(g => g.id === product.customGroup);
                                        return grp && !isSelected ? { borderTopColor: grp.color, borderTopWidth: '3px' } : {};
                                    })()}
                                >
                                    {/* Admin select-mode checkbox overlay (top-left) — Intel */}
                                    {isAdmin && intelSelectMode && productGroup === 'system' && (
                                        <button
                                            onClick={() => toggleIntelSelected(product.id)}
                                            disabled={intelBatchActive}
                                            className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-md flex items-center justify-center shadow ${
                                                isIntelSelected
                                                    ? 'bg-violet-600 text-white'
                                                    : 'bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-transparent hover:border-violet-400'
                                            } ${intelBatchActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            title={isIntelSelected ? T('products.card.unselect', 'Unselect') : T('products.card.selectForIntel', 'Select for intel generation')}
                                        >
                                            <span className="material-symbols-outlined text-[15px]">check</span>
                                        </button>
                                    )}
                                    {/* Delete checkbox overlay (top-left) — selalu visible kecuali Intel mode ON */}
                                    {canDelete && !(isAdmin && intelSelectMode && productGroup === 'system') && (
                                        <button
                                            onClick={(e) => handleCardSelect(product.id, e)}
                                            disabled={bulkDeleting || bulkMoving || bulkClearingIntel}
                                            className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-md flex items-center justify-center shadow transition-opacity ${
                                                isDeleteSelected
                                                    ? 'bg-red-600 text-white opacity-100'
                                                    : 'bg-white/90 dark:bg-slate-800/90 border border-slate-300 dark:border-slate-600 text-transparent hover:border-red-400 hover:text-red-400 opacity-70 hover:opacity-100 group-hover/card:opacity-100'
                                            } ${(bulkDeleting || bulkMoving || bulkClearingIntel) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            title={isDeleteSelected ? T('products.card.unselect', 'Unselect') : T('products.card.select', 'Select')}
                                        >
                                            <span className="material-symbols-outlined text-[15px]">check</span>
                                        </button>
                                    )}

                                    {/* Per-item intel status indicator (top-right) */}
                                    {intelStatus && (
                                        <div
                                            className={`absolute top-2 right-2 z-10 px-2 py-0.5 rounded-md text-[9px] font-bold flex items-center gap-1 shadow ${
                                                intelStatus.status === 'done'
                                                    ? (intelStatus.partial ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300')
                                                    : intelStatus.status === 'error'
                                                        ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                                        : intelStatus.status === 'running'
                                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                            }`}
                                            title={intelStatus.error || (intelStatus.partial ? T('products.card.intelPartialTitle', 'Stage 1 atau 2 gagal sebahagian') : '')}
                                        >
                                            {intelStatus.status === 'running' && <span className="material-symbols-outlined text-[11px] animate-spin">progress_activity</span>}
                                            {intelStatus.status === 'done' && <span className="material-symbols-outlined text-[11px]">{intelStatus.partial ? 'warning' : 'check'}</span>}
                                            {intelStatus.status === 'error' && <span className="material-symbols-outlined text-[11px]">error</span>}
                                            {intelStatus.status === 'pending' && <span className="material-symbols-outlined text-[11px]">schedule</span>}
                                            {intelStatus.status === 'running'
                                                ? T('products.intel.status.generating', 'Generating')
                                                : intelStatus.status === 'done'
                                                    ? (intelStatus.partial ? T('products.intel.status.partial', 'Partial') : T('products.intel.status.done', 'Done'))
                                                    : intelStatus.status === 'error'
                                                        ? T('products.intel.status.failed', 'Failed')
                                                        : T('products.intel.status.queued', 'Queued')}
                                        </div>
                                    )}

                                    {/* Product Header */}
                                    <div className="flex items-center gap-3 p-3.5">
                                        {/* Thumbnail — hover to replace (admin for System, owner for My Products) */}
                                        <div className="group/thumb relative w-12 h-12 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0">
                                            {product.imageUrl ? (
                                                <img alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" src={product.imageUrl} />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <span className="material-symbols-outlined text-lg text-slate-300">image</span>
                                                </div>
                                            )}
                                            {canReplaceImage(product) && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleTriggerReplaceImage(product)}
                                                    disabled={replacingImageId === product.id}
                                                    className="absolute inset-0 bg-black/50 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center text-white disabled:cursor-wait"
                                                    title={replacingImageId === product.id ? T('products.card.uploading', 'Uploading...') : T('products.card.replaceImage', 'Ganti gambar')}
                                                >
                                                    <span className="material-symbols-outlined text-[18px]">
                                                        {replacingImageId === product.id ? 'progress_activity' : 'upload'}
                                                    </span>
                                                </button>
                                            )}
                                        </div>

                                        {/* Info — Edit Mode vs Display Mode */}
                                        {editingProductId === product.id ? (
                                            <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-[10px] font-bold text-slate-400 shrink-0">#</span>
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={editProductData.number}
                                                        onChange={(e) => setEditProductData(prev => ({ ...prev, number: e.target.value }))}
                                                        className="flex-1 min-w-0 h-6 px-1.5 text-[11px] font-bold text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
                                                        placeholder={T('products.edit.numberPlaceholder', 'No. / Product ID')}
                                                    />
                                                </div>
                                                <input
                                                    type="text"
                                                    value={editProductData.name}
                                                    onChange={(e) => setEditProductData(prev => ({ ...prev, name: e.target.value }))}
                                                    className="w-full h-7 px-2 text-xs font-bold text-slate-800 dark:text-slate-100 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
                                                    placeholder={T('products.edit.namePlaceholder', 'Nama produk')}
                                                    autoFocus
                                                />
                                                <input
                                                    type="text"
                                                    value={editProductData.link}
                                                    onChange={(e) => setEditProductData(prev => ({ ...prev, link: e.target.value }))}
                                                    className="w-full h-6 px-2 text-[10px] text-slate-500 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
                                                    placeholder={T('products.edit.linkPlaceholder', 'Link TikTok Shop')}
                                                />
                                                <div className="grid grid-cols-3 gap-1">
                                                    <input
                                                        type="text"
                                                        value={editProductData.price}
                                                        onChange={(e) => setEditProductData(prev => ({ ...prev, price: e.target.value }))}
                                                        className="w-full h-6 px-2 text-[10px] text-slate-700 dark:text-slate-200 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400/30"
                                                        placeholder={T('products.edit.pricePlaceholder', 'Harga')}
                                                    />
                                                    <input
                                                        type="text"
                                                        value={editProductData.originalPrice}
                                                        onChange={(e) => setEditProductData(prev => ({ ...prev, originalPrice: e.target.value }))}
                                                        className="w-full h-6 px-2 text-[10px] text-slate-500 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400/30 line-through"
                                                        placeholder={T('products.edit.originalPlaceholder', 'Asal')}
                                                    />
                                                    <input
                                                        type="text"
                                                        value={editProductData.discount}
                                                        onChange={(e) => setEditProductData(prev => ({ ...prev, discount: e.target.value }))}
                                                        className="w-full h-6 px-2 text-[10px] text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-400/30"
                                                        placeholder={T('products.edit.discountPlaceholder', 'Diskaun')}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveProduct(product); if (e.key === 'Escape') cancelEditProduct(); }}
                                                    />
                                                </div>
                                                <p className="text-[9px] text-slate-400 dark:text-slate-500 leading-snug px-0.5">
                                                    {T('products.edit.priceHint', '💡 Auto-prefill dari scrape. Harga + diskaun akan diinject ke prompt skrip (cth fragment hook TIBA-TIBA). Pastikan tepat.')}
                                                </p>
                                                <div className="flex items-center gap-1 mt-0.5">
                                                    <button onClick={() => handleSaveProduct(product)} className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-indigo-500 hover:bg-indigo-600 text-white text-[10px] font-bold transition-colors">
                                                        <span className="material-symbols-outlined text-[12px]">check</span> {T('products.action.save', 'Simpan')}
                                                    </button>
                                                    <button onClick={cancelEditProduct} className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 text-[10px] font-bold transition-colors">
                                                        <span className="material-symbols-outlined text-[12px]">close</span> {T('products.action.cancel', 'Batal')}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5 mb-0.5">
                                                    <span className="text-[11px] font-bold text-indigo-500 whitespace-nowrap tabular-nums" title={T('products.card.tiktokIdTooltip', 'ID produk TikTok')}>#{productDisplayNo(product)}</span>
                                                    {/* Intelligence Badge */}
                                                    {product.hasIntelligence ? (
                                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 border border-emerald-200/60 dark:border-emerald-700/40">
                                                            <span className="material-symbols-outlined text-[10px] text-emerald-500" style={{ fontVariationSettings: "'FILL' 1" }}>auto_awesome</span>
                                                            <span className="text-[8px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">{T('products.card.ready', 'Ready')}</span>
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700/40">
                                                            <span className="material-symbols-outlined text-[10px] text-slate-400">pending</span>
                                                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">{T('products.card.pending', 'Pending')}</span>
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate uppercase">{product.name}</p>
                                                {/* Dua alamat, dua tujuan berbeza — dulu hanya yang pertama dipapar,
                                                    sebagai teks terpotong tanpa butang salin langsung.
                                                      • Showcase — format /view/product/ yang WAJIB untuk browser
                                                        showcase TikTok. Jangan sesekali tukar bentuknya.
                                                      • Sumber scrape — halaman produk sebenar yang robot buka.
                                                        Dibina dari ID (lihat buildScrapeSourceUrl), jadi bentuknya
                                                        sama rata untuk semua kad. */}
                                                {product.link && (
                                                    <div className="flex items-center gap-1 mt-0.5 text-[9px] text-slate-400 min-w-0">
                                                        <span className="material-symbols-outlined text-[10px] shrink-0">storefront</span>
                                                        <a
                                                            href={product.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="truncate hover:text-indigo-500 hover:underline"
                                                            title={T('products.card.showcaseTooltip', `Showcase — ${product.link}`, { link: product.link })}
                                                        >
                                                            {T('products.card.showcase', 'Showcase')}
                                                        </a>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); copyLinkToClipboard(product.link, `sc_${product.id}`); }}
                                                            className="shrink-0 p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-indigo-500"
                                                            title={T('products.card.copyShowcase', 'Salin link showcase')}
                                                        >
                                                            <span className="material-symbols-outlined text-[11px] align-middle">
                                                                {copiedLinkId === `sc_${product.id}` ? 'check' : 'content_copy'}
                                                            </span>
                                                        </button>

                                                        {(() => {
                                                            const src = buildScrapeSourceUrl(product);
                                                            if (!src) return null; // entry manual tanpa link TikTok
                                                            return (
                                                                <>
                                                                    <span className="text-slate-300 dark:text-slate-600 shrink-0">·</span>
                                                                    <a
                                                                        href={src}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="truncate hover:text-indigo-500 hover:underline"
                                                                        title={T('products.card.scrapeSourceTooltip', `Sumber scrape — ${src}`, { url: src })}
                                                                    >
                                                                        {T('products.card.scrapeSource', 'Sumber scrape')}
                                                                    </a>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); copyLinkToClipboard(src, `sr_${product.id}`); }}
                                                                        className="shrink-0 p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-indigo-500"
                                                                        title={T('products.card.copyScrapeSource', 'Salin link sumber scrape')}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[11px] align-middle">
                                                                            {copiedLinkId === `sr_${product.id}` ? 'check' : 'content_copy'}
                                                                        </span>
                                                                    </button>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                                {/* Price + Original Price + Discount — single inline row, labelled */}
                                                {/* Schema v2: scrapedData dah pindah ke subdoc — guna top-level field sahaja
                                                    (extension dah prefill masa import). */}
                                                {(() => {
                                                    // ★ MULTI-REGION (2026-08-01) — dulu fungsi ni buang "RM" di depan, lalu
                                                    //   tampal "RM" balik TANPA syarat. Untuk produk luar Malaysia harga
                                                    //   keluar bertindih: "RM S$21.74". Sekarang: harga yang sudah membawa
                                                    //   simbolnya sendiri (RM13.80 / S$21.74 / ฿250 / Rp75.000) dipapar apa
                                                    //   adanya; hanya nombor bogel (cth "13.80" daripada import Excel lama)
                                                    //   yang diberi awalan RM. Produk Malaysia kekal sama seperti sebelum ini
                                                    //   kerana harganya memang datang dengan "RM" tercetak dari halaman TikTok.
                                                    /* formatPrice/formatDiscount kini di peringkat modul supaya
                                                       paparan Jadual memakai penapis yang SAMA (dulu ia hidup
                                                       di sini sahaja, dan jadual yang menambah "RM" sendiri
                                                       menghasilkan "RMRM311.21"). */
                                                    const price = formatPrice(product?.price);
                                                    const originalPrice = formatPrice(product?.originalPrice);
                                                    const discount = formatDiscount(product?.discount);
                                                    return (
                                                        <div className="flex items-center gap-2 mt-1 text-[10px] flex-wrap leading-tight">
                                                            <span className="inline-flex items-baseline gap-1">
                                                                <span className="text-slate-400 dark:text-slate-500">{T('products.card.priceLabel', 'Harga')}</span>
                                                                <span className="font-bold text-emerald-600 dark:text-emerald-400">{price}</span>
                                                            </span>
                                                            <span className="text-slate-300 dark:text-slate-600">·</span>
                                                            <span className="inline-flex items-baseline gap-1">
                                                                <span className="text-slate-400 dark:text-slate-500">{T('products.card.originalLabel', 'Asal')}</span>
                                                                <span className={`text-slate-500 dark:text-slate-400 ${originalPrice !== '-' ? 'line-through' : ''}`}>{originalPrice}</span>
                                                            </span>
                                                            <span className="text-slate-300 dark:text-slate-600">·</span>
                                                            <span className="inline-flex items-baseline gap-1">
                                                                <span className="text-slate-400 dark:text-slate-500">{T('products.card.discountLabel', 'Diskaun')}</span>
                                                                <span className="font-bold text-rose-600 dark:text-rose-400">{discount}</span>
                                                            </span>
                                                        </div>
                                                    );
                                                })()}
                                                {/* Sebab PENDING — tanpa ini pelanggan nampak "PENDING" tanpa tahu
                                                    apa yang kurang, lalu sangka scrape gagal sepenuhnya. */}
                                                {(() => {
                                                    const reason = pendingReasonText(product, uiV39 ? t : undefined);
                                                    if (!reason) return null;
                                                    // Kad sendiri: sebab jadi butang terus ke borang taip manual.
                                                    // Sesetengah penjual letak keterangan dalam gambar sahaja,
                                                    // jadi scrape memang takkan berjaya untuk produk itu.
                                                    const isOwn = product.group === 'own' && product.addedBy === user?.email;
                                                    if (!isOwn) {
                                                        return (
                                                            <p className="flex items-start gap-1 mt-1 text-[9px] leading-snug text-amber-600 dark:text-amber-400">
                                                                <span className="material-symbols-outlined text-[11px] shrink-0">info</span>
                                                                <span className="min-w-0">{reason}</span>
                                                            </p>
                                                        );
                                                    }
                                                    return (
                                                        <button
                                                            type="button"
                                                            onClick={() => setCompleteDataProduct(product)}
                                                            title={T('products.card.fillDataTooltip', 'Taip sendiri data produk ini')}
                                                            className="flex items-start gap-1 mt-1 text-[9px] leading-snug text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 text-left group/fix"
                                                        >
                                                            <span className="material-symbols-outlined text-[11px] shrink-0">info</span>
                                                            <span className="min-w-0">
                                                                {reason}
                                                                <span className="ml-1 font-extrabold underline decoration-dotted underline-offset-2 whitespace-nowrap">{T('products.card.completeData', 'Lengkapkan Data')}</span>
                                                            </span>
                                                        </button>
                                                    );
                                                })()}
                                            </div>
                                        )}

                                        {/* Actions */}
                                        {editingProductId !== product.id && (
                                        <div className="flex flex-col items-center gap-0.5 opacity-0 group-hover/card:opacity-100 transition-opacity shrink-0">
                                            {/* Add to My Products — System tab only */}
                                            {productGroup === 'system' && (
                                                <button
                                                    onClick={() => handleAddToMyProducts(product)}
                                                    disabled={myProductSourceIds.has(product.id) || addingToMyProducts === product.id}
                                                    className={`p-1 rounded-lg transition-colors ${
                                                        myProductSourceIds.has(product.id)
                                                            ? 'text-emerald-400 cursor-default'
                                                            : addingToMyProducts === product.id
                                                                ? 'text-slate-300 cursor-wait'
                                                                : 'hover:bg-teal-50 dark:hover:bg-teal-900/20 text-slate-400 hover:text-teal-500'
                                                    }`}
                                                    title={myProductSourceIds.has(product.id) ? T('products.card.alreadyInMine', 'Dah ada dalam My Products') : T('products.card.addToMine', 'Tambah ke My Products')}
                                                >
                                                    <span className="material-symbols-outlined text-[15px]">
                                                        {myProductSourceIds.has(product.id) ? 'check_circle' : 'add_circle'}
                                                    </span>
                                                </button>
                                            )}
                                            {/* Copy Product Data (everyone) */}
                                            <button
                                                onClick={() => {
                                                    const data = `${product.number || 0}*|*${product.name || ''}*|*${product.imageUrl || ''}*|*${product.link || ''}`;
                                                    navigator.clipboard.writeText(data);
                                                    // Brief visual feedback
                                                    const btn = document.getElementById(`copy-btn-${product.id}`);
                                                    if (btn) { btn.textContent = 'check'; setTimeout(() => { btn.textContent = 'content_copy'; }, 1500); }
                                                }}
                                                className="p-1 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-900/20 text-slate-400 hover:text-sky-500 transition-colors"
                                                title={T('products.card.copyData', 'Copy product data')}
                                            >
                                                <span id={`copy-btn-${product.id}`} className="material-symbols-outlined text-[15px]">content_copy</span>
                                            </button>
                                            {/* ═══ EDIT — satu butang sahaja dalam rupa v3.9 ═══
                                                Rupa v3.9: borang GABUNGAN (maklumat + harga + data skrip)
                                                dalam satu modal. Rupa LAMA kekal dua laluan berasingan —
                                                pensel membuka borang inline pada kad, dan pautan
                                                "Lengkapkan Data" membuka borang data.
                                                Sebab digabung: HARGA dahulunya wujud dalam kedua-dua
                                                borang, iaitu dua tempat menulis medan yang sama. */}
                                            {(isAdmin || (product.group === 'own' && product.addedBy === user?.email)) && (
                                                <button
                                                    onClick={() => (uiV39 ? setCompleteDataProduct(product) : startEditProduct(product))}
                                                    className="p-1 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-slate-400 hover:text-indigo-500 transition-colors"
                                                    title={uiV39 ? T('products.card.editProductData', 'Edit Product Data') : T('products.card.editProduct', 'Edit produk')}
                                                >
                                                    <span className="material-symbols-outlined text-[15px]">edit</span>
                                                </button>
                                            )}
                                            {/* Butang data berasingan — rupa LAMA sahaja. Dalam v3.9 ia
                                                sudah termasuk dalam borang gabungan di atas. */}
                                            {!uiV39 && (isAdmin || (product.group === 'own' && product.addedBy === user?.email)) && (
                                                <button
                                                    onClick={() => setCompleteDataProduct(product)}
                                                    className="p-1 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 text-slate-400 hover:text-amber-500 transition-colors"
                                                    title={product.hasIntelligence ? T('products.card.editProductData', 'Edit data produk') : T('products.card.completeProductData', 'Lengkapkan data produk')}
                                                >
                                                    <span className="material-symbols-outlined text-[15px]">edit_note</span>
                                                </button>
                                            )}
                                            {/* View Intelligence */}
                                            {/* ⛔ "Lihat Intelligence Data" + "Paste Intelligence JSON"
                                                DIBUANG daripada rupa v3.9 (arahan user 2026-08-16) —
                                                untuk admin MAHUPUN pelanggan.
                                                Sebab: kedua-duanya cuma tingkap kepada JSON mentah.
                                                Pipeline membaca DATA, bukan skrin — jadi tiada satu pun
                                                daripadanya diperlukan untuk generate. Butang Paste malah
                                                MATI untuk pelanggan (Simpan tidak berbuat apa-apa).
                                                Semua medan yang menyuap AI kini boleh dilihat DAN diubah
                                                dalam borang "Edit Data Produk".
                                                Rupa lama kekal seperti asal — ia jaring keselamatan. */}
                                            {!uiV39 && product.hasIntelligence && (
                                                <button
                                                    onClick={() => openViewIntel(product)}
                                                    className="p-1 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-slate-400 hover:text-emerald-500 transition-colors"
                                                    title={T('products.card.viewIntel', 'Lihat Intelligence Data')}
                                                >
                                                    <span className="material-symbols-outlined text-[15px]">visibility</span>
                                                </button>
                                            )}
                                            {/* View Scraped Data (imported products only) — flag-based, sebab scrapedData dah pindah ke subdoc.
                                                ⛔ DIBUANG dari rupa v3.9 (arahan user 2026-08-20) — mengelirukan
                                                bertindih dengan ikon "Edit Product Data" (pensel) yang sudah
                                                boleh lihat DAN ubah semua data. Kekal pada rupa LAMA. */}
                                            {!uiV39 && (product.hasScrapedData || product.scrapedData) && (
                                                <button
                                                    onClick={() => openScrapedData(product)}
                                                    className="p-1 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-900/20 text-slate-400 hover:text-sky-500 transition-colors"
                                                    title={T('products.card.viewScraped', 'Lihat Scraped Data')}
                                                >
                                                    <span className="material-symbols-outlined text-[15px]">info</span>
                                                </button>
                                            )}
                                            {/* Paste Intelligence — rupa LAMA sahaja (lihat nota di atas) */}
                                            {!uiV39 && (isAdmin || (product.group === 'own' && product.addedBy === user?.email)) && (
                                                <button
                                                    onClick={() => openEditIntel(product)}
                                                    className="p-1 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 text-slate-400 hover:text-purple-500 transition-colors"
                                                    title={T('products.card.pasteIntel', 'Paste Intelligence JSON')}
                                                >
                                                    <span className="material-symbols-outlined text-[15px]">data_object</span>
                                                </button>
                                            )}
                                            {/* Delete — admin or own product */}
                                            {(isAdmin || (product.group === 'own' && product.addedBy === user?.email)) && (
                                                <button
                                                    onClick={() => handleDeleteProduct(product.id, product)}
                                                    className="p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500 transition-colors"
                                                    title={T('products.delete', 'Padam')}
                                                >
                                                    <span className="material-symbols-outlined text-[15px]">delete</span>
                                                </button>
                                            )}
                                        </div>
                                        )}
                                    </div>

                                    {/* Group Footer — admin or own product */}
                                    {(isAdmin || (product.group === 'own' && product.addedBy === user?.email)) && activeGroups.length > 0 && (
                                        <div className="relative px-3 pb-3">
                                            {(() => {
                                                const grp = activeGroups.find(g => g.id === product.customGroup);
                                                const isOpen = assigningGroupProductId === product.id;
                                                const r = groupMenuRect;
                                                const openUp = r ? (r.top > window.innerHeight - r.bottom) : true;
                                                const maxH = r ? Math.max(140, (openUp ? r.top : window.innerHeight - r.bottom) - 16) : 280;
                                                return (
                                                    <>
                                                        <button
                                                            onClick={(e) => {
                                                                if (isOpen) { setAssigningGroupProductId(null); }
                                                                else { setGroupMenuRect(e.currentTarget.getBoundingClientRect()); setAssigningGroupProductId(product.id); }
                                                            }}
                                                            className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                                                                grp
                                                                    ? 'text-white shadow-sm'
                                                                    : 'bg-slate-50 dark:bg-slate-800 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 border border-dashed border-slate-300 dark:border-slate-600'
                                                            }`}
                                                            style={grp ? { backgroundColor: grp.color } : {}}
                                                        >
                                                            <span className="material-symbols-outlined text-[13px]" style={{ fontVariationSettings: grp ? "'FILL' 1" : undefined }}>label</span>
                                                            <span className="flex-1 text-left truncate">{grp ? grp.name : T('products.group.pick', 'Pilih Group')}</span>
                                                            <span className="material-symbols-outlined text-[13px] opacity-70">{isOpen ? 'expand_less' : 'expand_more'}</span>
                                                        </button>
                                                        {isOpen && r && createPortal(
                                                            <>
                                                                <div className="fixed inset-0 z-[59]" onClick={() => setAssigningGroupProductId(null)} />
                                                                <div
                                                                    className="fixed z-[60] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1 overflow-y-auto"
                                                                    style={{
                                                                        left: r.left,
                                                                        width: r.width,
                                                                        maxHeight: maxH,
                                                                        ...(openUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
                                                                    }}
                                                                >
                                                                    <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 sticky top-0 bg-white dark:bg-slate-900">
                                                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider">{T('products.group.pick', 'Pilih Group')}</span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleAssignGroup(product.id, null, product)}
                                                                        className="w-full flex items-center gap-2 px-3 py-2 text-[10px] font-bold text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[12px]">remove_circle_outline</span>
                                                                        {T('products.noGroup', 'Tiada Group')}
                                                                        {!product.customGroup && <span className="ml-auto material-symbols-outlined text-[12px] text-violet-500" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>}
                                                                    </button>
                                                                    {groupsForProduct(product).map(g => (
                                                                        <button
                                                                            key={g.id}
                                                                            onClick={() => handleAssignGroup(product.id, g.id, product)}
                                                                            className="w-full flex items-center gap-2 px-3 py-2 text-[10px] font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                                                                        >
                                                                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: g.color }}></span>
                                                                            {g.name}
                                                                            {product.customGroup === g.id && <span className="ml-auto material-symbols-outlined text-[12px] text-violet-500" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </>,
                                                            document.body
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    )}

                                    {/* Admin: Generate / Re-Generate Intelligence button */}
                                    {isAdmin && productGroup === 'system' && !intelSelectMode && (
                                        <div className="px-3.5 pb-3">
                                            {!product.hasIntelligence ? (
                                                <button
                                                    onClick={() => handleGenerateOne(product, false)}
                                                    disabled={intelStatus?.status === 'running' || intelBatchActive}
                                                    className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-[10px] font-bold shadow-sm shadow-violet-500/20 active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title={T('products.admin.genIntelTooltip', 'Run Stage 1 (vision) + Stage 2 (research) — admin pre-cache')}
                                                >
                                                    <span className="material-symbols-outlined text-[13px]">auto_awesome</span>
                                                    {T('products.admin.genIntel', 'Generate Intelligence')}
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleGenerateOne(product, true)}
                                                    disabled={intelStatus?.status === 'running' || intelBatchActive}
                                                    className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:border-amber-500 text-[10px] font-bold active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title={T('products.admin.regenIntelTooltip', 'Re-generate (bypass cache) — bayar lagi sekali')}
                                                >
                                                    <span className="material-symbols-outlined text-[13px]">refresh</span>
                                                    {T('products.admin.regenIntel', 'Re-Generate Intelligence')}
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* Generate Prompt Button (disabled — coming soon, untuk Stage 3 future) */}
                                    {product.hasIntelligence && !isAdmin && (
                                        <div className="px-3.5 pb-3">
                                            <button
                                                disabled
                                                className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-800/60 border border-slate-200 dark:border-slate-700 text-[10px] font-bold text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-60"
                                                title={T('products.card.genPromptTooltip', 'Powered by OpenAI GPT-5 Mini')}
                                            >
                                                <span className="material-symbols-outlined text-[13px]">smart_toy</span>
                                                {T('products.card.genPrompt', 'Generate Prompt')}
                                                <span className="text-[8px] font-black px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 ml-1">{T('products.card.soon', 'SOON')}</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-6 px-2">
                                <span className="text-[11px] text-slate-400 font-medium">
                                    {T('products.pagination.showing',
                                        `Showing ${(currentPage - 1) * ITEMS_PER_PAGE + 1}–${Math.min(currentPage * ITEMS_PER_PAGE, filteredProducts.length)} of ${filteredProducts.length}`,
                                        {
                                            dari: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                                            hingga: Math.min(currentPage * ITEMS_PER_PAGE, filteredProducts.length),
                                            jumlah: filteredProducts.length,
                                        })}
                                </span>
                                <div className="flex items-center gap-1.5">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1.5 text-[10px] font-bold rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                    >
                                        {T('products.pagination.prev', '← Prev')}
                                    </button>
                                    <span className="text-[11px] font-bold text-slate-600 dark:text-slate-400 px-2">
                                        {currentPage} / {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-3 py-1.5 text-[10px] font-bold rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                    >
                                        {T('products.pagination.next', 'Next →')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
                    </>
                )}
            </div>
        </Panel>

            {/* ═══ MODAL: Bulk Upload ═══ */}
            {showBulkUpload && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fadeIn" onClick={() => setShowBulkUpload(false)}>
                    <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="px-4 sm:px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                                <span className="material-symbols-outlined text-white text-lg">upload_file</span>
                            </div>
                            <div className="flex-1">
                                <h2 className="text-sm font-bold text-slate-800 dark:text-white">{T('products.bulkUpload.title', 'Bulk Upload Products')}</h2>
                                <p className="text-[10px] text-slate-400">{T('products.bulkUpload.subtitle', 'Masukkan satu produk per baris')}</p>
                            </div>
                            <button onClick={() => setShowBulkUpload(false)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors">
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>

                        {/* Format Guide */}
                        <div className="px-6 py-3 bg-blue-50/50 dark:bg-blue-900/10 border-b border-blue-100 dark:border-blue-900/30">
                            <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 mb-1">{T('products.bulkUpload.formatLabel', 'Format:')}</p>
                            <code className="text-[9px] text-blue-500/80 dark:text-blue-400/60 font-mono">
                                {T('products.bulkUpload.formatExample', 'No *|* Nama Product *|* Image URL *|* Link')}
                            </code>
                            <p className="text-[9px] text-blue-500/60 dark:text-blue-400/40 mt-1">
                                {T('products.bulkUpload.formatNote', 'Atau cukup tulis nama product sahaja (1 per baris). No. akan auto-generate.')}
                            </p>
                        </div>

                        {/* Textarea */}
                        <div className="p-6">
                            <textarea
                                value={bulkText}
                                onChange={(e) => setBulkText(e.target.value)}
                                placeholder={T('products.bulkUpload.placeholder', `1 *|* Product Name A *|* https://image.url *|* https://link.url\n2 *|* Product Name B\n3 *|* Product Name C *|* https://image.url\n\nAtau tulis nama sahaja:\nProduct Name X\nProduct Name Y`)}
                                rows={12}
                                className="w-full bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-4 text-xs font-mono text-slate-700 dark:text-slate-200 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-400 resize-none"
                            />
                            <div className="flex items-center justify-between mt-1">
                                <span className="text-[10px] text-slate-400">
                                    {bulkText.trim()
                                        ? T('products.bulkUpload.linesDetected', `${bulkText.split('\n').filter(l => l.trim()).length} baris dikesan`, { n: bulkText.split('\n').filter(l => l.trim()).length })
                                        : T('products.bulkUpload.emptyHint', 'Sila masukkan data')}
                                </span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-2">
                            <button onClick={() => setShowBulkUpload(false)} className="px-4 py-2 text-[11px] font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                                {T('products.action.cancel', 'Batal')}
                            </button>
                            <button
                                onClick={handleBulkUpload}
                                disabled={!bulkText.trim() || bulkUploading}
                                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-[11px] font-bold shadow-lg shadow-indigo-500/20 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {bulkUploading ? (
                                    <>
                                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        {T('products.bulkUpload.uploading', 'Sedang Upload...')}
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined text-[16px]">upload</span>
                                        {T('products.bulkUpload.submit', 'Upload')} {bulkText.trim() ? `(${bulkText.split('\n').filter(l => l.trim()).length})` : ''}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ MODAL: Manual Add Products ═══ */}
            {showManualAdd && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setShowManualAdd(false); setManualEntries([]); }} />
                    <div className="relative w-full max-w-xl max-h-[90vh] mx-4 bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                                    <span className="material-symbols-outlined text-white text-[18px]">edit_note</span>
                                </div>
                                <div>
                                    <h2 className="text-sm font-bold text-slate-800 dark:text-white">{T('products.manual.title', 'Tambah Produk Manual')}</h2>
                                    <p className="text-[10px] text-slate-400 font-medium">{T('products.manual.subtitle', 'Masukkan data produk secara manual ke Library')}</p>
                                </div>
                            </div>
                            <button onClick={() => { setShowManualAdd(false); setManualEntries([]); }} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors">
                                <span className="material-symbols-outlined text-[20px]">close</span>
                            </button>
                        </div>

                        {/* Entries */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                            {manualEntries.map((entry, idx) => (
                                <div key={entry._key} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-lg rounded-2xl overflow-hidden relative group/card">
                                    {/* Card Header */}
                                    <div className="flex items-center gap-3 p-4 border-b border-slate-100 dark:border-slate-800">
                                        <div className="group/thumb relative w-16 aspect-square rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0 cursor-pointer"
                                            onClick={() => manualFileRefs.current[entry._key]?.click()}>
                                            {entry.imagePreview ? (
                                                <img alt="" className="w-full h-full object-cover" src={entry.imagePreview} />
                                            ) : (
                                                <div className="w-full h-full flex flex-col items-center justify-center gap-0.5">
                                                    <span className="material-symbols-outlined text-xl text-slate-300 group-hover/thumb:text-primary transition-colors">add_photo_alternate</span>
                                                    <span className="text-[8px] text-slate-300 font-bold uppercase group-hover/thumb:text-primary transition-colors">{T('products.manual.upload', 'Upload')}</span>
                                                </div>
                                            )}
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
                                                <span className="material-symbols-outlined text-white text-[18px]">upload</span>
                                            </div>
                                            <input ref={el => manualFileRefs.current[entry._key] = el} type="file" accept="image/*" className="hidden"
                                                onChange={(e) => handleManualImageUpload(entry._key, e.target.files[0])} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 mb-1">
                                                <span className="text-[12px] font-bold text-indigo-500 whitespace-nowrap tabular-nums" title={T('products.card.tiktokIdTooltip', 'ID produk TikTok')}>#{productDisplayNo(entry, idx + 1)}</span>
                                                {manualEntries.length > 1 && (
                                                    <button onClick={() => removeManualEntry(entry._key)} className="ml-auto p-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-300 hover:text-red-500 transition-colors">
                                                        <span className="material-symbols-outlined text-[14px]">close</span>
                                                    </button>
                                                )}
                                            </div>
                                            <input type="text" value={entry.name} onChange={(e) => updateManualEntry(entry._key, 'name', e.target.value)}
                                                placeholder={T('products.manual.namePlaceholder', 'Nama produk...')}
                                                className="w-full text-xs font-bold text-slate-800 dark:text-slate-100 uppercase leading-snug bg-transparent border-none focus:outline-none focus:ring-0 placeholder:text-slate-300 placeholder:normal-case p-0" />
                                        </div>
                                    </div>

                                    {/* Fields */}
                                    <div className="p-4 space-y-3">
                                        <div className="grid grid-cols-[80px_1fr] gap-3">
                                            <div>
                                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">{T('products.manual.numberLabel', 'No.')}</label>
                                                <input type="number" value={entry.number} onChange={(e) => updateManualEntry(entry._key, 'number', parseInt(e.target.value) || 0)}
                                                    className="w-full h-8 px-2.5 text-xs font-bold text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
                                            </div>
                                            <div>
                                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">{T('products.manual.nameLabel', 'Nama Produk')}</label>
                                                <input type="text" value={entry.name} onChange={(e) => updateManualEntry(entry._key, 'name', e.target.value)}
                                                    placeholder={T('products.manual.nameExample', 'Cth: Baju Kurung Moden')}
                                                    className="w-full h-8 px-2.5 text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-slate-300 transition-all" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">{T('products.manual.imageUrlLabel', 'URL Gambar')}</label>
                                            <div className="flex items-center gap-2">
                                                <input type="text" value={entry.imageUrl} onChange={(e) => handleManualImageUrl(entry._key, e.target.value)}
                                                    placeholder={T('products.manual.imageUrlPlaceholder', 'https://... atau klik thumbnail untuk upload')}
                                                    className="flex-1 h-8 px-2.5 text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-slate-300 transition-all" />
                                                <button onClick={() => manualFileRefs.current[entry._key]?.click()}
                                                    className="shrink-0 h-8 px-2.5 flex items-center gap-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-500 hover:text-primary transition-all text-[10px] font-bold">
                                                    <span className="material-symbols-outlined text-[14px]">upload</span>
                                                    {T('products.manual.upload', 'Upload')}
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">{T('products.manual.linkLabel', 'Link TikTok')}</label>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center flex-1 min-w-0">
                                                    <span className="flex items-center h-8 px-2 bg-slate-100 dark:bg-slate-800 border border-r-0 border-slate-200 dark:border-slate-700 rounded-l-lg shrink-0">
                                                        <svg className="w-3.5 h-3.5 text-slate-400" viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.76a8.12 8.12 0 004.77 1.52V6.84a4.83 4.83 0 01-1-.15z" />
                                                        </svg>
                                                    </span>
                                                    <input type="text" value={entry.link}
                                                        onChange={(e) => updateManualEntry(entry._key, 'link', e.target.value)}
                                                        placeholder="https://shop.tiktok.com/view/product/..."
                                                        className="flex-1 h-8 px-2.5 text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-slate-300 transition-all min-w-0" />
                                                </div>
                                                <div className="flex items-center shrink-0">
                                                    <span className="flex items-center h-8 px-2 bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20 border border-r-0 border-rose-200 dark:border-rose-800/50 rounded-l-lg" title={T('products.manual.productIdTooltip', 'Masukkan Product ID sahaja')}>
                                                        <span className="material-symbols-outlined text-[13px] text-rose-400" style={{ fontVariationSettings: "'FILL' 1" }}>tag</span>
                                                    </span>
                                                    <input type="text" placeholder={T('products.manual.productIdPlaceholder', 'Product ID')}
                                                        value={(() => { const match = entry.link?.match(/product\/(\d+)/); return match ? match[1] : ''; })()}
                                                        onChange={(e) => {
                                                            const id = e.target.value.replace(/\D/g, '');
                                                            updateManualEntry(entry._key, 'link', id ? `https://shop.tiktok.com/view/product/${id}?region=MY&locale=en-US&source=agency` : '');
                                                        }}
                                                        className="w-[110px] h-8 px-2 text-xs font-bold text-rose-600 dark:text-rose-400 bg-rose-50/50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-800/50 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-rose-300/50 focus:border-rose-400 placeholder:text-rose-300 dark:placeholder:text-rose-700 placeholder:font-medium transition-all" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
                            <button onClick={addManualEntry}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-bold text-primary hover:bg-primary/10 border border-dashed border-primary/30 hover:border-primary/60 transition-all">
                                <span className="material-symbols-outlined text-[16px]">add</span>
                                {T('products.manual.addMore', 'Tambah Lagi')}
                            </button>
                            <div className="flex items-center gap-2">
                                <button onClick={() => { setShowManualAdd(false); setManualEntries([]); }}
                                    className="px-4 py-2 rounded-lg text-[11px] font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                    {T('products.action.cancel', 'Batal')}
                                </button>
                                <button onClick={handleManualSubmit}
                                    disabled={manualSaving || !manualEntries.some(e => e.name.trim())}
                                    className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-[11px] font-bold shadow-lg shadow-indigo-500/20 active:scale-[0.97] transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                                    {manualSaving ? (
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <span className="material-symbols-outlined text-[15px]">check</span>
                                    )}
                                    {manualSaving
                                        ? T('products.manual.saving', 'Saving...')
                                        : T('products.manual.submit', `Tambah (${manualEntries.filter(e => e.name.trim()).length})`, { n: manualEntries.filter(e => e.name.trim()).length })}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ MODAL: Paste Intelligence JSON (Admin) ═══ */}
            {intelModalProduct && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fadeIn" onClick={closeEditIntel}>
                    <div className="bg-white dark:bg-slate-900 w-full max-w-2xl max-h-[85vh] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        {/* Header */}
                        <div className="px-4 sm:px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3 shrink-0">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
                                <span className="material-symbols-outlined text-white text-lg">data_object</span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="text-sm font-bold text-slate-800 dark:text-white truncate">
                                    {T('products.intel.editTitle',
                                        `Intelligence JSON — #${productDisplayNo(intelModalProduct)} ${intelModalProduct.name}`,
                                        { no: productDisplayNo(intelModalProduct), nama: intelModalProduct.name })}
                                </h2>
                                <p className="text-[10px] text-slate-400">{T('products.intel.editSubtitle', 'Paste data Intelligence (format JSON)')}</p>
                            </div>
                            <button onClick={closeEditIntel} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors">
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>

                        {/* Textarea */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {intelModalLoading && (
                                <div className="flex items-center gap-2 mb-3 text-[10px] text-slate-400">
                                    <div className="w-3.5 h-3.5 border-2 border-slate-200 border-t-purple-500 rounded-full animate-spin"></div>
                                    {T('products.intel.loadingExisting', 'Memuatkan intelligence sedia ada...')}
                                </div>
                            )}
                            <textarea
                                value={intelJsonText}
                                onChange={(e) => setIntelJsonText(e.target.value)}
                                disabled={intelModalLoading}
                                placeholder={`{\n  "keyword": "...",\n  "angles": [...],\n  "hooks": [...],\n  "benefits": [...]\n}`}
                                className="w-full h-[400px] bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-4 text-xs font-mono text-slate-700 dark:text-slate-200 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-purple-400/30 focus:border-purple-400 resize-none disabled:opacity-50"
                            />
                            {intelJsonText.trim() && (() => {
                                try {
                                    JSON.parse(intelJsonText); return (
                                        <div className="flex items-center gap-1.5 mt-2">
                                            <span className="material-symbols-outlined text-[14px] text-emerald-500" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                                            <span className="text-[10px] font-bold text-emerald-500">{T('products.intel.jsonValid', 'JSON valid')}</span>
                                        </div>
                                    );
                                } catch {
                                    return (
                                        <div className="flex items-center gap-1.5 mt-2">
                                            <span className="material-symbols-outlined text-[14px] text-red-500" style={{ fontVariationSettings: "'FILL' 1" }}>error</span>
                                            <span className="text-[10px] font-bold text-red-500">{T('products.intel.jsonInvalid', 'JSON tidak valid')}</span>
                                        </div>
                                    );
                                }
                            })()}
                        </div>

                        {/* Actions */}
                        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
                            <div>
                                {intelModalProduct.hasIntelligence && (
                                    <button
                                        onClick={() => {
                                            setIntelJsonText('');
                                            // Clear intelligence
                                            handleSaveIntelligence();
                                        }}
                                        className="text-[10px] font-bold text-red-500 hover:underline"
                                    >
                                        {/* Kunci `clearData`, BUKAN `clear`. `clear` ialah butang
                                            buang-tanda-pilihan yang tidak berbahaya; butang INI
                                            memadam Intelligence Data produk. Berkongsi kunci
                                            bermakna butang pemusnah ini tertulis "Clear" sahaja. */}
                                        {T('products.intel.clearData', 'Padam Intelligence Data')}
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={closeEditIntel} className="px-4 py-2 text-[11px] font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                                    {T('products.action.cancel', 'Batal')}
                                </button>
                                <button
                                    onClick={handleSaveIntelligence}
                                    disabled={savingIntel}
                                    className="flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white text-[11px] font-bold shadow-lg shadow-purple-500/20 active:scale-[0.97] disabled:opacity-50 transition-all"
                                >
                                    {savingIntel ? (
                                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <span className="material-symbols-outlined text-[16px]">save</span>
                                    )}
                                    {T('products.action.save', 'Simpan')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ MODAL: Manage Product Groups (Admin) ═══ */}
            {showManageGroups && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fadeIn" onClick={() => setShowManageGroups(false)}>
                    <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        {/* Header */}
                        <div className="px-4 sm:px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                <span className="material-symbols-outlined text-white text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>label</span>
                            </div>
                            <div className="flex-1">
                                <h2 className="text-sm font-bold text-slate-800 dark:text-white">{T('products.groups.title', 'Urus Group Produk')}</h2>
                                <p className="text-[10px] text-slate-400">{T('products.groups.subtitle', 'Cipta kumpulan untuk asingkan produk mengikut kategori')}</p>
                            </div>
                            <button onClick={() => setShowManageGroups(false)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors">
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>

                        {/* Existing Groups */}
                        <div className="px-6 py-4 max-h-[240px] overflow-y-auto custom-scrollbar">
                            {visibleGroups.length === 0 ? (
                                <div className="text-center py-6">
                                    <span className="material-symbols-outlined text-3xl text-slate-300 block mb-2">label_off</span>
                                    <p className="text-[11px] text-slate-400">{T('products.groups.empty', 'Belum ada kumpulan. Cipta kumpulan pertama di bawah.')}</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {visibleGroups.map(g => {
                                        const count = produkSkopProfil.filter(p => p.customGroup === g.id).length;
                                        const isEditing = editingGroupId === g.id;
                                        const canEdit = isAdmin || productGroup === 'own';
                                        return (
                                            <div key={g.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                                                <span className="w-4 h-4 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: g.color }}></span>
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        autoFocus
                                                        value={editGroupName}
                                                        onChange={(e) => setEditGroupName(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleRenameGroup(g.id);
                                                            else if (e.key === 'Escape') { setEditingGroupId(null); setEditGroupName(''); }
                                                        }}
                                                        maxLength={40}
                                                        disabled={renamingGroup}
                                                        className="flex-1 h-7 px-2 text-[11px] font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 border border-violet-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400/30 transition-all"
                                                    />
                                                ) : (
                                                    <span className="flex-1 text-[11px] font-bold text-slate-700 dark:text-slate-200 truncate">{g.name}</span>
                                                )}
                                                {!isEditing && (
                                                    <span className="text-[9px] font-extrabold text-slate-400 px-1.5 py-0.5 rounded-md bg-slate-200 dark:bg-slate-700">{T('products.groups.productCount', `${count} produk`, { n: count })}</span>
                                                )}
                                                {isEditing ? (
                                                    <>
                                                        <button
                                                            onClick={() => handleRenameGroup(g.id)}
                                                            disabled={renamingGroup || !editGroupName.trim()}
                                                            className="p-1 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-emerald-500 transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                                                            title={T('products.action.save', 'Simpan')}
                                                        >
                                                            {renamingGroup ? (
                                                                <span className="block w-3.5 h-3.5 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin"></span>
                                                            ) : (
                                                                <span className="material-symbols-outlined text-[14px]">check</span>
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => { setEditingGroupId(null); setEditGroupName(''); }}
                                                            disabled={renamingGroup}
                                                            className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors shrink-0"
                                                            title={T('products.action.cancel', 'Batal')}
                                                        >
                                                            <span className="material-symbols-outlined text-[14px]">close</span>
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        {canEdit && (
                                                            <button
                                                                onClick={() => { setEditingGroupId(g.id); setEditGroupName(g.name); }}
                                                                className="p-1 rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/20 text-slate-300 hover:text-violet-500 transition-colors shrink-0"
                                                                title={T('products.groups.renameTooltip', 'Rename kumpulan')}
                                                            >
                                                                <span className="material-symbols-outlined text-[14px]">edit</span>
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleDeleteGroup(g.id)}
                                                            className="p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-300 hover:text-red-500 transition-colors shrink-0"
                                                            title={T('products.groups.deleteTooltip', 'Padam kumpulan')}
                                                        >
                                                            <span className="material-symbols-outlined text-[14px]">delete</span>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Add New Group */}
                        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
                            <p className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">{T('products.groups.addNewTitle', 'Tambah Group Baru')}</p>
                            {productGroup === 'own' && (
                                <p className="text-[10px] text-slate-400 mb-3">
                                    {profilAktif
                                        ? T('products.groups.newForProfile', `Group ini milik akaun: ${userProfiles.find(p => p.id === profilAktif)?.name || ''}`, { nama: userProfiles.find(p => p.id === profilAktif)?.name || '' })
                                        : T('products.groups.newShared', 'Tiada akaun dipilih — group ini akan berkongsi (tiada akaun). Pilih satu akaun dahulu untuk group khas akaun.')}
                                </p>
                            )}
                            {/* Color Swatches */}
                            <div className="flex items-center gap-2 mb-3 flex-wrap">
                                {['#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6','#8b5cf6','#ec4899','#14b8a6'].map(color => (
                                    <button
                                        key={color}
                                        onClick={() => setNewGroupColor(color)}
                                        className={`w-6 h-6 rounded-full transition-all shrink-0 ${newGroupColor === color ? 'ring-2 ring-offset-2 ring-slate-400 dark:ring-offset-slate-800 scale-110' : 'hover:scale-110'}`}
                                        style={{ backgroundColor: color }}
                                        title={color}
                                    />
                                ))}
                            </div>
                            {/* Name Input + Add Button */}
                            <div className="flex items-center gap-2">
                                <span className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: newGroupColor }}></span>
                                <input
                                    type="text"
                                    value={newGroupName}
                                    onChange={(e) => setNewGroupName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddGroup(); }}
                                    placeholder={T('products.groups.namePlaceholder', 'Nama kumpulan (cth: Pakaian)')}
                                    maxLength={40}
                                    className="flex-1 h-8 px-3 text-xs font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400 placeholder:text-slate-300 transition-all"
                                />
                                <button
                                    onClick={handleAddGroup}
                                    disabled={savingGroup || !newGroupName.trim()}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-[11px] font-bold shadow-sm active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed transition-all shrink-0"
                                >
                                    {savingGroup ? (
                                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <span className="material-symbols-outlined text-[15px]">add</span>
                                    )}
                                    {T('products.groups.addBtn', 'Tambah')}
                                </button>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-3 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                            <button onClick={() => setShowManageGroups(false)} className="px-4 py-2 text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors">
                                {T('products.action.close', 'Tutup')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ MODAL: View Intelligence JSON (Read-only) ═══ */}
            {viewIntelProduct && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fadeIn" onClick={closeViewIntel}>
                    <div className="bg-white dark:bg-slate-900 w-full max-w-2xl max-h-[85vh] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        {/* Header */}
                        <div className="px-4 sm:px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3 shrink-0">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                                <span className="material-symbols-outlined text-white text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>auto_awesome</span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="text-sm font-bold text-slate-800 dark:text-white truncate">
                                    {T('products.intel.viewTitle',
                                        `Intelligence Data — #${productDisplayNo(viewIntelProduct)} ${viewIntelProduct.name}`,
                                        { no: productDisplayNo(viewIntelProduct), nama: viewIntelProduct.name })}
                                </h2>
                                <p className="text-[10px] text-slate-400">{T('products.intel.viewSubtitle', 'Data Intelligence untuk generate prompt')}</p>
                            </div>
                            <button onClick={closeViewIntel} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors">
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {viewIntelLoading ? (
                                <div className="flex items-center justify-center py-16 text-slate-400 text-xs">
                                    <div className="w-5 h-5 border-2 border-slate-200 border-t-emerald-500 rounded-full animate-spin mr-2"></div>
                                    {T('products.intel.loading', 'Memuatkan intelligence data...')}
                                </div>
                            ) : (
                                <pre className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-4 text-xs font-mono text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words overflow-x-auto">
                                    {JSON.stringify(viewIntelData, null, 2)}
                                </pre>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center shrink-0">
                            <button
                                disabled={viewIntelLoading || !viewIntelData}
                                onClick={() => {
                                    navigator.clipboard.writeText(JSON.stringify(viewIntelData, null, 2));
                                }}
                                className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 hover:text-purple-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <span className="material-symbols-outlined text-[14px]">content_copy</span>
                                {T('products.intel.copyJson', 'Copy JSON')}
                            </button>
                            <button onClick={closeViewIntel} className="px-4 py-2 text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors">
                                {T('products.action.close', 'Tutup')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ MODAL: View Scraped Data (imported products) ═══ */}
            {scrapedDataProduct && (
                <ScrapedDataModal
                    product={scrapedDataProduct}
                    onClose={closeScrapedData}
                    /* Prop `t` HANYA dihantar pada rupa v3.9 — corak sama
                       DashboardSections: tanpa prop, modal kekal dengan ayat
                       Melayunya sendiri supaya rupa LAMA tidak bertukar
                       bahasa. */
                    t={uiV39 ? t : undefined}
                />
            )}

            {/* ═══ MODAL: Lengkapkan Data Produk (pelanggan taip sendiri) ═══ */}
            {completeDataProduct && (
                <CompleteProductDataModal
                    product={completeDataProduct}
                    onClose={() => setCompleteDataProduct(null)}
                    onSave={handleSaveManualData}
                    /* Butang Upload dalam borang muncul HANYA bila pelanggan
                       memang boleh menulis gambar produk ini (admin untuk
                       System, pemilik untuk My Products). Tanpa prop ini borang
                       kekal medan URL sahaja — bukan butang mati yang menipu. */
                    onMuatNaikGambar={canReplaceImage(completeDataProduct)
                        ? (fail) => muatNaikGambarProduk(fail, completeDataProduct)
                        : undefined}
                    v39={uiV39}
                    /* Borang GABUNGAN dalam rupa v3.9 sahaja — rupa lama kekal
                       dengan dua borang berasingan (inline "Edit produk" +
                       borang data ini). */
                    penuh={uiV39}
                    t={uiV39 ? t : undefined}
                />
            )}

            {/* ═══ MODAL: Import Produk (Fasa 2, stepper) — My Products, v3.9 sahaja ═══ */}
            {uiV39 && importOpen && importPlatform !== 'shopee' && (
                <ImportStepperV39
                    tabPlatform={<TabPlatformImport platform="tiktok" onTukar={setImportPlatform} />}
                    open={importOpen}
                    onClose={() => setImportOpen(false)}
                    profiles={userProfiles}
                    /* Akaun destinasi = kad profile yang SEDANG dibuka. Wizard tidak
                       lagi ada pemilih akaun sendiri (arahan user 2026-08-21) — kalau
                       kad "Semua produk"/"Tanpa profile" yang dibuka, ini kosong dan
                       produk masuk tanpa akaun. */
                    profileId={profilAktif || ''}
                    groups={userLibraryGroups}
                    existingIds={ownProductIds}
                    isAdmin={isAdmin}
                    onSave={handleImportSave}
                    onAddGroup={handleCreateGroupInline}
                    t={t}
                />
            )}

            {/* ═══ MODAL: Import Produk — tab SHOPEE (2026-10-03). Komponen BERASINGAN daripada
                 stepper TikTok di atas (keputusan user: dua tab, jangan campur). ═══ */}
            {uiV39 && importOpen && importPlatform === 'shopee' && (
                <ImportShopeeV39
                    tabPlatform={<TabPlatformImport platform="shopee" onTukar={setImportPlatform} />}
                    open={importOpen}
                    onClose={() => setImportOpen(false)}
                    profiles={userProfiles}
                    profileId={profilAktif || ''}
                    groups={userLibraryGroups}
                    existingKeys={ownShopeeKeys}
                    storefronts={userShopeeStorefronts}
                    onSaveStorefronts={handleSaveShopeeStorefronts}
                    onSave={handleImportSaveShopee}
                    onAddGroup={handleCreateGroupInline}
                    t={t}
                />
            )}


            {/* ═══ MODAL: Bulk Generate Intelligence Confirm (Admin) ═══ */}
            {intelBulkConfirm && (() => {
                const items = intelBulkConfirm.items;
                const forceRefresh = intelBulkConfirm.forceRefresh;
                const skipCachedCount = forceRefresh ? 0 : items.filter(i => i.hasIntelligence).length;
                const willSpendCount = forceRefresh ? items.length : items.length - skipCachedCount;
                const estCost = (willSpendCount * COST_PER_INTEL_USD).toFixed(4);
                return (
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fadeIn" onClick={() => setIntelBulkConfirm(null)}>
                        <div onClick={e => e.stopPropagation()} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 max-w-md w-[90%] p-5">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="material-symbols-outlined text-violet-500 text-[22px]">{forceRefresh ? 'refresh' : 'auto_awesome'}</span>
                                <h2 className="text-sm font-bold text-slate-800 dark:text-white">
                                    {forceRefresh
                                        ? T('products.admin.regenIntel', 'Re-Generate Intelligence')
                                        : T('products.admin.genIntel', 'Generate Intelligence')}
                                </h2>
                            </div>
                            <div className="space-y-2 mb-4 text-[12px] text-slate-600 dark:text-slate-300">
                                <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1.5">
                                    <span>{T('products.intelBulk.selected', 'Items dipilih')}</span>
                                    <span className="font-bold text-slate-800 dark:text-white">{items.length}</span>
                                </div>
                                {!forceRefresh && skipCachedCount > 0 && (
                                    <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1.5">
                                        <span className="text-emerald-600 dark:text-emerald-400">{T('products.intelBulk.cacheHit', 'Cache hit (skip, free)')}</span>
                                        <span className="font-bold text-emerald-600 dark:text-emerald-400">{skipCachedCount}</span>
                                    </div>
                                )}
                                <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1.5">
                                    <span>{T('products.intelBulk.willCall', 'Akan call API')}</span>
                                    <span className="font-bold text-slate-800 dark:text-white">{willSpendCount}</span>
                                </div>
                                <div className="flex justify-between pb-1.5">
                                    <span>{T('products.intelBulk.estCost', 'Anggaran kos (USD)')}</span>
                                    <span className="font-bold text-violet-600 dark:text-violet-400">~${estCost}</span>
                                </div>
                                <div className="text-[10px] text-slate-400 dark:text-slate-500 italic">
                                    {forceRefresh
                                        ? T('products.intelBulk.noteForce', 'Mode resubmit — bypass cache. Setiap item akan call OpenAI walau dah ada intelligence.')
                                        : T('products.intelBulk.noteNormal', `Parallel max ${INTEL_PARALLEL_LIMIT} concurrent. Boleh Stop bila-bila masa.`, { n: INTEL_PARALLEL_LIMIT })}
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <button onClick={() => setIntelBulkConfirm(null)} className="px-4 py-2 text-[11px] font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                                    {T('products.action.cancel', 'Batal')}
                                </button>
                                <button
                                    onClick={confirmBulkRun}
                                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-[11px] font-bold text-white shadow active:scale-[0.97] transition-all ${
                                        forceRefresh
                                            ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700'
                                            : 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700'
                                    }`}
                                >
                                    <span className="material-symbols-outlined text-[14px]">{forceRefresh ? 'refresh' : 'play_arrow'}</span>
                                    {forceRefresh
                                        ? T('products.intelBulk.runForce', 'Re-Generate Sekarang')
                                        : T('products.intelBulk.run', 'Generate Sekarang')}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ═══ MODAL: Bulk Delete Confirm ═══ */}
            {bulkDeleteConfirm && (() => {
                const items = bulkDeleteConfirm.items;
                const grpName = activeGroups.find(g => g.id === customGroupFilter)?.name;
                const contextMsg = customGroupFilter === 'all'
                    ? T('products.bulkDelete.msgAll',
                        `You are about to delete ${items.length} product${items.length === 1 ? '' : 's'}. This action cannot be undone.`,
                        { n: items.length })
                    : customGroupFilter === 'uncategorized'
                        ? T('products.bulkDelete.msgNoGroup',
                            `You are about to delete ${items.length} product${items.length === 1 ? '' : 's'} in "No Group". This action cannot be undone.`,
                            { n: items.length })
                        : T('products.bulkDelete.msgGroup',
                            `You are about to delete ${items.length} product${items.length === 1 ? '' : 's'} in group "${grpName}". This action cannot be undone.`,
                            { n: items.length, group: grpName });
                const closeModal = () => { if (!bulkDeleting) setBulkDeleteConfirm(null); };
                return (
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fadeIn"
                        onClick={closeModal}
                    >
                        <div
                            onClick={e => e.stopPropagation()}
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 max-w-md w-[90%] flex flex-col max-h-[80vh]"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-5 pb-3 border-b border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-red-500 text-[22px]">warning</span>
                                    <h2 className="text-sm font-bold text-slate-800 dark:text-white">{T('products.bulkDelete.title', 'Delete Products?')}</h2>
                                </div>
                                <button
                                    onClick={closeModal}
                                    disabled={bulkDeleting}
                                    className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={T('products.action.closeTooltip', 'Close')}
                                >
                                    <span className="material-symbols-outlined text-[20px]">close</span>
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-5 overflow-y-auto flex-1">
                                <p className="text-[12px] text-slate-600 dark:text-slate-300 mb-3">
                                    {contextMsg}
                                </p>
                                <div className="space-y-1.5 max-h-60 overflow-y-auto bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 border border-slate-100 dark:border-slate-800">
                                    {items.map(p => (
                                        <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 bg-white dark:bg-slate-900 rounded-md border border-slate-100 dark:border-slate-800">
                                            <div className="w-8 h-8 rounded-md bg-slate-100 dark:bg-slate-800 overflow-hidden shrink-0 flex items-center justify-center">
                                                {p.imageUrl ? (
                                                    <img src={p.imageUrl} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <span className="material-symbols-outlined text-[16px] text-slate-300">inventory_2</span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 truncate" title={T('products.card.tiktokIdTooltip', 'ID produk TikTok')}>#{productDisplayNo(p)}</div>
                                                <div className="text-[11px] text-slate-700 dark:text-slate-200 truncate">{p.name || T('products.card.noName', '(No name)')}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end p-5 pt-3 border-t border-slate-100 dark:border-slate-800">
                                <button
                                    onClick={handleBulkDelete}
                                    disabled={bulkDeleting}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[11px] font-bold text-white shadow active:scale-[0.97] transition-all bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {bulkDeleting ? (
                                        <>
                                            <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>
                                            {T('products.bulkDelete.deleting', 'Deleting...')}
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined text-[14px]">delete</span>
                                            {T('products.bulkDelete.confirmBtn', `Delete ${items.length} Product${items.length === 1 ? '' : 's'}`, { n: items.length })}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ═══ MODAL: Bulk Clear Intelligence Confirm (Admin) ═══ */}
            {bulkClearIntelConfirm && (() => {
                const items = bulkClearIntelConfirm.items;
                const closeModal = () => { if (!bulkClearingIntel) setBulkClearIntelConfirm(null); };
                return (
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fadeIn"
                        onClick={closeModal}
                    >
                        <div
                            onClick={e => e.stopPropagation()}
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 max-w-md w-[90%] flex flex-col max-h-[80vh]"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-5 pb-3 border-b border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-amber-500 text-[22px]">cleaning_services</span>
                                    <h2 className="text-sm font-bold text-slate-800 dark:text-white">{T('products.clearIntel.title', 'Clear Intelligence?')}</h2>
                                </div>
                                <button
                                    onClick={closeModal}
                                    disabled={bulkClearingIntel}
                                    className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={T('products.action.closeTooltip', 'Close')}
                                >
                                    <span className="material-symbols-outlined text-[20px]">close</span>
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-5 overflow-y-auto flex-1">
                                {/* ⚠️ Kiraan dulu dibalut <span> tebal di tengah ayat. Ia
                                    dibina semula sebagai SATU kunci penuh — susunan
                                    perkataan berbeza antara bahasa, jadi ayat yang
                                    dicantum daripada serpihan akan pecah dalam BM. */}
                                <p className="text-[12px] text-slate-600 dark:text-slate-300 mb-2">
                                    {T('products.clearIntel.msg',
                                        `You are about to clear cached intelligence (Stage 1+2) for ${items.length} product${items.length === 1 ? '' : 's'}.`,
                                        { n: items.length })}
                                </p>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3 italic">
                                    {T('products.clearIntel.note',
                                        `Produk masih wujud — hanya cache intelligence yang dibuang. Re-generate akan call API balik (~$${(items.length * COST_PER_INTEL_USD).toFixed(4)} kos).`,
                                        { kos: (items.length * COST_PER_INTEL_USD).toFixed(4) })}
                                </p>
                                <div className="space-y-1.5 max-h-60 overflow-y-auto bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 border border-slate-100 dark:border-slate-800">
                                    {items.map(p => (
                                        <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 bg-white dark:bg-slate-900 rounded-md border border-slate-100 dark:border-slate-800">
                                            <div className="w-8 h-8 rounded-md bg-slate-100 dark:bg-slate-800 overflow-hidden shrink-0 flex items-center justify-center">
                                                {p.imageUrl ? (
                                                    <img src={p.imageUrl} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <span className="material-symbols-outlined text-[16px] text-slate-300">inventory_2</span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 truncate" title={T('products.card.tiktokIdTooltip', 'ID produk TikTok')}>#{productDisplayNo(p)}</div>
                                                <div className="text-[11px] text-slate-700 dark:text-slate-200 truncate">{p.name || T('products.card.noName', '(No name)')}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end p-5 pt-3 border-t border-slate-100 dark:border-slate-800">
                                <button
                                    onClick={handleBulkClearIntel}
                                    disabled={bulkClearingIntel}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[11px] font-bold text-white shadow active:scale-[0.97] transition-all bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {bulkClearingIntel ? (
                                        <>
                                            <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>
                                            {T('products.clearIntel.clearing', 'Clearing...')}
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined text-[14px]">cleaning_services</span>
                                            {T('products.clearIntel.confirmBtn', `Clear Intel for ${items.length} Product${items.length === 1 ? '' : 's'}`, { n: items.length })}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ═══ MODAL: Profile TikTok — cipta / namakan semula / padam ═══ */}
            {profileModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50" onClick={() => !savingProfile && setProfileModal(null)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
                        <h2 className="text-sm font-bold text-slate-800 dark:text-white mb-1">
                            {profileModal.mode === 'edit'
                                ? T('products.profileModal.editTitle', 'Sunting Profile')
                                : T('products.profileModal.addTitle', 'Tambah Profile TikTok')}
                        </h2>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-4">
                            {T('products.profileModal.desc', 'Satu profile mewakili satu akaun TikTok anda. Produk boleh dipindahkan antara profile pada bila-bila masa.')}
                        </p>

                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{T('products.profileModal.nameLabel', 'Nama profile')}</label>
                        <input
                            autoFocus
                            value={profileModal.name}
                            onChange={(e) => setProfileModal(m => ({ ...m, name: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !savingProfile) handleSaveProfile(); }}
                            placeholder={T('products.profileModal.namePlaceholder', 'Contoh: TT Utama')}
                            className="w-full px-3 py-2 mb-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-[13px] text-slate-800 dark:text-white outline-none focus:border-slate-400"
                        />

                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{T('products.profileModal.usernameLabel', 'Username TikTok (pilihan)')}</label>
                        <input
                            value={profileModal.username}
                            onChange={(e) => setProfileModal(m => ({ ...m, username: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !savingProfile) handleSaveProfile(); }}
                            placeholder={T('products.profileModal.usernamePlaceholder', 'contoh: ttutama.my')}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-[13px] text-slate-800 dark:text-white outline-none focus:border-slate-400"
                        />

                        {/* ═══ Warna + ikon identiti akaun ═══ */}
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-4 mb-1.5">{T('products.profileModal.colorLabel', 'Warna')}</label>
                        <div className="flex items-center gap-2 flex-wrap">
                            {PROFILE_COLORS.map(c => {
                                const dipilih = (profileModal.color || PROFILE_COLORS[0]) === c;
                                return (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => setProfileModal(m => ({ ...m, color: c }))}
                                        aria-label={c}
                                        className="rounded-lg transition-transform active:scale-90"
                                        style={{ width: 26, height: 26, background: c, outline: dipilih ? `2px solid ${c}` : '2px solid transparent', outlineOffset: 2 }}
                                    />
                                );
                            })}
                        </div>

                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-4 mb-1.5">{T('products.profileModal.iconLabel', 'Ikon')}</label>
                        <div className="flex items-center gap-1.5 flex-wrap">
                            {/* "Huruf" = tiada ikon, guna huruf pertama nama akaun */}
                            <button
                                type="button"
                                onClick={() => setProfileModal(m => ({ ...m, icon: null }))}
                                title={T('products.profileModal.iconLetter', 'Huruf')}
                                className={`w-8 h-8 rounded-lg border flex items-center justify-center text-[12px] font-bold transition-colors ${!profileModal.icon ? 'border-slate-800 dark:border-white text-slate-800 dark:text-white' : 'border-slate-200 dark:border-slate-700 text-slate-400 hover:border-slate-400'}`}
                            >
                                {(profileModal.name || 'A').trim().charAt(0).toUpperCase() || 'A'}
                            </button>
                            {PROFILE_ICON_KEYS.map(k => {
                                const IkonPilih = PROFILE_ICONS[k];
                                const dipilih = profileModal.icon === k;
                                return (
                                    <button
                                        key={k}
                                        type="button"
                                        onClick={() => setProfileModal(m => ({ ...m, icon: k }))}
                                        className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-colors ${dipilih ? 'border-slate-800 dark:border-white text-slate-800 dark:text-white' : 'border-slate-200 dark:border-slate-700 text-slate-400 hover:border-slate-400'}`}
                                    >
                                        <IkonPilih size={16} strokeWidth={1.9} />
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex items-center gap-2 mt-5">
                            {profileModal.mode === 'edit' && (
                                <button
                                    onClick={() => handleDeleteProfile(profileModal.id)}
                                    disabled={savingProfile}
                                    className="px-3 py-2 rounded-lg text-[11px] font-bold text-red-600 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                                >
                                    {T('products.delete', 'Padam')}
                                </button>
                            )}
                            <div className="flex-1" />
                            <button
                                onClick={() => setProfileModal(null)}
                                disabled={savingProfile}
                                className="px-3 py-2 rounded-lg text-[11px] font-bold text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-slate-400 transition-colors disabled:opacity-50"
                            >
                                {T('products.action.cancel', 'Batal')}
                            </button>
                            <button
                                onClick={handleSaveProfile}
                                disabled={savingProfile || !profileModal.name.trim()}
                                className="px-4 py-2 rounded-lg text-[11px] font-bold text-white bg-slate-800 dark:bg-white dark:text-slate-900 hover:opacity-90 transition-opacity disabled:opacity-50"
                            >
                                {savingProfile ? T('products.action.saving', 'Menyimpan…') : T('products.action.save', 'Simpan')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ MODAL: Pindah SATU group ke profile ═══ */}
            {groupToProfile && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50" onClick={() => !bulkProfileBusy && setGroupToProfile(null)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
                        <h2 className="text-sm font-bold text-slate-800 dark:text-white mb-1">{T('products.moveGroup.title', 'Pindah group ke profile')}</h2>
                        {/* ⚠️ Nama group dulu dibalut <span> tebal di tengah ayat. Ia
                            dibina semula sebagai SATU kunci penuh — ayat yang dicantum
                            daripada serpihan akan pecah bila susunan perkataan berubah
                            antara bahasa. */}
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-4">
                            {T('products.moveGroup.desc',
                                `Group ${groupToProfile.groupName} dan semua produk di dalamnya akan menjadi milik akaun yang dipilih. Selepas ini group ini hanya muncul di bawah akaun tersebut.`,
                                { group: groupToProfile.groupName })}
                        </p>

                        <div className="space-y-1 max-h-[240px] overflow-y-auto">
                            {userProfiles.map(pr => (
                                <button
                                    key={pr.id}
                                    onClick={() => handleMoveGroupToProfile(groupToProfile.groupId, pr.id)}
                                    disabled={bulkProfileBusy}
                                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] font-bold text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:border-slate-400 transition-colors text-left disabled:opacity-50"
                                >
                                    <span className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-black text-slate-500 shrink-0">
                                        {(pr.name || '?').trim().charAt(0).toUpperCase()}
                                    </span>
                                    <span className="flex-1 truncate">{pr.name}</span>
                                    <span className="text-[10px] font-normal text-slate-400">
                                        {T('products.moveGroup.profileProductCount',
                                            `${products.filter(p => p.profileId === pr.id).length} produk`,
                                            { n: products.filter(p => p.profileId === pr.id).length })}
                                    </span>
                                </button>
                            ))}
                            {userProfiles.length === 0 && (
                                <p className="text-[11px] text-slate-400 italic px-1 py-2">{T('products.moveGroup.noProfiles', 'Belum ada profile. Tambah profile dahulu.')}</p>
                            )}
                            <button
                                onClick={() => handleMoveGroupToProfile(groupToProfile.groupId, null)}
                                disabled={bulkProfileBusy}
                                className="w-full px-3 py-2 rounded-lg text-[12px] font-bold text-slate-500 border border-dashed border-slate-300 dark:border-slate-600 hover:border-slate-400 transition-colors text-left disabled:opacity-50"
                            >
                                {T('products.moveGroup.removeFromProfile', 'Keluarkan dari profile')}
                            </button>
                        </div>

                        <div className="flex justify-end mt-5">
                            <button
                                onClick={() => setGroupToProfile(null)}
                                disabled={bulkProfileBusy}
                                className="px-3 py-2 rounded-lg text-[11px] font-bold text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-slate-400 transition-colors disabled:opacity-50"
                            >
                                {bulkProfileBusy ? T('products.action.processing', 'Memproses…') : T('products.action.close', 'Tutup')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Hidden file input for replace image */}
            <input
                ref={replaceImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleReplaceImageFile}
            />
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════
   KAD PROFILE TIKTOK — rupa v3.9
   Kad aktif memakai bingkai --s-select, corak SAMA dengan kad produk
   yang dipilih. Slot kosong memakai ikon orang; kad "Semua produk"
   memakai ikon lapisan kerana ia bukan satu akaun TikTok.

   ⭐ useT() dipanggil TERUS di sini (tiada penampan `T` seperti dalam
      LibraryView) kerana kad ini dipapar HANYA dalam rupa v3.9 — ketiga-tiga
      tempat ia dipasang berada di dalam blok `uiV39 && productGroup==='own'`.
      Kalau ia dipakai semula oleh rupa lama kelak, ubah kepada corak prop
      `t` dahulu; memanggil t() di sana akan menukar bahasa rupa lama.
   ═══════════════════════════════════════════════════════════════ */
function KadProfilTT({ tajuk, subtajuk, inisial, warna, ikonNama, terakhir, ikon, kosong, aktif, jumlah, group, sedia, tunggu, onPilih, onSunting,
    // Chip "Tanpa profile" — hanya kad "Semua produk" yang menghantarnya.
    tanpaProfilBil = 0, tanpaProfilAktif = false, onPilihTanpaProfil, tanpaProfilTeks }) {
    const t = useT();
    const IkonProfilPilihan = ikonNama ? PROFILE_ICONS[ikonNama] : null;
    // Latar tinted + ikon/huruf warna penuh — terbaca pada dua-dua tema.
    const gayaLencana = (warna && !ikon && !kosong)
        ? { background: `${warna}22`, border: `1px solid ${warna}55`, color: warna }
        : { background: 'var(--s-fill)', border: '1px solid var(--s-line)', color: kosong ? 'var(--s-ink-3)' : 'var(--s-ink)' };
    return (
        <Card
            hover
            onClick={onPilih}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPilih?.(); } }}
            role="button"
            tabIndex={0}
            aria-pressed={!!aktif}
            style={{
                flex: '1 1 180px', minWidth: 180, maxWidth: 260, cursor: 'pointer',
                padding: '12px 14px',
                borderColor: aktif ? 'var(--s-select)' : undefined,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span aria-hidden style={{
                    width: 32, height: 32, flex: 'none', borderRadius: 10,
                    ...gayaLencana,
                    display: 'grid', placeItems: 'center',
                    fontSize: 13, fontWeight: 700,
                }}>
                    {ikon ? <IkonLapis size={15} strokeWidth={1.9} />
                        : kosong ? <IkonProfil size={15} strokeWidth={1.9} />
                            : IkonProfilPilihan ? <IkonProfilPilihan size={16} strokeWidth={1.9} />
                                : inisial}
                </span>
                {aktif && <Badge tone="neutral" style={{ marginLeft: 'auto' }}>{t('products.profile.active')}</Badge>}
                {onSunting && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onSunting(); }}
                        title={t('products.profile.editTooltip')}
                        aria-label={t('products.profile.editTooltip')}
                        style={{
                            marginLeft: aktif ? 4 : 'auto', width: 22, height: 22, padding: 0, cursor: 'pointer',
                            background: 'none', border: 0, display: 'grid', placeItems: 'center', color: 'var(--s-ink-3)',
                        }}
                    >
                        <IkonPensel size={13} strokeWidth={1.9} />
                    </button>
                )}
            </div>

            <div style={{
                marginTop: 9, fontSize: 13.5, fontWeight: 600, color: 'var(--s-ink)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{tajuk}</div>
            <div style={{ marginTop: 1, fontSize: 12, color: 'var(--s-ink-3)' }}>{subtajuk}</div>

            {!kosong && (
                <>
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--s-ink-2)' }}>
                        {/* Bentuk tunggal dipilih di sini — enjin i18n ini tiada peraturan
                            jamak, jadi tanpa pilihan ini kad tertulis "1 groups". Melayu tiada
                            jamak, jadi kedua-dua kunci membawa ayat yang sama di sana. */}
                        {t(jumlah === 1 ? 'products.profile.product1' : 'products.profile.products', { n: jumlah })}
                        {group !== undefined && <><span style={{ color: 'var(--s-ink-3)' }}> · </span>{t(group === 1 ? 'products.profile.group1' : 'products.profile.groups', { n: group })}</>}
                    </div>
                    <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <Badge tone="ok">{t('products.profile.ready', { n: sedia })}</Badge>
                        {tunggu > 0 && <Badge tone="warn">{t('products.profile.pending', { n: tunggu })}</Badge>}
                        {/* ⚠️ Chip ini BOLEH DITEKAN — ia satu-satunya jalan melihat produk
                            yang belum ada profile, jadi menukarnya menjadi teks mati akan
                            membuang satu keupayaan sebenar. Ia sengaja bergaris putus supaya
                            tidak dibaca sebagai lencana status seperti dua chip di sebelahnya.
                            stopPropagation WAJIB: kad ini sendiri satu butang, dan tanpanya
                            klik akan mencetuskan "Semua produk" sekali gus membatalkan
                            tapisan yang baru sahaja ditekan. */}
                        {tanpaProfilBil > 0 && onPilihTanpaProfil && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onPilihTanpaProfil(); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}
                                title={t('products.profile.withoutProfileTip')}
                                aria-pressed={!!tanpaProfilAktif}
                                style={{
                                    cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, lineHeight: '18px',
                                    padding: '0 8px', borderRadius: 999,
                                    border: '1px ' + (tanpaProfilAktif ? 'solid' : 'dashed') + ' var(--s-line-strong)',
                                    background: tanpaProfilAktif ? 'var(--s-fill)' : 'none',
                                    color: tanpaProfilAktif ? 'var(--s-ink)' : 'var(--s-ink-3)',
                                }}
                            >
                                {tanpaProfilTeks}
                            </button>
                        )}
                    </div>
                    {terakhir && (
                        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--s-ink-3)' }}>
                            {t('products.profile.lastUsed', { v: masaLaluRingkas(terakhir, t) })}
                        </div>
                    )}
                </>
            )}
        </Card>
    );
}
